import { Bot, CheckCircle2, ChevronDown, ChevronRight, ChevronUp, FileText, Headphones, Loader2, Maximize2, MessageSquare, Mic, Minimize2, PenLine, RotateCcw, Send, Shapes, Video, Volume2, VolumeX, X } from 'lucide-react';
import DrawingBoard from '../drawing/DrawingBoard';
import { useEffect, useRef, useState } from 'react';
import { ApiService } from '../../services/api';

type TutorRole = 'user' | 'assistant';
type TutorToolName = 'show_pdf_on_canvas' | 'open_lesson_video' | 'open_simulation' | 'open_practice_test' | 'move_to_next_gain';

interface TutorMessage {
  id?: string;
  role: TutorRole;
  content: string;
  transient?: boolean;
  // Sistem notu (geçiş, ✓ giderildi, açık kaldı): AI balonu gibi görünmez; transient ile birlikte
  // modele ve oturum geçmişine gitmez
  notice?: boolean;
}

interface AITutorToolCall {
  id?: string;
  name: TutorToolName;
  arguments: Record<string, any>;
}

interface TutorQuestion {
  id: string | number;
  text?: string;
  options?: Record<string, string> | string[] | Array<{ text?: string; value?: string; label?: string }>;
  correctAnswer?: string;
  mediaUrl?: string;
  solutionVideoUrl?: string;
}

interface PracticeTestResultSummary {
  total: number;
  answered: number;
  correct: number;
  wrong: number;
  blank: number;
  // Sunucu puanlaması için TAM setin nihai cevapları (kazanım kapama sunucuda yapılır)
  answers?: Array<{ questionId: string | number; selected: string | null }>;
  wrongQuestions: Array<{
    index: number;
    text: string;
    selectedAnswer?: string;
    selectedAnswerText?: string;
    correctAnswer?: string;
    correctAnswerText?: string;
  }>;
}

type AITeacherLiveSession = Awaited<ReturnType<typeof ApiService.createAITeacherLiveSession>>;

interface AITeacherLiveConnection {
  session: AITeacherLiveSession;
  socket: WebSocket;
  contextKey: string;
}

type PdfPageImage = {
  id?: string | number;
  pageNumber: number;
  imageUrl: string;
};

type ActiveMaterial =
  | { type: "empty" }
  | {
    type: "pdf";
    pdfUrl: string;
    pageNumber: number;
    title?: string;
    totalPages?: number;
    pageImages?: PdfPageImage[];
  }
  | { type: "video"; videoId: string; videoUrl: string; title: string; startTime: string; endTime: string; transcriptExcerpt?: string }
  | { type: "simulation"; topicId: string }
  | { type: "test-loading"; gainId: string }
  | { type: "test-error"; gainId: string; message: string }
  | { type: "test"; gainId: string; questionId?: string; questions: TutorQuestion[]; source?: string; servedAt?: number };

/**
 * Pekiştirme testinin YARIM KALAN durumu. Çalışma alanı tek yuvalı: test açıkken
 * öğrenci "pdf aç" derse test bileşeni unmount oluyor ve içindeki cevaplar gidiyordu
 * (canlıda 29 sohbetin 2'sinde yaşandı). Taslak bileşenin dışında tutulur; öğrenci
 * teste döndüğünde kaldığı yerden devam eder.
 */
type PracticeTestDraft = {
  selectedAnswers: Record<string, string>;
  currentIndex: number;
  isCompleted: boolean;
  isReviewMode: boolean;
};

// Soru kimliklerini ve servis zamanını içerir: aynı kazanıma YENİ bir set (ya da tekrar testinde AYNI sorular)
// servis edilirse eski denemenin taslağı/inceleme ekranı geri gelmesin.
const buildPracticeTestKey = (material: Extract<ActiveMaterial, { type: "test" }>) =>
  `${material.gainId}|${material.servedAt || 0}|${material.questions.map((item, index) => String(item?.id ?? index)).join(",")}`;

// Kalınan testin SORU SORU incelemesi SIRALIDIR (sahibin kararları: 20 Eki, 30 Eki): ekrandaki yanlışı AI anlatır, öğrenci
// "anladım" deyince sıradaki açılır; sıradakiler o zamana kadar kilitlidir, konuşulmuş sorulara sessizce geri bakılır.
// Hepsi geçilince test yeniden başlar.
type PracticeReview = {
  currentNumber: number;
  numbers: number[];
  discussed: number[];
  // Sıradaki konuşulacak yanlış; ondan sonrakiler kilitli (hepsi bittiyse null)
  frontier: number | null;
  // AI bu soruyu anlatırken false: "anladım" onun cevabı bitince açılır
  canConfirm: boolean;
  allDone: boolean;
  onFocus: (number: number) => void;
  onConfirm: () => void;
  onStartRetest: () => void;
};

// Kalınan testin incelenecek sorusu (öğrencinin cevabıyla) — AI tek tek anlatır (sahibin 20 Eki kararı)
type FailedReviewQuestion = {
  number: number;
  text: string;
  options?: Record<string, string>;
  correctAnswer?: string;
  solution?: string;
  studentAnswer: string | null;
};

interface AITutorProps {
  isOpen: boolean;
  context: Record<string, any>;
  onClose: () => void;
  sidebarExpanded?: boolean;
  onShowPdfOnCanvas?: (payload: { pdf_url: string; page_number: number }) => void;
  onOpenSimulation?: (payload: { topic_id: string }) => void;
}

// İncelemede sohbete yazılan "anladım" düğmeyle aynıdır (TED 30 Eki: öğrenci "ANLADIM" yazdı, sayılmadı). Liste bilinçli dar:
// "yanlış anladım", "anladım mı", "anladım ama…" soruyu kapatmamalı; listede olmayan her şey AI'ya gider, zararsız yol odur.
const REVIEW_CONFIRM_PATTERN = /^((tamam|tamamdır|evet|ok|okey|sanırım|galiba|hocam|öğretmenim)\s+)*anlad[ıi]m(\s+(tamam|tamamdır|evet|hocam|öğretmenim|teşekkürler|teşekkür ederim|sağ ?ol\S*|geçelim|devam))*$/;
const isReviewConfirmText = (text: string) => REVIEW_CONFIRM_PATTERN.test(
  text.toLocaleLowerCase("tr-TR").replace(/[.,!…]+/g, " ").replace(/\s+/g, " ").trim(),
);

// SESLİ mod temposu: metin, sesin okumasıyla eşzamanlı aksın diye konuşma hızında (45 ms/karakter).
// Yalnız gerçekten ses ÇALARKEN kullanılır — durationMs (ses süresi) verilen çağrılar bu profille akar.
const AI_TUTOR_TYPING_SETTINGS = {
  audioDurationMultiplier: 0.95,
  fallbackMsPerCharacter: 45,
  minDurationMs: 1200,
  maxDurationMs: 90000,
  syncedStartDelayMs: 140,
  minCharacterDelayMs: 18,
  maxCharacterDelayMs: 55,
  spaceDelayMs: 8,
  pausesMs: {
    comma: 120,
    clause: 180,
    sentence: 280,
    paragraph: 380,
    turnEnd: 220,
  },
};

// METİN modu temposu: cevap sunucudan zaten TAM gelmiş durumda; konuşma hızında yazdırmak
// yapay bekletmeydi (800 karakter ≈ 40 sn, kutu da kilitli). Okumadan hızlı akan kısa bir
// akış hissi yeter: ~5 ms/karakter, hafif duraklamalar, üst sınır 5 sn.
const AI_TUTOR_TEXT_TYPING_SETTINGS = {
  msPerCharacter: 5,
  minDurationMs: 400,
  maxDurationMs: 5000,
  minCharacterDelayMs: 2,
  maxCharacterDelayMs: 8,
  spaceDelayMs: 1,
  pausesMs: {
    comma: 20,
    clause: 30,
    sentence: 50,
    paragraph: 70,
    turnEnd: 80,
  },
};
const AI_TUTOR_LIVE_FIRST_DATA_TIMEOUT_MS = 15000;
const AI_TUTOR_LIVE_TURN_COMPLETE_GRACE_MS = 900;
// Gemini 3.8 Live araç çağırdığı turu sessiz kapatıp anlatımı araç yanıtından sonra YENİ turda söylüyor (~1-1,2 sn sonra);
// 900 ms'lik pay yetmiyordu ve öğrenci yalnız "Tamam, buradan devam edelim." görüyordu.
const AI_TUTOR_LIVE_TOOL_CONTINUATION_WAIT_MS = 8000;
const AI_TUTOR_LIVE_TOOL_RESULT_TEXT: Record<string, string> = {
  show_pdf_on_canvas: "PDF sayfası öğrencinin ekranında açıldı; öğrenci şu an o sayfayı görüyor.",
  open_lesson_video: "Video öğrencinin ekranında açıldı.",
  open_simulation: "Simülasyon öğrencinin ekranında açıldı.",
  open_practice_test: "Test isteği öğrencinin ekranına iletildi.",
  move_to_next_gain: "Kazanım geçişi işlendi.",
};
// 3.8 Live araç argümanlarını ({pdf_url:...}) nadiren yazı dökümüne sızdırıyor; bu parçalar öğrenciye gösterilmez.
const isLiveToolArgumentEcho = (fragment: string) => /(pdf_url|video_url|page_number|_url\s*:)/i.test(fragment);
const AI_TUTOR_SPEECH_STREAM_TEXT_FALLBACK_MS = 2200;
const AI_TUTOR_RECORDING_MAX_MS = 22000;
const AI_TUTOR_RECORDING_SILENCE_MS = 1100;
const AI_TUTOR_RECORDING_SPEECH_RMS = 0.035;
const AI_TUTOR_VOICE_LEVEL_IDLE = 0.08;

const TEXTUAL_TOOL_CALL_NAMES: TutorToolName[] = ["open_lesson_video", "show_pdf_on_canvas", "open_simulation", "open_practice_test"];
const TEXTUAL_TOOL_CALL_PATTERN = new RegExp(`(^|\\n)\\s*(${TEXTUAL_TOOL_CALL_NAMES.join("|")})\\s*\\(([^)]*)\\)\\s*`, "g");

const splitToolCallArguments = (argsText = "") => {
  const args: string[] = [];
  let current = "";
  let quote = "";
  let escaped = false;

  for (const char of argsText) {
    if (escaped) {
      current += char;
      escaped = false;
      continue;
    }

    if (char === "\\") {
      current += char;
      escaped = true;
      continue;
    }

    if (quote) {
      current += char;
      if (char === quote) quote = "";
      continue;
    }

    if (char === '"' || char === "'") {
      current += char;
      quote = char;
      continue;
    }

    if (char === ",") {
      if (current.trim()) args.push(current.trim());
      current = "";
      continue;
    }

    current += char;
  }

  if (current.trim()) args.push(current.trim());
  return args;
};

const parseToolCallArgumentValue = (value = "") => {
  const trimmed = value.trim();
  if (!trimmed) return "";

  if ((trimmed.startsWith("'") && trimmed.endsWith("'")) || (trimmed.startsWith('"') && trimmed.endsWith('"'))) {
    return trimmed.slice(1, -1).replace(/\\(['"\\])/g, "$1");
  }

  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
  if (/^(true|false)$/i.test(trimmed)) return /^true$/i.test(trimmed);

  return trimmed;
};

const parseTextualToolCallArguments = (argsText = "") =>
  splitToolCallArguments(argsText).reduce<Record<string, any>>((acc, item) => {
    const match = item.match(/^\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*=\s*([\s\S]*)$/);
    if (!match) return acc;

    acc[match[1]] = parseToolCallArgumentValue(match[2]);
    return acc;
  }, {});

// Araç çağrısının metne sızan ikinci biçimi ("…default_api:move_to_next_gain{…}") — temizlenir, çalıştırılmaz
const LEAKED_TOOL_CALL_PATTERN = /\S*default_api:[a-z_]+\s*(\{[^}]*\}|\([^)]*\))?\s*/gi;

const extractTextualToolCallsFromMessage = (message = "") => {
  const toolCalls: AITutorToolCall[] = [];
  const cleanedMessage = String(message || "")
    .replace(LEAKED_TOOL_CALL_PATTERN, "")
    .replace(TEXTUAL_TOOL_CALL_PATTERN, (fullMatch, linePrefix, name, argsText) => {
      const toolName = name as TutorToolName;
      toolCalls.push({
        id: `textual-tool-${toolCalls.length}`,
        name: toolName,
        arguments: parseTextualToolCallArguments(argsText),
      });

      return linePrefix || "";
    })
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return { message: cleanedMessage, toolCalls };
};

const getTypingPauseAfterChar = (
  text: string,
  index: number,
  pausesMs: { comma: number; clause: number; sentence: number; paragraph: number },
) => {
  const char = text[index] || "";
  const nextChar = text[index + 1] || "";

  if (char === "\n") return pausesMs.paragraph;
  if (/[.!?]/.test(char)) {
    return nextChar === "\n" ? pausesMs.paragraph : pausesMs.sentence;
  }
  if (/[;:]/.test(char)) return pausesMs.clause;
  if (char === ",") return pausesMs.comma;

  return 0;
};

// durationMs verildiyse SESLİ moddur (metin ses süresine eşitlenir); verilmediyse METİN modu —
// hızlı akış profili kullanılır.
const streamAssistantText = (
  fullText: string,
  onUpdate: (partialText: string) => void,
  onDone?: () => void,
  durationMs?: number,
) => {
  const text = fullText || "";
  const timerIds = new Set<number>();
  let isStopped = false;
  let didFinish = false;
  const clearTimers = () => {
    timerIds.forEach((timerId) => window.clearTimeout(timerId));
    timerIds.clear();
  };
  const finish = () => {
    if (didFinish) return;
    didFinish = true;
    isStopped = true;
    clearTimers();
    onDone?.();
  };

  if (!text) {
    finish();

    return { flush: finish, cancel: finish };
  };

  const isVoiceSynced = Boolean(durationMs);
  const profile = isVoiceSynced
    ? {
      minCharacterDelayMs: AI_TUTOR_TYPING_SETTINGS.minCharacterDelayMs,
      maxCharacterDelayMs: AI_TUTOR_TYPING_SETTINGS.maxCharacterDelayMs,
      spaceDelayMs: AI_TUTOR_TYPING_SETTINGS.spaceDelayMs,
      pausesMs: AI_TUTOR_TYPING_SETTINGS.pausesMs,
      minTypingMs: 900,
    }
    : {
      minCharacterDelayMs: AI_TUTOR_TEXT_TYPING_SETTINGS.minCharacterDelayMs,
      maxCharacterDelayMs: AI_TUTOR_TEXT_TYPING_SETTINGS.maxCharacterDelayMs,
      spaceDelayMs: AI_TUTOR_TEXT_TYPING_SETTINGS.spaceDelayMs,
      pausesMs: AI_TUTOR_TEXT_TYPING_SETTINGS.pausesMs,
      minTypingMs: 300,
    };
  const characters = Array.from(text);
  const targetDuration = isVoiceSynced
    ? Math.min(
      AI_TUTOR_TYPING_SETTINGS.maxDurationMs,
      Math.max(AI_TUTOR_TYPING_SETTINGS.minDurationMs, (durationMs as number) * AI_TUTOR_TYPING_SETTINGS.audioDurationMultiplier),
    )
    : Math.min(
      AI_TUTOR_TEXT_TYPING_SETTINGS.maxDurationMs,
      Math.max(AI_TUTOR_TEXT_TYPING_SETTINGS.minDurationMs, characters.length * AI_TUTOR_TEXT_TYPING_SETTINGS.msPerCharacter),
    );

  const totalPauseMs = characters.reduce((sum, _, index) => sum + getTypingPauseAfterChar(text, index, profile.pausesMs), 0);
  const typingDuration = Math.max(profile.minTypingMs, targetDuration - totalPauseMs);
  const baseCharacterDelayMs = Math.min(
    profile.maxCharacterDelayMs,
    Math.max(profile.minCharacterDelayMs, typingDuration / Math.max(1, characters.length)),
  );
  let currentIndex = 0;

  const tick = () => {
    if (isStopped) return;
    if (currentIndex >= characters.length) {
      const doneTimer = window.setTimeout(finish, profile.pausesMs.turnEnd);
      timerIds.add(doneTimer);
      return;
    }

    currentIndex += 1;
    onUpdate(characters.slice(0, currentIndex).join(""));

    const currentChar = characters[currentIndex - 1] || "";
    const pauseMs = getTypingPauseAfterChar(text, currentIndex - 1, profile.pausesMs);
    const nextDelayMs = /\s/.test(currentChar)
      ? profile.spaceDelayMs
      : baseCharacterDelayMs + pauseMs;

    const tickTimer = window.setTimeout(() => {
      timerIds.delete(tickTimer);
      tick();
    }, nextDelayMs);
    timerIds.add(tickTimer);
  };

  tick();

  return {
    flush: () => {
      if (didFinish) return;
      currentIndex = characters.length;
      onUpdate(text);
      finish();
    },
    cancel: finish,
  };
};

const estimateSpeechDurationMs = (text: string) => {
  const normalizedLength = text.replace(/\s+/g, " ").trim().length;
  return Math.min(
    AI_TUTOR_TYPING_SETTINGS.maxDurationMs,
    Math.max(AI_TUTOR_TYPING_SETTINGS.minDurationMs, normalizedLength * AI_TUTOR_TYPING_SETTINGS.fallbackMsPerCharacter),
  );
};

const base64ToUint8Array = (base64: string) => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
};

const getSampleRateFromMimeType = (mimeType = "") => {
  const match = String(mimeType || "").match(/rate=(\d+)/i);
  return Number(match?.[1]) || 24000;
};

const appendLiveTextFragment = (current: string, fragment: string) => {
  const cleanFragment = String(fragment || "").replace(/\s+/g, " ").trim();
  const cleanCurrent = String(current || "").replace(/\s+/g, " ").trim();
  if (!cleanFragment) return cleanCurrent;
  if (!cleanCurrent) return cleanFragment;
  if (cleanCurrent.endsWith(cleanFragment)) return cleanCurrent;
  if (cleanFragment.startsWith(cleanCurrent)) return cleanFragment;

  const needsSpace = !/\s$/.test(cleanCurrent) && !/^[,.;:!?]/.test(cleanFragment);
  return `${cleanCurrent}${needsSpace ? " " : ""}${cleanFragment}`;
};

const isLikelyIncompleteAssistantText = (text = "") => {
  const cleaned = String(text || "").replace(/\s+/g, " ").trim();
  if (!cleaned) return true;
  if (/[.!?…]$/.test(cleaned)) return false;
  if (/[,;:]$/.test(cleaned)) return true;
  if (/\b(ve|veya|ya da|ile|ama|fakat|çünkü|ki|sence|peki|örneğin|mesela)$/i.test(cleaned)) return true;

  return false;
};

const isInternalMetaAssistantText = (text = "") => {
  const cleaned = String(text || "").replace(/\s+/g, " ").trim();
  if (!cleaned) return false;
  const lower = cleaned.toLocaleLowerCase("en-US");
  const englishMetaHitCount = [
    "tutoring",
    "session",
    "focusing",
    "aiming",
    "initial",
    "target",
    "missing gain",
    "module",
    "according to",
    "plan",
  ].filter((pattern) => lower.includes(pattern)).length;

  return (
    englishMetaHitCount >= 2 ||
    /^\*\*[^*]*(?:focusing|tutoring|session|teacher response)[^*]*\*\*/i.test(cleaned) ||
    /\binitiating tutoring session\b/i.test(cleaned) ||
    /\bfocusing on .*tutoring\b/i.test(cleaned) ||
    /\bi'?m aiming\b/i.test(cleaned) ||
    /\bi'?m starting (?:a )?new tutoring session\b/i.test(cleaned) ||
    /\bmy initial focus\b/i.test(cleaned) ||
    /\bcentral target\b/i.test(cleaned) ||
    /\bmissing gain\b/i.test(cleaned) ||
    /\baccording to the plan\b/i.test(cleaned) ||
    /\baktif ekran bağlamı\b/i.test(cleaned) ||
    /\bsystem instruction\b/i.test(cleaned)
  );
};

const getSafeAssistantMessage = (message: string, fallback: string) =>
  isInternalMetaAssistantText(message) ? fallback : message;

const parseMentionedPdfPageNumber = (text = "") => {
  const normalized = String(text || "")
    .toLocaleLowerCase("tr-TR")
    .replace(/ı/g, "i")
    .replace(/ü/g, "u")
    .replace(/ö/g, "o")
    .replace(/ş/g, "s")
    .replace(/ç/g, "c")
    .replace(/ğ/g, "g");

  const numericPageMatch =
    normalized.match(/(?:sayfa(?:sinda|sinde|da|de|yi|i|ya|ye)?\s*(\d{1,3})|(\d{1,3})\s*[.)]?\s*sayfa(?:sinda|sinde|da|de|yi|i|ya|ye)?)/i);
  const numericPage = Number(numericPageMatch?.[1] || numericPageMatch?.[2] || 0);
  if (numericPage > 0) return numericPage;

  const ordinalPages: Record<string, number> = {
    ilk: 1,
    birinci: 1,
    ikinci: 2,
    ucuncu: 3,
    dorduncu: 4,
    besinci: 5,
    altinci: 6,
    yedinci: 7,
    sekizinci: 8,
    dokuzuncu: 9,
    onuncu: 10,
  };

  const ordinalMatch = normalized.match(/\b(ilk|birinci|ikinci|ucuncu|dorduncu|besinci|altinci|yedinci|sekizinci|dokuzuncu|onuncu)\s+sayfa(?:sinda|sinde|da|de|yi|i|ya|ye)?\b/);
  return ordinalMatch ? ordinalPages[ordinalMatch[1]] || 0 : 0;
};

const normalizeLiveContextList = (value: any) => {
  if (!Array.isArray(value)) return value ? [String(value)] : [];

  return value
    .map((item) => String(item?.gainId || item?.id || item?.url || item?.videoUrl || item?.pdfUrl || item || ""))
    .filter(Boolean);
};

const buildAITeacherLiveContextKey = (liveContext: Record<string, any> = {}) => {
  // SADECE büyük bağlam değişikliklerini key'e dahil et — kazanımlar arası geçişte (currentGainIndex)
  // ve ufak metadata değişikliklerinde Live bağlantı koparılmasın, konuşma geçmişi sıfırlanmasın.
  return JSON.stringify({
    source: liveContext.source || "",
    lessonId: liveContext.lessonId || liveContext.lessonTitle || "",
    topicId: liveContext.topicId || "",
    contentId: liveContext.contentId || "",
    // Tüm kazanım listesi — bireysel currentGainIndex değişimi kabul edilir, listenin kendisi değişirse yeni oturum
    gainIds: normalizeLiveContextList(liveContext.gainIds),
    sessionPlanId: liveContext.sessionPlanId || "",
  });
};

// Canlı (Live) yolda sunucunun deterministik "video isteği köprüsü" yok: öğrenci açıkça video isteyip model aracı
// çağırmayınca (3.8 Live sürekli oturumda "teknik aksaklık, açamıyorum" diyebiliyor) sistem videoyu kendisi açar.
// Regex'ler sunucudaki wantsVideoMaterial / shouldOpenVideoForContext ile aynı.
const LIVE_VIDEO_REQUEST_RE = /(^|[^a-zçğıiöşü0-9])(video\w*|vide|[iı]zle)/i;
const LIVE_VIDEO_DONE_RE = /([iı]zledim|videoyu bitirdim|video bitti|[iı]zlemeyi (bitirdim|tamamladım))/i;
const LIVE_QUESTION_EXPLANATION_RE = /(soruyu|sorunun|bu soruyu|şu soruyu|su soruyu)/i;
// Öğrenci bir sonraki kazanıma geçmek istiyor mu — SUNUCUDAKİ KURALIN AYNISI
// (YesV3-Backend src/modules/ai/services/next.gain.rules.js → wantsNextGain; vakalar oradaki
// testte). Yazılı modda sunucu isteği doğrulayıp studentRequested işaretler; sesli (Live) modda
// araç doğrudan Gemini'den gelir ve bu kontrol panelde yapılmak zorunda. Saha (7 Eki): dar kalıp
// "3. eksik kazanıma geçelim" isteğini kilide takıp AI'yı "geçtik" dedirtirken sistemi yerinde bırakıyordu.
// NOT: JS'de \b ve \w Türkçe harfleri (ç, ı, ş…) kelime harfi saymaz — \S* ve boşluk kontrolü kullanılır.
const NEXT_GAIN_NOUN = "(ka?z?a?n[ıi]m|kzn[ıi]?m|eksi|eksğ|konu(?!ş))\\S*";
const NEXT_GAIN_ORDINALS: Record<string, number> = { birinci: 1, ikinci: 2, üçüncü: 3, ucuncu: 3, dördüncü: 4, dorduncu: 4, beşinci: 5, besinci: 5, altıncı: 6, altinci: 6, yedinci: 7, sekizinci: 8, dokuzuncu: 9, onuncu: 10 };
// Önce iş, sonra geçiş ("2-3 soru sor ve sıradaki kazanıma geçelim") ŞİMDİ geçiş değildir (saha 10 Eki, Burak)
const NEXT_GAIN_DEFERRED = /(soru|örnek|ornek|test|alıştırma|alistirma)\S*\s+(\S+\s+)?(sor|ver|çöz|coz|aç|ac|yap|getir|göster|goster)(alım|alim|elim|un|ün|sana|sene|ar|er|ır|ir|abilir|ebilir|[uüıi]p)?(?=[\s.!?,]|$)|(^|\s)(sonra|ardından|ardindan|bitince|bitirince|çözünce|cozunce|çözdükten|cozdukten)(\s+(da|de))?\s+(\S+\s+){0,2}?\S*geç/;
const wantsNextGainMessage = (message: string, currentIndex: number | null = null) => {
  const m = String(message || "").toLocaleLowerCase("tr-TR").trim();
  if (!m || /geç(tik|ti|miş)\s*m[iı]|geçme(yelim|den|sek|k istemiyorum)/.test(m) || NEXT_GAIN_DEFERRED.test(m)) return false;
  // geri/aynı yere işaret — yalnız geçişin nesnesinde ("önceki kazanıma geç", "geri geçelim")
  if (new RegExp(`(^|\\s)(önceki|evvelki|ilk|birinci|aynı|ayni|eski|bu|şu|su)\\s+(eksik\\s+)?${NEXT_GAIN_NOUN}\\s+geç|geri\\s+(geç|dön)|geç\\S*\\s+geri`).test(m)) return false;
  // numaralı/sıralı istek yalnız gerçekten SIRADAKİ numaraysa ("2. kazanımdayken 5.'ye" atlama yok)
  const numberedRe = new RegExp(`(\\d+|${Object.keys(NEXT_GAIN_ORDINALS).join("|")})\\s*\\.?\\s*(eksik\\s+)?${NEXT_GAIN_NOUN}(\\s+\\S+)?\\s+geç`);
  const numbered = m.match(numberedRe);
  if (numbered && currentIndex !== null && (NEXT_GAIN_ORDINALS[numbered[1]] || Number(numbered[1])) !== currentIndex + 2) return false;
  return new RegExp(`(diğer|diger|sıradaki|siradaki|bir sonraki|sonraki|yeni|öteki|oteki|başka|baska)\\s+${NEXT_GAIN_NOUN}`).test(m)
    || /(sıradakine|siradakine|sonrakine|diğerine|digerine)\s+geç/.test(m)
    || /^(sıradakine|siradakine|sonrakine|diğerine|digerine|öbürüne|oburune|ötekine|otekine)(\s+geç\S*)?[\s.!?]*$/.test(m)
    || numberedRe.test(m)
    || new RegExp(`${NEXT_GAIN_NOUN}\\s+geç(elim|iyoruz|ebilir\\S*|sene|mek ist\\S*|eyim|in)?(?=[\\s.!?,]|$)`).test(m);
};

// AI'nın açık geçiş TEKLİFİNE kısa onay ("sıradaki kazanıma geçelim mi?" → "evet") — sunucudaki
// studentRequestsNextGain'in teklif dalının kaba karşılığı (sesli modda sunucu yoktur).
const approvesNextGainOffer = (studentText: string, assistantText: string) => {
  const reply = String(studentText || "").toLocaleLowerCase("tr-TR").trim();
  const offer = String(assistantText || "").toLocaleLowerCase("tr-TR");
  if (!reply || reply.length > 40 || reply.split(/\s+/).length > 5) return false;
  // olumsuz/belirsiz cevap onay değildir ("geçmeyelim", "devam etmeyelim", "tamam anlamadım", "hayır")
  if (/geçme|etmeyelim|anlamadım|hayır|istemiyorum|henüz/.test(reply)) return false;
  if (!/^((evet|olur|tamam|tabi|hadi|haydi|peki)\s+)?(evet|olur|tamam|tamamdır|tabi|hadi|haydi|peki|geçelim|gecelim|geç|devam|devam edelim)([.!\s]|$)/.test(reply)) return false;
  // teklif "devam" seçeneği de içeriyorsa çıplak "devam" BU kazanımda devam demek olabilir
  if (/devam/.test(offer) && /^devam/.test(reply)) return false;
  return new RegExp(`(sıradaki|siradaki|bir sonraki|sonraki|diğer|diger|öteki|oteki)\\s+${NEXT_GAIN_NOUN}.{0,60}?(geçelim m[iı]|geçelim,? ne dersin|geçmek ister m|geçmeye hazır m|geçmemi ister m|geçmeye ne dersin|geçsek m[iı])`).test(offer);
};

// Metin bir kazanım geçişini İLAN ediyor mu ("sıradaki kazanıma geçiyoruz") — sunucudaki
// assistantAnnouncesTransition'ın kaba karşılığı; yalnız kilide takılan geçişte metni düzeltmek için.
// Kazanım adı öğrenciye kelime ortasında kesilmeden gösterilir ("…belirl", "…yalnı" — saha 10 Eki)
const gainLabel = (text: string, max = 240) => {
  const clean = String(text || "").replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  return `${cut.slice(0, Math.max(cut.lastIndexOf(" "), Math.floor(max * 0.6)))}…`;
};

// "00:12:30" → "12:30"; kazanımın video aralığı AI'ya ve öğrenciye okunur biçimde
const formatVideoRange = (start?: string, end?: string) => {
  const clean = (t?: string) => {
    const parts = String(t || "").split(":").map((x) => Number(x) || 0);
    if (!parts.length || parts.every((x) => !x)) return "";
    const [h, m, sec] = parts.length === 3 ? parts : [0, parts[0] || 0, parts[1] || 0];
    return `${h * 60 + m}:${String(sec).padStart(2, "0")}`;
  };
  const a = clean(start);
  const b = clean(end);
  return a && b ? `${a}–${b}` : a || b;
};

const announcesGainTransition = (text: string) =>
  /(sıradaki|siradaki|bir sonraki|sonraki|diğer|diger|yeni)\s+(kazanım|kazanim|eksi)\S*.{0,40}?(geçiyoruz|geciyoruz|geçtik|gectik|başlıyoruz|basliyoruz)/.test(
    String(text || "").toLocaleLowerCase("tr-TR"),
  );

const wantsLiveVideoOpen = (message: string) => {
  const normalized = String(message || "").toLocaleLowerCase("tr-TR").trim();
  if (!normalized) return false;
  return LIVE_VIDEO_REQUEST_RE.test(normalized) && !LIVE_VIDEO_DONE_RE.test(normalized) && !LIVE_QUESTION_EXPLANATION_RE.test(normalized);
};

// Canlı modda mikrofon sesi OpenAI'ye yazıya çevrilmeye gitmez; kayıt boyunca 16 kHz PCM olarak biriktirilir ve kayıt
// bitince Live oturumuna tek seferde verilir (activityStart → ses → activityEnd). Metin Gemini'nin inputTranscription'ından gelir.
const LIVE_INPUT_SAMPLE_RATE = 16000;
const LIVE_INPUT_CHUNK_SAMPLES = 8000; // 0,5 sn
const LIVE_INPUT_MIN_PEAK = 400; // 32767 üzerinden; altı sessizlik sayılır
interface LiveAudioTurn {
  pcm16: Int16Array;
  userMessageId: string;
}
const downsampleFloat32ToInt16 = (input: Float32Array, fromRate: number, toRate: number) => {
  if (fromRate === toRate) {
    const out = new Int16Array(input.length);
    for (let i = 0; i < input.length; i += 1) out[i] = Math.max(-32768, Math.min(32767, Math.round(input[i] * 32767)));
    return out;
  }
  const ratio = fromRate / toRate;
  const outLength = Math.floor(input.length / ratio);
  const out = new Int16Array(outLength);
  for (let i = 0; i < outLength; i += 1) {
    const start = Math.floor(i * ratio);
    const end = Math.min(input.length, Math.floor((i + 1) * ratio));
    let sum = 0;
    for (let j = start; j < end; j += 1) sum += input[j];
    const sample = end > start ? sum / (end - start) : 0;
    out[i] = Math.max(-32768, Math.min(32767, Math.round(sample * 32767)));
  }
  return out;
};
const int16ToBase64 = (samples: Int16Array) => {
  const bytes = new Uint8Array(samples.buffer, samples.byteOffset, samples.byteLength);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000)));
  }
  return window.btoa(binary);
};
const concatInt16 = (chunks: Int16Array[]) => {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const out = new Int16Array(total);
  let offset = 0;
  chunks.forEach((chunk) => { out.set(chunk, offset); offset += chunk.length; });
  return out;
};
const pcmPeak = (samples: Int16Array) => {
  let peak = 0;
  for (let i = 0; i < samples.length; i += 1) { const v = Math.abs(samples[i]); if (v > peak) peak = v; }
  return peak;
};

const buildAITeacherLiveClientContent = (text: string) => ({
  turns: [
    {
      role: "user" as const,
      parts: [{ text: String(text || "").trim() || "Devam edelim." }],
    },
  ],
  turnComplete: true,
});

const timecodeToSeconds = (timecode?: string) => {
  if (!timecode) return 0;

  const parts = String(timecode)
    .split(":")
    .map((part) => Number.parseInt(part, 10));

  if (parts.some((part) => Number.isNaN(part))) return 0;
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];

  return 0;
};

const getVimeoId = (url: string) => {
  const cleaned = String(url || "").trim().replace(/^@+/, "");
  if (/^\d{6,}$/.test(cleaned)) return cleaned;
  const match = cleaned.match(/(?:player\.vimeo\.com\/video\/|vimeo\.com\/(?:.*\/)?(?:video\/)?)(\d{6,})/);
  return match?.[1] || "";
};

const getYoutubeId = (url: string) => {
  const cleaned = String(url || "").trim().replace(/^@+/, "");
  const match =
    cleaned.match(/[?&]v=([A-Za-z0-9_-]{11})/) ||
    cleaned.match(/youtu\.be\/([A-Za-z0-9_-]{11})/) ||
    cleaned.match(/youtube(?:-nocookie)?\.com\/embed\/([A-Za-z0-9_-]{11})/);
  return match?.[1] || "";
};

const resolveTutorMediaUrl = (url: string) => {
  const cleaned = String(url || "").trim().replace(/^@+/, "");
  if (!cleaned || /^https?:\/\//i.test(cleaned)) return cleaned;

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "";
  if (!apiBaseUrl) return cleaned;

  try {
    const apiUrl = new URL(apiBaseUrl, window.location.origin);
    if (cleaned.startsWith("/api/")) return `${apiUrl.origin}${cleaned}`;
    return `${apiBaseUrl.replace(/\/$/, "")}${cleaned.startsWith("/") ? cleaned : `/${cleaned}`}`;
  } catch (_) {
    return cleaned;
  }
};

const ShinyStatusBubble = ({ text }: { text: string }) => (
  <div className="flex justify-start">
    <style>
      {`
        @keyframes aiTutorShine {
          0% { transform: translateX(-120%); }
          100% { transform: translateX(140%); }
        }
        .ai-tutor-shiny::after {
          content: "";
          position: absolute;
          inset: 0;
          width: 55%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.9), transparent);
          animation: aiTutorShine 1.45s ease-in-out infinite;
        }
      `}
    </style>
    <div className="ai-tutor-shiny relative overflow-hidden rounded-2xl rounded-tl-md border border-orange-100 bg-orange-50 px-3 py-2 text-sm font-medium text-orange-700 shadow-sm">
      <span className="relative z-10 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" />
        {text}
      </span>
    </div>
  </div>
);

const VoiceBubbleControl = ({
  expanded,
  animating,
  disabled,
  level,
  onClick,
  onCancel,
}: {
  expanded: boolean;
  animating: boolean;
  disabled: boolean;
  level: number;
  onClick: () => void;
  onCancel?: () => void;
}) => {
  const safeLevel = Math.min(1, Math.max(AI_TUTOR_VOICE_LEVEL_IDLE, Number(level) || AI_TUTOR_VOICE_LEVEL_IDLE));

  return (
    <>
      <style>
        {`
          .ai-tutor-voice-control {
            --voice-level: 0.08;
            overflow: hidden;
            transition:
              width 320ms cubic-bezier(0.22, 1, 0.36, 1),
              flex-basis 320ms cubic-bezier(0.22, 1, 0.36, 1),
              background-color 220ms ease,
              border-radius 220ms ease,
              box-shadow 220ms ease;
          }

          .ai-tutor-voice-mic {
            transform: scale(calc(1 + (var(--voice-level) * 0.12)));
            box-shadow:
              inset 0 0 0 1px rgba(255,255,255,0.34),
              0 0 0 1.5px rgba(255,255,255,0.18);
          }

          .ai-tutor-voice-ring {
            content: "";
            position: absolute;
            inset: -3px;
            border-radius: 1rem;
            border: 2px solid rgba(255,255,255,0.72);
            opacity: 0;
            transform: scale(0.94);
            transition: opacity 180ms ease, transform 180ms ease;
            pointer-events: none;
          }

          .ai-tutor-voice-control.is-animating .ai-tutor-voice-ring {
            opacity: 0.95;
            transform: scale(calc(1.02 + (var(--voice-level) * 0.28)));
            box-shadow: 0 0 calc(4px + (var(--voice-level) * 12px)) rgba(255,255,255,0.32);
          }

          .ai-tutor-voice-dot {
            transform: scale(1);
            transition: transform 220ms ease, opacity 220ms ease;
          }

          .ai-tutor-voice-control.is-animating .ai-tutor-voice-dot {
            animation: aiTutorVoiceDot 760ms ease-in-out infinite;
          }

          .ai-tutor-voice-control.is-animating .ai-tutor-voice-dot:nth-child(2) { animation-delay: 90ms; }
          .ai-tutor-voice-control.is-animating .ai-tutor-voice-dot:nth-child(3) { animation-delay: 180ms; }
          .ai-tutor-voice-control.is-animating .ai-tutor-voice-dot:nth-child(4) { animation-delay: 270ms; }

          @keyframes aiTutorVoiceDot {
            0%, 100% { transform: scaleY(0.78); opacity: 0.82; }
            45% { transform: scaleY(calc(1.05 + (var(--voice-level) * 0.42))); opacity: 1; }
          }
        `}
      </style>
      <div
        className={`ai-tutor-voice-control ${animating ? "is-animating" : ""} flex h-12 items-center bg-orange-500 shadow-sm ${
          expanded ? "flex-1 gap-2 rounded-[24px] px-2" : "w-12 flex-none gap-0 rounded-2xl px-0"
        }`}
        style={{ "--voice-level": safeLevel } as any}
      >
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          title={expanded ? "Kaydı durdur" : "Mikrofonla konuş"}
          aria-label={expanded ? "Kaydı durdur" : "Mikrofonla konuş"}
          className={`ai-tutor-voice-mic relative flex flex-shrink-0 items-center justify-center rounded-2xl bg-orange-500 text-white transition-all duration-300 ease-out hover:bg-orange-600 disabled:cursor-default disabled:opacity-80 ${
            expanded ? "h-9 w-9" : "h-12 w-12"
          }`}
        >
          <span className="ai-tutor-voice-ring" />
          <Mic className={`relative z-10 transition-all duration-300 ${expanded ? "h-4 w-4" : "h-5 w-5"}`} />
        </button>
        <span
          className={`flex min-w-0 flex-1 items-center justify-center gap-1.5 px-2 transition-all duration-300 ease-out ${
            expanded ? "scale-100 opacity-100" : "pointer-events-none scale-95 opacity-0"
          }`}
        >
          {[0, 1, 2, 3].map((dot) => (
            <span
              key={dot}
              className="ai-tutor-voice-dot h-6 w-4 rounded-full bg-white shadow-[0_1px_5px_rgba(0,0,0,0.16)]"
            />
          ))}
        </span>
        <button
          type="button"
          onClick={onCancel}
          title="Sesli modu kapat"
          aria-label="Sesli modu kapat"
          tabIndex={expanded ? 0 : -1}
          className={`flex h-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/16 text-white transition-all duration-300 ease-out hover:bg-white/24 ${
            expanded ? "w-8 opacity-100" : "pointer-events-none w-0 opacity-0"
          }`}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </>
  );
};

const AITutor = ({
  isOpen,
  context,
  onClose,
  sidebarExpanded = true,
  onShowPdfOnCanvas,
  onOpenSimulation,
}: AITutorProps) => {
  const [messages, setMessages] = useState<TutorMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [audioMode, setAudioMode] = useState<"live" | "tts" | "browser" | "none">("tts"); // Kullanıcının seçtiği AI öğretmen ses modeli
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceBubbleActive, setVoiceBubbleActive] = useState(false);
  const [voiceConversationActive, setVoiceConversationActive] = useState(false);
  const [voiceLevel, setVoiceLevel] = useState(AI_TUTOR_VOICE_LEVEL_IDLE);
  const [isPreparingSpeech, setIsPreparingSpeech] = useState(false);
  const [speechNeedsGesture, setSpeechNeedsGesture] = useState(false);
  const [speechError, setSpeechError] = useState("");
  const [lastAssistantMessage, setLastAssistantMessage] = useState("");
  const [isTypingMessage, setIsTypingMessage] = useState(false);
  const [isGainCardExpanded, setIsGainCardExpanded] = useState(false);
  const [activeMaterial, setActiveMaterial] = useState<ActiveMaterial>({ type: "empty" });
  // Yarım kalan test taslakları (test anahtarı → cevaplar/konum). Materyal değişince
  // test bileşeni unmount olur; taslak burada yaşamaya devam eder.
  const practiceTestDraftsRef = useRef<Record<string, PracticeTestDraft>>({});
  // Ekranda başka materyal açıkken "Teste dön" düğmesini gösterebilmek için son açık test.
  const [pendingTest, setPendingTest] = useState<Extract<ActiveMaterial, { type: "test" }> | null>(null);
  const [resolvedContext, setResolvedContext] = useState<Record<string, any>>(context);
  // Oturum bağlamının ANLIK kopyası — istekler buradan kurulur. State'i okuyan kapanışlar bir önceki
  // render'ın değerini görüyordu: panel kapanırken bağlam eski konuya ayarlanıyor, yeni konu açılınca
  // ilk istek o eski değerle gidiyor ve sunucunun cevabıyla oturum sonuna kadar kalıcılaşıyordu (saha
  // 12 Eki, Gamze: panelde Cümlede Anlam - 3, AI'da Paragrafın Yapısı - 1; 15 oturumun 3'ü böyle).
  const resolvedContextRef = useRef<Record<string, any>>(context);
  const setResolvedContextNow = (
    next: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>),
  ) => {
    const value = typeof next === "function" ? next(resolvedContextRef.current) : next;
    resolvedContextRef.current = value;
    setResolvedContext(value);
  };
  const pendingSpeechReadyRef = useRef<{ durationMs: number } | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  const recordingSilenceIntervalRef = useRef<number | null>(null);
  const recordingMaxTimeoutRef = useRef<number | null>(null);
  const recordingSilenceStartRef = useRef<number | null>(null);
  const recordingSpeechDetectedRef = useRef(false);
  const recordingAudioContextRef = useRef<AudioContext | null>(null);
  const recordingAudioSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  // Canlı modda kayıt boyunca biriken 16 kHz PCM (Live'a doğrudan ses girişi)
  const livePcmCaptureRef = useRef<{
    context: AudioContext;
    source: MediaStreamAudioSourceNode;
    processor: ScriptProcessorNode;
    chunks: Int16Array[];
  } | null>(null);
  const stopRecordingRef = useRef<() => void>(() => undefined);
  // Tamamlanmış quiz'in kazanımı — move_to_next_gain gelene kadar aynı teste tekrar açılmasını engeller
  const completedTestGainIdRef = useRef<string | null>(null);
  // Son kazanım ilerleme zamanı — quiz-geçiş + move_to_next_gain aynı anda tetiklenip kazanım ATLAMASINI engeller
  const lastGainAdvanceAtRef = useRef<number>(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const streamAudioContextRef = useRef<AudioContext | null>(null);
  const streamAudioSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const streamPlaybackTimeRef = useRef(0);
  const pcmRemainderRef = useRef<Uint8Array | null>(null);
  const hasQueuedAudibleStreamRef = useRef(false);
  const voiceLevelResetTimerRef = useRef<number | null>(null);
  const speechRequestIdRef = useRef(0);
  const liveConnectionRef = useRef<AITeacherLiveConnection | null>(null);
  const voiceEnabledRef = useRef(voiceEnabled);
  const voiceConversationActiveRef = useRef(false);
  const voiceCancelRequestedRef = useRef(false);
  const typingFlushRef = useRef<(() => void) | null>(null);
  const typingCancelRef = useRef<(() => void) | null>(null);
  const activeTypingMessageRef = useRef<{ id: string; fullText: string } | null>(null);
  const startedRef = useRef(false);
  const messageIdRef = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const audioModeRef = useRef<"live" | "tts" | "browser" | "none">("tts");
  // Başarısız pekiştirme testinin kazanımı — öğrenci geçemeden model move_to_next_gain çağırsa bile İLERLEME KİLİTLİ
  const lastFailedTestGainIdRef = useRef<string | null>(null);
  // Canlı modda öğrencinin isteğiyle kazanım geçildiğinde bağlantı kapatılıyor (yeni grounding için) ve 3.8 Live aracı
  // sessiz turda çağırdığı için yeni kazanım TANITILMIYORDU; ekran boşalınca gizli mesajla tanıtım başlatılır.
  const pendingGainKickoffRef = useRef<number | null>(null);
  // Panel meşgulken (AI yazıyor/konuşuyor/yanıt bekleniyor) gelen pekiştirme testi SONUCU
  // mesajı. Eskiden sessizce düşüyordu → AI testin geçildiğini ve sistemin sıradaki kazanıma
  // geçtiğini HİÇ öğrenmiyor, eski konuşmayı sürdürüyordu (saha 7 Eki, yesakademi: test 3/3
  // geçildi, gösterge 2. kazanıma geçti, AI 1. kazanımın test sorusunu anlatmaya devam etti).
  // Panel boşalınca gönderilir.
  // Birden çok mesaj bekleyebilir (test sonucu + düğmeyle geçiş) — sırayla gider, biri ötekini ezmez.
  const pendingHiddenMessagesRef = useRef<Array<{ text: string; context?: Record<string, any>; afterReply?: () => void; onError?: () => void }>>([]);
  // ÖNCE ÖLÇ (sahibin kararı, 8 Eki): kazanımın başında AI kısa tanıtımı bitirince test kendiliğinden
  // açılır. Tanıtım sürerken açılırsa ses/yazı kesilirdi — panel boşalınca açılır (bkz. effect).
  const pendingGainStartTestRef = useRef<number | null>(null);
  const [gainStartTick, setGainStartTick] = useState(0);
  // OTURUM DÖNEMİ: panel her açılış/kapanış/sıfırlamada artar. Geç dönen sunucu yanıtları (test
  // puanı, geçiş sonucu) dönem değiştiyse ekrana ✓/not basmaz, kuyruğa mesaj koymaz — yoksa
  // kapanmış oturumun sonucu bir sonraki oturuma taşınıyordu (bağımsız inceleme, 7 Eki).
  const sessionEpochRef = useRef(0);
  // Süren canlı tur SESLİ bir öğrenci turu mu: sesli turda etkileşim sayacı tur BİTİNCE artar;
  // geçiş o turun içinde gelirse sayaç bir eksik kalıyordu.
  const liveAudioTurnRef = useRef(false);
  // Sesli turda "öğrenci istemedi" diye bekletilen geçiş: döküm (inputTranscription) araçtan SONRA
  // gelebildiği için karar tur sonunda, tam metinle yeniden verilir (ikinci inceleme, 7 Eki).
  const deferredLiveMoveRef = useRef<AITutorToolCall | null>(null);
  // Beklemeye alınınca bir kez yeniden çizim tetikler: meşguliyet bu render'dan önce bitmişse
  // hiçbir durum değişmez ve bekleyen mesaj takılı kalırdı.
  const [hiddenQueueTick, setHiddenQueueTick] = useState(0);
  // Bu oturumda KAPANAN (✓) ve testsiz geçildiği için AÇIK KALAN eksikler — kazanım kartında
  // gösterilir. Saha şikâyeti (7 Eki): "eksiğimi giderdiğim görünmüyor".
  const [resolvedGainIds, setResolvedGainIds] = useState<string[]>([]);
  const [leftOpenGainIds, setLeftOpenGainIds] = useState<string[]>([]);
  // Bu oturumda pekiştirme testi GEÇİLEN kazanımlar — inceleme ekranında "Tekrar çöz" yerine
  // "testi geçtin" gösterilir (stage sahası 8 Eki: 1 yanlışla geçilen testte "Tekrar çöz" kalıyordu).
  const [passedTestGainIds, setPassedTestGainIds] = useState<string[]>([]);
  // "Sıradaki kazanım" düğmesi testi çözülmemiş kazanımda önce onay ister
  const [confirmSkipGain, setConfirmSkipGain] = useState(false);
  // Konu bitti: son kazanım kapandı / testi geçildi ve oturum sunucuda tamamlandı — "Konuyu bitir" kalkar. Eskiden son
  // kazanımda kapanış hiç denenmiyordu; eksik açık kalıp sonraki oturumda yine geliyordu (sahibin 19 Eki tespiti).
  const [topicFinished, setTopicFinished] = useState(false);
  const finishingTopicRef = useRef(false);
  // Panelden çıkışta kapanış bu oturumda BİR KEZ denenir (X ve sayfa değişimi aynı yoldan)
  const exitClosureEpochRef = useRef(-1);
  // Canlı tur sürüyor mu / tur içinde kazanım geçildi mi: geçişte bağlantı turun İÇİNDEN kapatılırsa dinleyiciler silinip
  // tur yetim kalıyordu ("Hazırlanıyor…" 45 sn asılı, sonra aynı mesaj yeniden gönderilip kazanım atlatılabiliyordu).
  const liveTurnActiveRef = useRef(false);
  const liveAdvanceDuringTurnRef = useRef(false);
  // ÖĞRETİLMEDEN-ATLAMA KİLİDİ: aktif kazanımda öğrencinin yazdığı mesaj sayısı (öğretim etkileşimi ölçüsü).
  // <2 ise kazanım daha yeni geldi → move_to_next_gain UYGULANMAZ (açık "atla/biliyorum" hariç).
  const userTurnsOnGainRef = useRef(0);
  // Şu an AÇIK (devam eden) testin kazanımı — orta-testte soruların yenilenmesini engeller.
  const openTestGainIdRef = useRef<string | null>(null);
  // Tanım-düzeyi kazanım bildirimi kazanım başına bir kez yapılır (boş test döngüsü önlenir)
  const definitionOnlyNotifiedRef = useRef<Set<string>>(new Set());
  // Testin AÇILAMADIĞI denenerek görülen kazanımlar (tanım-düzeyi ya da 3'ten az taze soru).
  // Her turda context'e gider; sunucu hem prompt'a kalıcı uyarı koyar hem aracı bastırır.
  const nonTestableGainIdsRef = useRef<string[]>([]);
  // Kazanım başına BAŞARISIZ test sayısı. İlk başarısızlıkta AI doğru şıkkı SÖYLEMEZ, ipucu
  // verip yeniden denetir; ikinciden itibaren tam çözümü anlatır. Sahibin kararı (9 Ağu):
  // eskiden AI ilk turda cevabı söylüyordu, öğrenci 10 sn sonra işaretleyip %100 yapıyordu —
  // kapanış ölçmeye değil, açıklamayı dinleyip yeniden işaretlemeye dayanıyordu.
  const failedAttemptsByGainRef = useRef<Record<string, number>>({});
  // KALINAN TESTİN İNCELEMESİ — SORU SORU (sahibin 20 Eki kararı): ekrandaki yanlışı AI konuşur; öğrenci o soru hakkında
  // yazınca "Bu soruyu anladım" açılır ve sıradaki yanlışa geçilir; hepsi bitmeden test yeniden başlamaz. Eskiden "Tekrar çöz"
  // aynı yanlışları hemen açıyordu (TED 19 Eki: aynı 3 soru 10 sn'de 3/3); mesaj sayan ilk sürüm de yetmedi (stage 20 Eki:
  // 4 yanlışta 4 mesajın hepsi tek soru üzerineydi). Yanlışlar öğrencinin cevaplarıyla her turda modelin context'inde durur.
  const failedReviewRef = useRef<{
    gainId: string;
    revealAnswer: boolean;
    questions: FailedReviewQuestion[];
    currentNumber: number;
    discussed: number[];
  } | null>(null);
  const [reviewState, setReviewState] = useState<{ gainId: string; currentNumber: number; discussed: number[] } | null>(null);
  const lastServedTestRef = useRef<{ gainId: string; questions: any[] } | null>(null);
  const syncReviewState = () => {
    const review = failedReviewRef.current;
    setReviewState(review
      ? { gainId: review.gainId, currentNumber: review.currentNumber, discussed: [...review.discussed] }
      : null);
  };
  const clearFailedReview = () => {
    failedReviewRef.current = null;
    setReviewState(null);
  };
  // Bu kazanımda test yeniden açılabilir mi: inceleme yoksa evet; varsa bütün yanlışlar "anladım"la geçilmiş olmalı
  const reviewAllDone = (gainId?: string) => {
    const review = failedReviewRef.current;
    if (!review || (gainId && review.gainId !== gainId)) return true;
    return review.questions.every((q) => review.discussed.includes(q.number));
  };
  // Öğrencinin bu kazanımdaki her mesajı: öğretim sayacı
  const countUserTurn = () => {
    userTurnsOnGainRef.current += 1;
  };
  // Öğrencinin PDF'te ekranda baktığı gerçek sayfa (Faz 3: sayfa görüntüsü bu numaradan seçilir)
  const activePdfPageRef = useRef<number | null>(null);
  // Mesaj kutusu — yanıt bitince otomatik odaklanır (masaüstünde; mobilde klavye kendiliğinden açılmasın)
  const chatInputRef = useRef<HTMLInputElement | null>(null);
  // Mobil: sohbet ve materyal yan yana sığmıyor — tek seferde biri tam ekran gösterilir
  const [mobileView, setMobileView] = useState<"chat" | "workspace">("chat");
  // Karalama tahtası: AI anlatırken öğrenci elle işlem yapabilsin (soru çözme
  // ekranındaki kalemin karşılığı). Panel kapanınca kapalı başlaması için isOpen'a
  // bağlı sıfırlanır; tahta içeriği canvas'ta tutulur, panel kapanınca gider.
  const [showDrawingBoard, setShowDrawingBoard] = useState(false);
  // Tam ekran: panel uygulama başlığını ve sol menüyü de kaplar. Tarayıcının
  // requestFullscreen'i KULLANILMAZ — iOS Safari onu <video> dışında desteklemiyor
  // (aynı tuzak AI Yardım panelinde telefonda düğmeyi ölü bırakmıştı).
  const [isExpanded, setIsExpanded] = useState(false);
  // Ekranda AÇIK olan pekiştirme testinin soruları — modele context'te verilir ki soru içeriği/numara uydurmasın
  const openTestQuestionsRef = useRef<Array<{ number: number; text: string; options?: Record<string, string>; correctAnswer?: string; solution?: string; solutionSource?: string; visualDescription?: string }>>([]);
  // Test açılmadan ÖNCE öğrenci sesli konuşuyor muydu? Test bitince sesi ancak o zaman geri aç (text kullanıcıyı zorlama)
  const voiceActiveBeforeTestRef = useRef(false);
  // Sohbet oturumu kimliği — gözlem/transkript gruplaması için (panel açılınca/sıfırlanınca yenilenir)
  const conversationIdRef = useRef<string>("");
  const newConversationId = () => {
    try {
      if (typeof crypto !== "undefined" && (crypto as any).randomUUID) return `conv_${(crypto as any).randomUUID()}`;
    } catch {}
    return `conv_${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`;
  };
  const lastUserMessageRef = useRef<string>("");
  // OTORİTER kazanım index'i (senkron): sunucu yanıtındaki eski context yankısı ilerlemeyi geri EZEMEZ
  const gainIndexRef = useRef<number>(Number((context as any)?.currentGainIndex) || 0);
  const mergeServerContext = (nextContext: Record<string, any>) => ({
    ...nextContext,
    currentGainIndex: Math.max(Number(nextContext?.currentGainIndex) || 0, gainIndexRef.current),
  });

  const createMessageId = () => {
    messageIdRef.current += 1;
    return `ai-tutor-message-${messageIdRef.current}`;
  };

  const updateVoiceLevel = (level: number) => {
    const normalizedLevel = Math.min(1, Math.max(AI_TUTOR_VOICE_LEVEL_IDLE, Number(level) || AI_TUTOR_VOICE_LEVEL_IDLE));
    setVoiceLevel((currentLevel) => Math.max(normalizedLevel, currentLevel * 0.72));

    if (voiceLevelResetTimerRef.current !== null) {
      window.clearTimeout(voiceLevelResetTimerRef.current);
    }
    voiceLevelResetTimerRef.current = window.setTimeout(() => {
      setVoiceLevel(AI_TUTOR_VOICE_LEVEL_IDLE);
      voiceLevelResetTimerRef.current = null;
    }, 220);
  };

  const flushCurrentAssistantTyping = () => {
    if (typingFlushRef.current) {
      typingFlushRef.current();
    } else if (activeTypingMessageRef.current?.fullText) {
      const { id, fullText } = activeTypingMessageRef.current;
      setMessages((prev) =>
        prev.map((message) =>
          message.id === id ? { ...message, content: fullText } : message,
        ),
      );
    }
    activeTypingMessageRef.current = null;
    typingFlushRef.current = null;
    typingCancelRef.current = null;
    pendingSpeechReadyRef.current = null;
    setIsTypingMessage(false);
  };

  const stopCurrentAudio = () => {
    speechRequestIdRef.current += 1;

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }

    streamAudioSourcesRef.current.forEach((source) => {
      try {
        source.stop();
      } catch {
        // Source may already be stopped.
      }
    });
    streamAudioSourcesRef.current.clear();
    streamPlaybackTimeRef.current = 0;
    pcmRemainderRef.current = null;
    hasQueuedAudibleStreamRef.current = false;
    pendingSpeechReadyRef.current = null;
    setVoiceLevel(AI_TUTOR_VOICE_LEVEL_IDLE);
    if (voiceLevelResetTimerRef.current !== null) {
      window.clearTimeout(voiceLevelResetTimerRef.current);
      voiceLevelResetTimerRef.current = null;
    }

    const streamAudioContext = streamAudioContextRef.current;
    streamAudioContextRef.current = null;
    if (streamAudioContext && streamAudioContext.state !== "closed") {
      streamAudioContext.close().catch(() => undefined);
    }
  };

  const closeLiveConnection = () => {
    const connection = liveConnectionRef.current;
    liveConnectionRef.current = null;
    if (!connection?.socket) return;

    connection.socket.onmessage = null;
    connection.socket.onerror = null;
    connection.socket.onclose = null;
    if (connection.socket.readyState === WebSocket.OPEN || connection.socket.readyState === WebSocket.CONNECTING) {
      connection.socket.close();
    }
  };

  const getStreamAudioContext = (sampleRate: number) => {
    const AudioContextClass = window.AudioContext || (window as Window & typeof globalThis & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) throw new Error("Tarayıcı anlık ses oynatmayı desteklemiyor.");

    let audioContext = streamAudioContextRef.current;
    if (!audioContext || audioContext.state === "closed") {
      audioContext = new AudioContextClass({ sampleRate });
      streamAudioContextRef.current = audioContext;
      streamPlaybackTimeRef.current = 0;
    }

    if (audioContext.state === "suspended") {
      audioContext.resume().catch(() => undefined);
    }

    return audioContext;
  };

  const queuePcmAudioChunk = (chunk: Uint8Array, sampleRate: number) => {
    let pcmChunk = chunk;
    if (pcmRemainderRef.current?.byteLength) {
      const combined = new Uint8Array(pcmRemainderRef.current.byteLength + chunk.byteLength);
      combined.set(pcmRemainderRef.current, 0);
      combined.set(chunk, pcmRemainderRef.current.byteLength);
      pcmChunk = combined;
      pcmRemainderRef.current = null;
    }

    const usableByteLength = pcmChunk.byteLength - (pcmChunk.byteLength % 2);
    if (usableByteLength < pcmChunk.byteLength) {
      pcmRemainderRef.current = pcmChunk.slice(usableByteLength);
    }
    if (usableByteLength <= 0) return 0;

    let audioStartByte = 0;
    if (!hasQueuedAudibleStreamRef.current) {
      const silenceScanView = new DataView(pcmChunk.buffer, pcmChunk.byteOffset, usableByteLength);
      let firstAudibleSample = -1;
      for (let index = 0; index < usableByteLength / 2; index += 1) {
        if (Math.abs(silenceScanView.getInt16(index * 2, true)) > 450) {
          firstAudibleSample = index;
          break;
        }
      }

      if (firstAudibleSample < 0) return 0;

      const prerollSamples = Math.floor(sampleRate * 0.035);
      audioStartByte = Math.max(0, firstAudibleSample - prerollSamples) * 2;
      hasQueuedAudibleStreamRef.current = true;
    }

    const audioByteLength = usableByteLength - audioStartByte;
    if (audioByteLength <= 0) return 0;

    const audioContext = getStreamAudioContext(sampleRate);
    const sampleCount = audioByteLength / 2;
    const audioBuffer = audioContext.createBuffer(1, sampleCount, sampleRate);
    const channelData = audioBuffer.getChannelData(0);
    const view = new DataView(pcmChunk.buffer, pcmChunk.byteOffset + audioStartByte, audioByteLength);
    let squareSum = 0;

    for (let index = 0; index < sampleCount; index += 1) {
      const sample = Math.max(-1, Math.min(1, view.getInt16(index * 2, true) / 32768));
      channelData[index] = sample;
      squareSum += sample * sample;
    }
    updateVoiceLevel(Math.sqrt(squareSum / sampleCount) * 5.5);

    const source = audioContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(audioContext.destination);

    const now = audioContext.currentTime;
    // Daha geniş buffer (120ms lookahead) — ağ jitter'ı nedeniyle ses kesintilerini önler
    const minLookahead = 0.12;
    const startAt = Math.max(streamPlaybackTimeRef.current || now + minLookahead, now + minLookahead);
    source.start(startAt);
    streamPlaybackTimeRef.current = startAt + audioBuffer.duration;

    streamAudioSourcesRef.current.add(source);
    source.onended = () => {
      streamAudioSourcesRef.current.delete(source);
    };

    return audioBuffer.duration * 1000;
  };

  const cleanupRecordingMonitor = () => {
    if (recordingSilenceIntervalRef.current !== null) {
      window.clearInterval(recordingSilenceIntervalRef.current);
      recordingSilenceIntervalRef.current = null;
    }
    if (recordingMaxTimeoutRef.current !== null) {
      window.clearTimeout(recordingMaxTimeoutRef.current);
      recordingMaxTimeoutRef.current = null;
    }

    try {
      recordingAudioSourceRef.current?.disconnect();
    } catch {
      // Source may already be disconnected.
    }
    recordingAudioSourceRef.current = null;

    const audioContext = recordingAudioContextRef.current;
    recordingAudioContextRef.current = null;
    if (audioContext && audioContext.state !== "closed") {
      audioContext.close().catch(() => undefined);
    }

    recordingSilenceStartRef.current = null;
    recordingSpeechDetectedRef.current = false;
    setVoiceLevel(AI_TUTOR_VOICE_LEVEL_IDLE);
  };

  const startRecordingSilenceMonitor = (stream: MediaStream) => {
    recordingMaxTimeoutRef.current = window.setTimeout(() => stopRecordingRef.current(), AI_TUTOR_RECORDING_MAX_MS);

    const AudioContextClass = window.AudioContext || (window as Window & typeof globalThis & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;

    const audioContext = new AudioContextClass();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 1024;
    const samples = new Uint8Array(analyser.fftSize);
    source.connect(analyser);

    recordingAudioContextRef.current = audioContext;
    recordingAudioSourceRef.current = source;
    recordingSilenceStartRef.current = null;
    recordingSpeechDetectedRef.current = false;

    recordingSilenceIntervalRef.current = window.setInterval(() => {
      analyser.getByteTimeDomainData(samples);
      let squareSum = 0;

      for (let index = 0; index < samples.length; index += 1) {
        const centered = (samples[index] - 128) / 128;
        squareSum += centered * centered;
      }

      const rms = Math.sqrt(squareSum / samples.length);
      updateVoiceLevel((rms - 0.012) * 11);
      const now = Date.now();
      if (rms >= AI_TUTOR_RECORDING_SPEECH_RMS) {
        recordingSpeechDetectedRef.current = true;
        recordingSilenceStartRef.current = null;
        return;
      }

      if (!recordingSpeechDetectedRef.current) return;
      if (!recordingSilenceStartRef.current) {
        recordingSilenceStartRef.current = now;
        return;
      }

      if (now - recordingSilenceStartRef.current >= AI_TUTOR_RECORDING_SILENCE_MS) {
        stopRecordingRef.current();
      }
    }, 180);
  };

  const playBrowserSpeechFallback = (text: string, onReady?: (durationMs: number) => void) => {
    if (!("speechSynthesis" in window)) {
      throw new Error("Tarayıcı sesli okuma desteklemiyor.");
    }

    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    const voices = window.speechSynthesis.getVoices();
    const turkishVoice = voices.find((voice) => voice.lang?.toLowerCase().startsWith("tr"));
    if (turkishVoice) {
      utterance.voice = turkishVoice;
    }
    utterance.lang = "tr-TR";
    utterance.rate = 0.95;
    utterance.pitch = 1;
    utterance.onstart = () => {
      setIsSpeaking(true);
      setIsPreparingSpeech(false);
    };
    utterance.onend = () => {
      setIsSpeaking(false);
      setIsPreparingSpeech(false);
    };
    utterance.onerror = () => {
      setIsSpeaking(false);
      setIsPreparingSpeech(false);
      setSpeechNeedsGesture(true);
    };
    onReady?.(estimateSpeechDurationMs(text));
    window.speechSynthesis.speak(utterance);
  };

  const getRequestContext = () => {
    const rc = resolvedContextRef.current;
    return {
      ...context,
      ...rc,
      gainId: rc.gainId || context.gainId,
      gainIds: rc.gainIds || context.gainIds,
      gains: rc.gains || context.gains,
      gainText: rc.gainText || context.gainText,
      topicTitle: rc.topicTitle || context.topicTitle,
      contentTitle: rc.contentTitle || context.contentTitle,
      lessonTitle: rc.lessonTitle || context.lessonTitle,
      unitTitle: rc.unitTitle || context.unitTitle,
      topicId: rc.topicId || context.topicId,
      contentId: rc.contentId || context.contentId,
      pdfUrl: rc.pdfUrl || context.pdfUrl,
      relatedMaterials: rc.relatedMaterials || context.relatedMaterials,
      source: rc.source || context.source,
      // AI ekranda ne açık olduğunu bilsin — zaten açık materyali "açalım mı" diye teklif etmesin
      activeMaterialType: activeMaterial.type,
      // PDF açıkken ekranda gerçekten bakılan sayfa — sayfa görüntüsü ve prompt bu numarayı esas alır
      activePdfPageNumber: activeMaterial.type === "pdf" ? activePdfPageRef.current || undefined : undefined,
      // sessionPlanId her zaman açıkça koru — büyük resolvedContext içinde kaybolmasın
      sessionPlanId: context.sessionPlanId || rc.sessionPlanId || undefined,
      // sessionPlanCtx büyük obje — backend'e gönderme, sadece sessionPlanId yeterli
      sessionPlanCtx: undefined,
      // isPracticeResult TEK TURA özel — backend echo'suyla resolvedContext'e yapışıp
      // geçiş/test korumalarını kalıcı kilitliyordu; normal isteklerde daima temiz gider
      isPracticeResult: undefined,
      // isGainTransition da TEK TURA özel (geçiş bildirimi) — sonraki turlara taşınmaz
      isGainTransition: undefined,
      // Ekranda açık pekiştirme testi varsa soruları modele ver (soru içeriği/numara uydurmasın)
      openTestQuestions: openTestQuestionsRef.current.length ? openTestQuestionsRef.current : undefined,
      // Kalınan testin yanlışları (öğrencinin cevaplarıyla) — test yeniden başlayana kadar her turda
      failedTestReview: failedReviewRef.current
        ? {
            revealAnswer: failedReviewRef.current.revealAnswer,
            currentNumber: failedReviewRef.current.currentNumber,
            discussed: failedReviewRef.current.discussed,
            questions: failedReviewRef.current.questions,
          }
        : undefined,
      // Gözlem: sohbet oturumu kimliği (transkript gruplaması)
      conversationId: conversationIdRef.current || undefined,
      // Testi AÇILAMADIĞI denenip görülen kazanımlar. Sunucu bunu iki iş için kullanır:
      // context'e kalıcı "bu kazanımda test yok" satırı koyar ve open_practice_test çağrısını
      // düşürür. Tek seferlik gizli mesaj yetmiyordu — model uyarıdan sonra aracı yine çağırdı.
      nonTestableGainIds: nonTestableGainIdsRef.current.length ? nonTestableGainIdsRef.current : undefined,
      // Panel geçiş köprüsünü sistem notu olarak gösterir ve yeni kazanımı AI'ya tanıttırır (sunucu buna göre davranır)
      clientGainNotice: true,
    };
  };

  const topicGainPlan = Array.isArray(resolvedContext.relatedMaterials?.gainPlan)
    ? resolvedContext.relatedMaterials.gainPlan
    : [];
  const currentGainIndex = topicGainPlan.length
    ? Math.min(Math.max(0, Number(resolvedContext.currentGainIndex) || 0), topicGainPlan.length - 1)
    : 0;
  // Tek kazanımlık planda da göster — öğrenci hangi kazanımda olduğunu her zaman görsün
  // (eksik kapsamı daraltılınca tek eksikli girişte gösterge kaybolmuştu).
  const hasTopicGainPlan = topicGainPlan.length > 0;
  // "Sıradaki kazanım" düğmesi (7 Eki 2026): geçiş sohbetteki sözlere bağlı kalmasın — öğrenci
  // "3. eksik kazanıma geçelim" yazıp takılmasın, kesin bir yolu olsun.
  const currentPlanItem = topicGainPlan[currentGainIndex] || null;
  const currentPlanGainId = String(currentPlanItem?.gainId || "");
  const canAdvanceByButton = topicGainPlan.length > 1 && currentGainIndex < topicGainPlan.length - 1;
  // Son kazanımda "Sıradaki kazanım" yerine "Konuyu bitir"
  const canFinishByButton = topicGainPlan.length > 0 && currentGainIndex >= topicGainPlan.length - 1 && !topicFinished;

  const enhanceToolCallsForAssistantMessage = (
    assistantText: string,
    toolCalls: AITutorToolCall[] = [],
    toolContext: Record<string, any> = resolvedContext,
  ) => {
    const workspaceToolNames = new Set(["show_pdf_on_canvas", "open_lesson_video", "open_simulation", "open_practice_test"]);
    const hasPdfTool = toolCalls.some((toolCall) => toolCall.name === "show_pdf_on_canvas");
    const mentionedPageNumber = parseMentionedPdfPageNumber(assistantText);
    const normalizedAssistantText = String(assistantText || "").toLocaleLowerCase("tr-TR");
    const mentionsPdfPage =
      mentionedPageNumber > 0 ||
      /\b(pdf|materyal|sayfa)\b.{0,100}\b(sayfa|aç|gör|incele)/i.test(normalizedAssistantText);
    if (hasPdfTool && mentionedPageNumber > 0) {
      return toolCalls.map((toolCall) =>
        toolCall.name === "show_pdf_on_canvas"
          ? {
            ...toolCall,
            arguments: {
              ...toolCall.arguments,
              page_number: mentionedPageNumber,
            },
          }
          : toolCall,
      );
    }
    if (!mentionsPdfPage || hasPdfTool) return toolCalls;

    const gainPlan = Array.isArray(toolContext.relatedMaterials?.gainPlan) ? toolContext.relatedMaterials.gainPlan : [];
    const plannedGainIndex = gainPlan.length
      ? Math.min(Math.max(0, Number(toolContext.currentGainIndex) || 0), gainPlan.length - 1)
      : 0;
    const plannedPdf = gainPlan[plannedGainIndex]?.pdf || (!gainPlan.length ? toolContext.relatedMaterials?.pdfs?.[0] : null);
    const pdfUrl = plannedPdf?.url || (!gainPlan.length ? toolContext.pdfUrl : "");
    if (!pdfUrl) return toolCalls;

    const pdfToolCall: AITutorToolCall = {
      id: "ui-pdf-open-from-message",
      name: "show_pdf_on_canvas",
      arguments: {
        pdf_url: pdfUrl,
        title: plannedPdf?.title || toolContext.contentTitle || "PDF Materyali",
        total_pages: Number(plannedPdf?.totalPages || 0),
        page_number: Number(mentionedPageNumber || plannedPdf?.pageNumber || 1),
      },
    };

    return [pdfToolCall, ...toolCalls.filter((toolCall) => !workspaceToolNames.has(toolCall.name))];
  };

  useEffect(() => {
    voiceEnabledRef.current = voiceEnabled;
    if (!voiceEnabled) {
      setVoiceConversationActive(false);
      voiceConversationActiveRef.current = false;
      stopRecordingRef.current?.();
      closeLiveConnection();
    }
  }, [voiceEnabled]);

  // Kullanıcının seçtiği AI öğretmen ses modelini fetch et (live veya tts)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const userPreference = (() => {
        try {
          const stored = localStorage.getItem("aiTutorAudioMode");
          return stored === "live" || stored === "tts" ? stored : null;
        } catch {
          return null;
        }
      })();

      try {
        const settings = await ApiService.getAITeacherSettings();
        const mode = String(settings?.selectedAudioModel?.mode || "tts");
        if (cancelled) return;
        const adminMode = mode === "live" ? "live" : mode === "browser" ? "browser" : mode === "none" ? "none" : "tts";
        // Admin 'Sessiz' veya 'Tarayıcı Sesi' seçtiyse öğrenci tercihi (localStorage) EZİLİR;
        // sesli modlarda (tts/live) öğrencinin kendi live/tts tercihi geçerli kalır
        const resolved = adminMode === "none" || adminMode === "browser" ? adminMode : (userPreference || adminMode);
        setAudioMode(resolved);
        audioModeRef.current = resolved;
        if (resolved === "none") {
          // Sessiz mod: otomatik seslendirme tamamen kapalı
          setVoiceEnabled(false);
        }
      } catch {
        // Fetch başarısız olursa öğrenci tercihi ya da varsayılan 'tts' kalır
        if (userPreference && !cancelled) {
          setAudioMode(userPreference);
          audioModeRef.current = userPreference;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const toggleAudioMode = () => {
    const nextMode: "live" | "tts" = audioMode === "live" ? "tts" : "live";
    setAudioMode(nextMode);
    audioModeRef.current = nextMode;
    try {
      localStorage.setItem("aiTutorAudioMode", nextMode);
    } catch {
      // localStorage erişilemezse sessizce geç
    }
    if (nextMode === "tts") {
      // Live → TTS: açık socket'i kapat, sıradaki mesaj TTS ile gelir
      closeLiveConnection();
    }
    // TTS → Live: sıradaki mesajda otomatik olarak yeni live socket açılır
  };

  useEffect(() => {
    voiceConversationActiveRef.current = voiceConversationActive;
  }, [voiceConversationActive]);

  useEffect(() => {
    if (voiceConversationActive || isRecording || isSpeaking || isPreparingSpeech || (voiceBubbleActive && (isLoading || isTypingMessage))) {
      setVoiceBubbleActive(true);
      return;
    }

    const collapseTimer = window.setTimeout(() => setVoiceBubbleActive(false), 900);
    return () => window.clearTimeout(collapseTimer);
  }, [voiceConversationActive, isRecording, isSpeaking, isPreparingSpeech, isLoading, isTypingMessage, voiceBubbleActive]);

  // Esc tam ekrandan çıkarır (paneli KAPATMAZ — öğrenci sohbeti kaybetmesin).
  useEffect(() => {
    if (!isOpen || !isExpanded) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setIsExpanded(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, isExpanded]);

  useEffect(() => {
    if (!isOpen) {
      // X'e basılmadan kapandıysa (sayfa değişimi) kazanım kapanışı burada denenir; X yolu zaten denediyse tekrar etmez
      if (startedRef.current) tryCloseActiveGainOnExit();
      setTopicFinished(false);
      setShowDrawingBoard(false);
      setIsExpanded(false);
      startedRef.current = false;
      typingCancelRef.current?.();
      activeTypingMessageRef.current = null;
      typingFlushRef.current = null;
      typingCancelRef.current = null;
      setMessages([]);
      setInput("");
      setResolvedContextNow(context);
      gainIndexRef.current = Number((context as any)?.currentGainIndex) || 0;
      openTestQuestionsRef.current = [];
      pendingHiddenMessagesRef.current = [];
      pendingGainStartTestRef.current = null;
      deferredLiveMoveRef.current = null;
      sessionEpochRef.current += 1;
      setResolvedGainIds([]);
      setLeftOpenGainIds([]);
      setPassedTestGainIds([]);
      setConfirmSkipGain(false);
      // Yeni oturum: geçiş/etkileşim sayaçları sıfırlanır (kilit oturum-içi zincirleme geçişe özel)
      lastGainAdvanceAtRef.current = 0;
      userTurnsOnGainRef.current = 0;
      openTestGainIdRef.current = null;
      // Test kilitleri de oturuma özeldir — kapanışta temizlenmezse önceki oturumda geçilen
      // kazanımın testi bir daha ASLA açılamıyordu (panel hep mount kaldığı için)
      completedTestGainIdRef.current = null;
      lastFailedTestGainIdRef.current = null;
      clearFailedReview();
      definitionOnlyNotifiedRef.current = new Set();
      // Test-yok kara listesi ve başarısız-deneme sayacı da OTURUMA özeldir: temizlenmezse
      // sonradan soru eklenen (testable olan) kazanım sonsuza dek "test yok" kalıyordu ve
      // yeni oturumdaki ilk başarısızlık "ikinci"ymiş gibi tam çözüm anlattırıyordu.
      // Plandan gelen testable=false bilgisi tohumlama effect'iyle zaten yeniden dolar.
      nonTestableGainIdsRef.current = [];
      failedAttemptsByGainRef.current = {};
      setActiveMaterial({ type: "empty" });
      setIsSpeaking(false);
      setIsPreparingSpeech(false);
      setSpeechNeedsGesture(false);
      setSpeechError("");
      setLastAssistantMessage("");
      setIsTypingMessage(false);
      setVoiceBubbleActive(false);
      setVoiceConversationActive(false);
      voiceConversationActiveRef.current = false;
      voiceCancelRequestedRef.current = false;
      setVoiceLevel(AI_TUTOR_VOICE_LEVEL_IDLE);
      stopCurrentAudio();
      closeLiveConnection();
      cleanupRecordingMonitor();
      recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
      recordingStreamRef.current = null;
      recorderRef.current = null;
      return;
    }

    if (!startedRef.current) {
      startedRef.current = true;
      conversationIdRef.current = newConversationId(); // yeni panel oturumu = yeni sohbet kaydı grubu
      setResolvedContextNow(context);
      gainIndexRef.current = Number((context as any)?.currentGainIndex) || 0;
      lastGainAdvanceAtRef.current = 0; // yeni oturum: kilit sayaçları temiz başlasın
      userTurnsOnGainRef.current = 0;
      pendingHiddenMessagesRef.current = [];
      pendingGainStartTestRef.current = null;
      deferredLiveMoveRef.current = null;
      sessionEpochRef.current += 1;
      openTestGainIdRef.current = null;
      completedTestGainIdRef.current = null;
      lastFailedTestGainIdRef.current = null;
      clearFailedReview();
      definitionOnlyNotifiedRef.current = new Set();
      nonTestableGainIdsRef.current = [];
      failedAttemptsByGainRef.current = {};
      // AudioContext'i hemen başlat — kullanıcı butona basınca gesture var,
      // Live mod'un ilk audio chunkı geldiğinde gesture timeout olmasın
      try {
        getStreamAudioContext(24000);
      } catch {}

      const sessionPlanId = context?.sessionPlanId;
      if (sessionPlanId) {
        (async () => {
          let resumeOverride: Record<string, any> | undefined;
          let hasRestoredHistory = false;
          try {
            const plan = await ApiService.getSessionPlan(String(sessionPlanId));
            const savedIndex = Number(plan?.currentGainIndex) || 0;
            if (savedIndex > 0) {
              setResolvedContextNow((prev) => ({ ...prev, currentGainIndex: savedIndex }));
              gainIndexRef.current = savedIndex;
              resumeOverride = { currentGainIndex: savedIndex };
            }

            const savedHistory = Array.isArray(plan?.history) ? plan.history : [];
            if (savedHistory.length > 0) {
              const restoredMessages: TutorMessage[] = savedHistory.map((entry) => ({
                id: createMessageId(),
                role: entry.role === "assistant" ? "assistant" : "user",
                content: String(entry.content || ""),
              }));
              setMessages(restoredMessages);
              const lastAssistant = [...restoredMessages].reverse().find((m) => m.role === "assistant");
              if (lastAssistant) setLastAssistantMessage(lastAssistant.content);
              hasRestoredHistory = true;
              // Kaldığı kazanım önceki oturumda zaten çalışıldı — "anlatıldı" sayılsın
              userTurnsOnGainRef.current = Math.max(userTurnsOnGainRef.current, 2);
            }
          } catch {
            // Plan yüklenemezse sıfırdan başla
          } finally {
            if (!hasRestoredHistory) {
              void sendMessage("", { ...(resumeOverride ? { contextOverride: resumeOverride } : {}), afterReply: scheduleGainStartTest });
            }
          }
        })();
      } else {
        void sendMessage("", { afterReply: scheduleGainStartTest });
      }
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, isLoading]);

  // Her asistan cevabı tamamlandıktan sonra konuşma geçmişini SessionPlan'a kaydet.
  // Streaming sırasında flood etmesin diye debounced.
  useEffect(() => {
    if (!isOpen) return;
    const sessionPlanId = context?.sessionPlanId || resolvedContext?.sessionPlanId;
    if (!sessionPlanId) return;
    if (messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (last?.role !== "assistant" || !last.content) return;
    if (isTypingMessage) return;

    const handle = window.setTimeout(() => {
      const history = messages
        .filter((m) => m.content && !m.transient)
        .map((m) => ({ role: m.role as "user" | "assistant", content: m.content }));
      void ApiService.updateSessionPlanProgress(String(sessionPlanId), { history }).catch(() => {
        // History kaydedilemezse sessizce geç — kullanıcı akışı bozulmasın
      });
    }, 1500);

    return () => window.clearTimeout(handle);
  }, [messages, isTypingMessage, isOpen, context?.sessionPlanId, resolvedContext?.sessionPlanId]);

  const playTtsAudio = async (
    text: string,
    options: {
      requireGestureFallback?: boolean;
      onReady?: (durationMs: number) => void;
    } = {},
  ) => {
    const { requireGestureFallback = true, onReady } = options;
    const cleanText = text.trim();
    if (!cleanText) return;

    // Sessiz mod: HİÇ ses yok — ne API ne cihaz sesi. Metin beklemeden akar.
    if (audioModeRef.current === "none") {
      setIsPreparingSpeech(false);
      onReady?.(estimateSpeechDurationMs(cleanText));
      return;
    }

    // Tarayıcı sesi modu: API'ye hiç gitme, cihazda seslendir (maliyet sıfır).
    if (audioModeRef.current === "browser") {
      try {
        setSpeechError("");
        setSpeechNeedsGesture(false);
        stopCurrentAudio();
        playBrowserSpeechFallback(cleanText, onReady);
      } catch {
        setSpeechError("Tarayıcı sesli okumayı desteklemiyor.");
        setIsPreparingSpeech(false);
        onReady?.(estimateSpeechDurationMs(cleanText));
      }
      return;
    }

    try {
      setIsPreparingSpeech(true);
      setSpeechError("");
      setSpeechNeedsGesture(false);
      setIsSpeaking(false);

      stopCurrentAudio();
      const requestId = speechRequestIdRef.current;
      try {
        getStreamAudioContext(24000);
      } catch {
        // Non-stream fallback will surface a readable error if browser audio is unavailable.
      }

      let streamReadyNotified = false;
      let streamStarted = false;
      let textFallbackStarted = false;
      let streamTextFallbackTimer: number | null = window.setTimeout(() => {
        if (streamReadyNotified || requestId !== speechRequestIdRef.current) return;

        textFallbackStarted = true;
        streamReadyNotified = true;
        setIsPreparingSpeech(false);
        onReady?.(estimateSpeechDurationMs(cleanText));
      }, AI_TUTOR_SPEECH_STREAM_TEXT_FALLBACK_MS);

      const clearStreamTextFallbackTimer = () => {
        if (streamTextFallbackTimer === null) return;
        window.clearTimeout(streamTextFallbackTimer);
        streamTextFallbackTimer = null;
      };
      const notifyStreamReady = () => {
        if (streamReadyNotified || requestId !== speechRequestIdRef.current) return;
        clearStreamTextFallbackTimer();
        streamReadyNotified = true;
        setIsPreparingSpeech(false);
        setIsSpeaking(true);
        onReady?.(estimateSpeechDurationMs(cleanText));
      };
      const finishStreamPlayback = () => {
        const audioContext = streamAudioContextRef.current;
        const remainingMs = audioContext
          ? Math.max(0, (streamPlaybackTimeRef.current - audioContext.currentTime) * 1000)
          : 0;

        window.setTimeout(() => {
          if (requestId !== speechRequestIdRef.current) return;
          clearStreamTextFallbackTimer();
          setIsSpeaking(false);
          setIsPreparingSpeech(false);
        }, remainingMs + 150);
      };

      const streamResult = await ApiService.streamAITeacherSpeech(cleanText, {
        onChunk: (chunk, metadata) => {
          if (!voiceEnabledRef.current || requestId !== speechRequestIdRef.current) return;
          if (textFallbackStarted && !streamStarted) return;

          const queuedDurationMs = queuePcmAudioChunk(chunk, metadata.sampleRate);
          if (queuedDurationMs > 0) {
            streamStarted = true;
            if (textFallbackStarted) {
              clearStreamTextFallbackTimer();
              setIsPreparingSpeech(false);
              setIsSpeaking(true);
            } else {
              notifyStreamReady();
            }
          }
        },
        onEnd: finishStreamPlayback,
      });

      if (streamResult.streamed) {
        clearStreamTextFallbackTimer();
        if (!streamStarted) {
          setIsPreparingSpeech(false);
          if (!streamReadyNotified) onReady?.(estimateSpeechDurationMs(cleanText));
        }

        const audioContext = streamAudioContextRef.current;
        if (audioContext?.state !== "running" && requireGestureFallback) {
          setIsSpeaking(false);
          setSpeechNeedsGesture(true);
        }
        return;
      }

      clearStreamTextFallbackTimer();
      if (textFallbackStarted && !streamStarted) {
        setIsPreparingSpeech(false);
        return;
      }
      const audioBlob = streamResult.blob || await ApiService.generateAITeacherSpeech(cleanText);
      if (!voiceEnabledRef.current) {
        setIsPreparingSpeech(false);
        onReady?.(estimateSpeechDurationMs(cleanText));
        return;
      }
      if (textFallbackStarted && !streamStarted) {
        setIsPreparingSpeech(false);
        return;
      }
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      audioUrlRef.current = audioUrl;

      let readyNotified = false;
      const notifyReady = () => {
        if (readyNotified) return;
        readyNotified = true;
        const durationMs = Number.isFinite(audio.duration) && audio.duration > 0
          ? audio.duration * 1000
          : estimateSpeechDurationMs(cleanText);
        onReady?.(durationMs);
      };

      audio.onplay = () => setIsSpeaking(true);
      audio.onended = () => {
        setIsSpeaking(false);
        setIsPreparingSpeech(false);
      };
      audio.onerror = () => {
        setIsSpeaking(false);
        setIsPreparingSpeech(false);
        setSpeechError("Ses dosyası çalınamadı.");
        setSpeechNeedsGesture(true);
      };

      await new Promise<void>((resolve) => {
        if (audio.readyState >= 1) {
          notifyReady();
          resolve();
          return;
        }

        const timeoutId = window.setTimeout(() => {
          notifyReady();
          resolve();
        }, 1200);

        audio.onloadedmetadata = () => {
          window.clearTimeout(timeoutId);
          notifyReady();
          resolve();
        };
      });

      await audio.play();
    } catch (error) {
      const serverError = error instanceof Error ? error.message : "Ses üretilemedi.";
      try {
        if (!voiceEnabledRef.current || (audioModeRef.current as string) === "none") {
          // Sessiz modda hata fallback'i de SESSİZ — PC sesine düşme
          setIsPreparingSpeech(false);
          onReady?.(estimateSpeechDurationMs(cleanText));
          return;
        }
        playBrowserSpeechFallback(cleanText, onReady);
        console.warn("AI Öğretmen Gemini TTS çalışmadı, tarayıcı sesi kullanılıyor:", serverError);
        setSpeechError("");
      } catch {
        setIsSpeaking(false);
        setIsPreparingSpeech(false);
        setSpeechError(serverError);
        if (requireGestureFallback) {
          setSpeechNeedsGesture(true);
        }
      }
    }
  };

  const loadPracticeTest = async (gainId: string, questionId?: string) => {
    if (!gainId) {
      setActiveMaterial({ type: "test-error", gainId: "", message: "Bu test için kazanım bilgisi bulunamadı." });
      return;
    }

    // Testi açılamadığı ZATEN bilinen kazanım (planda testable=false ya da bu oturumda
    // denendi): sunucuya boşuna gitme, yükleniyor ekranı da gösterme — doğrudan bilgi ver.
    if (nonTestableGainIdsRef.current.includes(gainId)) {
      setActiveMaterial({
        type: "test-error",
        gainId,
        message: "Bu kazanımda ölçmeye yetecek yeni soru yok. Testi atlayıp anlatımla pekiştireceğiz.",
      });
      if (!definitionOnlyNotifiedRef.current.has(gainId)) {
        definitionOnlyNotifiedRef.current.add(gainId);
        void sendHiddenMessage(
          `[SİSTEM] Bu kazanımda ölçmeye yetecek yeni soru YOK; pekiştirme testi AÇILAMAZ (open_practice_test çağırma). Kazanımı anlat, gerekirse kendi sözlü örneğinle pekiştir ve teste atıf yapma.`,
        );
      }
      return;
    }

    setActiveMaterial({ type: "test-loading", gainId });

    try {
      const recentMessages = messages
        .filter((m) => m.content?.trim())
        .slice(-6)
        .map((m) => ({ role: m.role, content: String(m.content).slice(0, 600) }));

      const session = await ApiService.getAITeacherPracticeTest({
        gainId,
        context: { ...getRequestContext(), recentMessages },
      });

      const questions = Array.isArray(session?.questions) ? session.questions : [];
      if (!questions.length) {
        openTestQuestionsRef.current = [];
        // Testle ÖLÇÜLEMEYEN iki durum aynı yoldan gider: tanım-düzeyi kazanım (hiç soru yok)
        // ve yetersiz havuz (3'ten az taze soru — ölçme için az, şansa kapanmasın diye açılmıyor).
        // Backend'in message alanı MODELE yazılmış talimattır, öğrenciye gösterilmez: öğrenci
        // nötr metin görür, model gizli mesajla öğrenir (yoksa "testi açtım, çöz" diye devam
        // edip aynı boş ekranı döngüye sokuyordu).
        if (session?.source === "definition-only" || session?.source === "insufficient-pool") {
          const yetersizHavuz = session.source === "insufficient-pool";
          if (!nonTestableGainIdsRef.current.includes(gainId)) nonTestableGainIdsRef.current.push(gainId);
          setActiveMaterial({
            type: "test-error",
            gainId,
            message: yetersizHavuz
              ? "Bu kazanımda ölçmeye yetecek yeni soru kalmadı. Testi atlayıp anlatımla pekiştireceğiz."
              : "Bu kazanım tanım düzeyinde — bağlı test sorusu yok. Testi atlayıp anlatımla pekiştireceğiz.",
          });
          if (!definitionOnlyNotifiedRef.current.has(gainId)) {
            definitionOnlyNotifiedRef.current.add(gainId);
            void sendHiddenMessage(
              yetersizHavuz
                ? `[SİSTEM] Bu kazanımda ölçmeye yetecek yeni soru YOK; pekiştirme testi AÇILAMAZ (open_practice_test çağırma). Kazanımı anlat, gerekirse kendi sözlü örneğinle pekiştir ve teste atıf yapma.`
                : `[SİSTEM] Bu kazanımda bağlı ölçme sorusu YOK; pekiştirme testi AÇILAMAZ (open_practice_test çağırma). Kavramı temel tanım düzeyinde anlat, istersem kısa sözlü örnek ver ve teste atıf yapma.`,
            );
          }
          return;
        }
        setActiveMaterial({ type: "test-error", gainId, message: session?.message || "Bu kazanım için gösterilecek test sorusu bulunamadı." });
        return;
      }

      // Modele verilecek grounding: numara + tam metin + (gerçek) şık + doğru cevap.
      // TYT sorularında şıklar metnin İÇİNDE (options sadece harf etiketi) → o durumda options'ı gönderme.
      openTestQuestionsRef.current = questions.map((q: any, i: number) => {
        const opts = q?.options && typeof q.options === "object" ? q.options : null;
        const hasRealOptions = !!opts && Object.entries(opts).some(
          ([label, text]) => String(text || "").trim() && String(text).trim().toUpperCase() !== String(label).toUpperCase(),
        );
        return {
          number: i + 1,
          text: String(q?.text || ""),
          options: hasRealOptions ? (opts as Record<string, string>) : undefined,
          correctAnswer: q?.correctAnswer ? String(q.correctAnswer) : undefined,
          solution: q?.solution ? String(q.solution) : undefined,
          // "yazili" = çözücüyle doğrulanmış yazılı çözüm (ses dökümü değil) → backend 'harf güvenilmez' uyarısını düşürür
          solutionSource: q?.solutionSource ? String(q.solutionSource) : undefined,
          visualDescription: q?.visualDescription ? String(q.visualDescription) : undefined,
        };
      });

      setActiveMaterial({
        type: "test",
        gainId,
        questionId,
        questions,
        source: session?.source,
        servedAt: Date.now(),
      });
      openTestGainIdRef.current = gainId; // açık test kaydı → orta-testte yeniden açma/yenileme engellenir
      // Sonuç için sabit kopya (PDF/video açılınca openTestQuestionsRef boşalır); yeni test = inceleme bitti
      lastServedTestRef.current = { gainId, questions: openTestQuestionsRef.current };
      clearFailedReview();

      // Live modda sistem talimatı SADECE oturum açılışında kurulur; test sonradan açıldığında
      // live grounding donuk kalıp soruları görmez (AI uydurur/tekrar eder). Bağlantıyı kapat →
      // sonraki tur openTestQuestions içeren TAZE grounding ile yeniden kurulur.
      if (audioModeRef.current === "live") closeLiveConnection();
    } catch (error: any) {
      openTestQuestionsRef.current = [];
      setActiveMaterial({
        type: "test-error",
        gainId,
        message: error?.message || "Pekiştirme testi yüklenemedi.",
      });
    }
  };

  // ─── KAZANIM GEÇİŞİ: TEK KAPI (7 Eki 2026) ───────────────────────────────────────
  // Eskiden geçiş üç yerde ayrı ayrı yapılıyordu (AI aracı, test geçilince otomatik ilerleme,
  // sesli mod); her biri sırayı, defteri ve kapanışı kendi bildiği gibi, sonucu beklemeden
  // yazıyordu. Saha (Burak Sarıipek, 7 Eki): yollar birbirinden habersiz kaldı, öğrenci 2.
  // kazanımda takıldı. Artık her geçiş advanceGain'den ve sunucunun /gain/advance ucundan geçer.
  const gainPlanSummary = (plan: any[]) =>
    plan.map((g: any) => ({
      gainId: g?.gainId,
      lessonSectionContentGainId: g?.lessonSectionContentGainId,
      gainText: String(g?.gainText || "").slice(0, 300),
      testable: g?.testable,
    }));

  // Sunucuya yalnız kimlik/sıra bilgisi giden hafif bağlam (PDF metni, görseller taşınmaz)
  const slimRequestContext = (overrides: Record<string, any> = {}) => {
    const full = getRequestContext();
    const plan = Array.isArray(full.relatedMaterials?.gainPlan) ? full.relatedMaterials.gainPlan : [];
    return {
      sessionPlanId: full.sessionPlanId,
      conversationId: full.conversationId,
      source: full.source,
      lessonTitle: full.lessonTitle,
      topicTitle: full.topicTitle,
      contentTitle: full.contentTitle,
      gainId: full.gainId,
      currentGainIndex: gainIndexRef.current,
      relatedMaterials: { gainPlan: gainPlanSummary(plan) },
      ...overrides,
    };
  };

  // Sessiz aksaklıkları sunucu günlüğüne yaz (scripts/ai-teacher-health.js sayar). Öğrenciyi etkilemez.
  const reportTutorEvent = (type: string, detail: Record<string, any> = {}) => {
    void ApiService.reportAITeacherEvent({ type, detail, context: slimRequestContext() }).catch(() => {});
  };

  // Sohbete sistem notu (modele ve oturum geçmişine gitmez)
  const pushTutorNotice = (text: string) => {
    setMessages((prev) => [...prev, { id: createMessageId(), role: "assistant", content: text, transient: true, notice: true }]);
  };

  const planGainText = (gainId: string) => {
    const plan = Array.isArray(resolvedContext.relatedMaterials?.gainPlan) ? resolvedContext.relatedMaterials.gainPlan : [];
    const item = plan.find((g: any) => g?.gainId === gainId || g?.lessonSectionContentGainId === gainId);
    return gainLabel(item?.gainText || "");
  };

  const planGainItemFor = (gainId: string) => {
    const plan = Array.isArray(resolvedContext.relatedMaterials?.gainPlan) ? resolvedContext.relatedMaterials.gainPlan : [];
    return plan.find((g: any) => g?.gainId === gainId || g?.lessonSectionContentGainId === gainId) || null;
  };

  // Kalınan testten sonra AI'nın önerebileceği materyaller (yalnız gerçekten olanlar)
  const buildResourceNote = (item: any) => {
    const video = item?.video;
    const parts: string[] = [];
    if (video?.url) {
      const range = formatVideoRange(video.startTime, video.endTime);
      parts.push(`bu kazanımın KONU ANLATIM VİDEOSU VAR${range ? ` (bu kazanımı anlatan bölüm: ${range})` : ""} — takıldığım kavram için izlemeyi öner, istersem open_lesson_video ile aç`);
    }
    if (item?.pdf?.url) parts.push("föyde bu kazanımın sayfası VAR — gerekirse show_pdf_on_canvas ile aç");
    return parts.length ? `Elindeki materyaller: ${parts.join("; ")}. ` : "";
  };

  // Sunucu bir eksiği GERÇEKTEN kapattığında: kartta ✓, sohbette not, açık listeler tazelenir
  // (ders ekranındaki Eksikler sekmesi, Eksiklerim, Konu Eksikleri, Koçum).
  const markGainResolved = (gainId: string, resolvedCount: number) => {
    if (!gainId || !resolvedCount) return;
    setResolvedGainIds((prev) => (prev.includes(gainId) ? prev : [...prev, gainId]));
    setLeftOpenGainIds((prev) => prev.filter((id) => id !== gainId));
    const text = planGainText(gainId);
    pushTutorNotice(`✓ Eksiğin giderildi${text ? `: "${text}"` : ""}`);
    window.dispatchEvent(new CustomEvent("aiTeacherGainResolved", { detail: { gainId, resolvedCount } }));
  };

  // Geçiş kilide takıldıysa modelin yeni kazanımı anlatan metni yerine giden dürüst mesaj
  const buildStayOnGainText = () => {
    const plan = Array.isArray(resolvedContext.relatedMaterials?.gainPlan) ? resolvedContext.relatedMaterials.gainPlan : [];
    const text = gainLabel(plan[gainIndexRef.current]?.gainText || "");
    return `Hâlâ "${text || "bu"}" kazanımındayız. Geçmek istersen aşağıdaki "Sıradaki kazanım" düğmesini kullanabilirsin.`;
  };

  // Sohbetten istenen geçişte yeni kazanımı model bu turda tanıtmadıysa (sunucu: gainIntroPending)
  // panel boşalınca gizli mesajla tanıttırılır; kazanım başı testi o tanıtımdan SONRA açılır.
  // Saha (10 Eki, Burak): tanıtım yoktu, "Hazırsan başlayalım" köprüsüne "başlayalım" deyince model
  // geçişi tekrarlıyordu; test de AI'nın sorusundan önce açılıp çakışıyordu. Panel boşa çıkmadan
  // ÖNCE kurulmalı — tanıtım etkisi yalnız meşguliyet bitince tetiklenir.
  const queueGainIntro = (response: { gainIntroPending?: boolean }, applied: { advancedTo: number | null }) => {
    if (!response?.gainIntroPending || applied.advancedTo === null) return;
    pendingGainStartTestRef.current = null;
    pendingGainKickoffRef.current = applied.advancedTo;
  };

  // Sunucu cevabını sohbete koyar. Geçiş köprüsü (gainTransitionNotice) bir SİSTEM NOTUDUR: AI balonu
  // olmaz, geçmişe ve modele gitmez. Saha (10 Eki, Burak): köprü AI'nın kendi sözü gibi geçmişe
  // giriyordu; kesik kazanım adını yarım cümlesi sanan AI "Özür dilerim, sözüm yarıda kalmış" dedi.
  // Panel geçişi kilide takıldıysa köprü gösterilmez (geçiş olmadı).
  const deliverAssistantReply = async (
    response: { gainTransitionNotice?: boolean },
    applied: { advancedTo: number | null },
    assistantMessage: string,
  ) => {
    if (response?.gainTransitionNotice) {
      pushTutorNotice(applied.advancedTo !== null ? assistantMessage : buildStayOnGainText());
      return;
    }
    await appendAssistantMessage(assistantMessage);
  };

  type GainAdvanceReason = "test-passed" | "button" | "model-tool" | "close";
  /**
   * Bir sonraki kazanıma geç. Sıra ANINDA (senkron) ilerler — aynı turdaki materyal araçları
   * ve sesli mod bunu bekliyor; kalıcı kayıt, kapanış, defter ve günlük sunucuda TEK uçta.
   * Dönüş: geçildiyse yeni sıra, geçilmediyse null.
   */
  const advanceGain = (reason: GainAdvanceReason, options: { gainPlan?: any[]; clearWorkspace?: boolean } = {}): number | null => {
    const plan = options.gainPlan
      || (Array.isArray(resolvedContext.relatedMaterials?.gainPlan) ? resolvedContext.relatedMaterials.gainPlan : []);
    const fromIndex = gainIndexRef.current;
    if (!plan.length || fromIndex >= plan.length - 1) return null;
    // Çift tık / aynı anda iki yol: 1,5 sn içinde ikinci geçiş yok (kazanım atlatırdı)
    if (Date.now() - lastGainAdvanceAtRef.current < 1500) return null;
    const toIndex = fromIndex + 1;
    const fromGainId = String(plan[fromIndex]?.gainId || "");
    const fromTestable = plan[fromIndex]?.testable !== false && !nonTestableGainIdsRef.current.includes(fromGainId);
    const epoch = sessionEpochRef.current;
    // "Anlatıldı" sayılması için kazanımda gerçek etkileşim (öğretilmeden-atlama kilidiyle aynı eşik).
    // Süren SESLİ turdaki konuşma henüz sayılmadı (tur bitince sayılır) — o da hesaba katılır.
    const turnsOnGain = userTurnsOnGainRef.current + (liveTurnActiveRef.current && liveAudioTurnRef.current ? 1 : 0);
    const taught = reason === "test-passed" || turnsOnGain >= 2;
    const requestContext = slimRequestContext({ currentGainIndex: fromIndex, relatedMaterials: { gainPlan: gainPlanSummary(plan) } });

    lastGainAdvanceAtRef.current = Date.now();
    gainIndexRef.current = toIndex;
    setResolvedContextNow((prev) => ({ ...prev, currentGainIndex: toIndex }));
    completedTestGainIdRef.current = null; // yeni kazanım → test tekrar açılabilir
    openTestQuestionsRef.current = []; // eski kazanımın açık test soruları context'ten düşsün
    userTurnsOnGainRef.current = 0; // yeni kazanım → öğretim etkileşimi sayacı sıfırla (atlama kilidi aktif)
    openTestGainIdRef.current = null;
    clearFailedReview();
    setConfirmSkipGain(false);
    // Test geçilince "Test Tamamlandı" ekranı kalır; diğer geçişlerde çalışma alanı boşalır
    if (options.clearWorkspace !== false) setActiveMaterial({ type: "empty" });
    // Sohbetten istenen geçişte yeni kazanımın testi tanıtımdan sonra açılır (önce ölç). Düğmede
    // bildirim cevabından sonra; test geçilince öğrenci "Sıradaki kazanımın testine başla" ile başlar.
    if (reason === "model-tool") scheduleGainStartTest();

    void ApiService.advanceAITeacherGain({ fromIndex, reason, taught, context: requestContext })
      .then((r) => {
        if (epoch !== sessionEpochRef.current) return; // oturum kapandı/sıfırlandı
        if (r?.resolvedCount) {
          markGainResolved(fromGainId, r.resolvedCount);
        } else if (r?.leftOpen && fromGainId) {
          setLeftOpenGainIds((prev) => (prev.includes(fromGainId) ? prev : [...prev, fromGainId]));
          const text = planGainText(fromGainId);
          pushTutorNotice(
            `Bu eksik açık kaldı${text ? `: "${text}"` : ""}. ` +
              (fromTestable ? "Testini çözünce kapanır" : "Bu kazanımı birlikte çalışınca kapanır") +
              "; Eksiklerim'den istediğin zaman dönebilirsin.",
          );
        }
      })
      .catch((error: any) => {
        if (epoch !== sessionEpochRef.current) return;
        reportTutorEvent("advance_failed", { reason, fromIndex, message: String(error?.message || "") });
        // Sunucu kapısı düştüyse en azından "kaldığın yerden devam" bozulmasın
        if (requestContext.sessionPlanId) {
          void ApiService.updateSessionPlanProgress(String(requestContext.sessionPlanId), { currentGainIndex: toIndex }).catch(() => {});
        }
      });

    // Sesli (Live) oturumun talimatı yalnız açılışta kurulur: geçişte bağlantı kapatılır, sonraki
    // tur yeni kazanımla kurulur. Süren canlı turun içindeysek tur bitince kapatılır.
    if (liveTurnActiveRef.current) {
      liveAdvanceDuringTurnRef.current = true;
    } else {
      closeLiveConnection();
    }
    return toIndex;
  };

  /**
   * Konuyu bitir — SON kazanımda. Sonraki kazanım olmadığı için advanceGain hiçbir şey yapmıyordu: son kazanım kapanmıyor,
   * sonraki oturumda yine geliyordu (sahibin 19 Eki tespiti). Sunucu geçişteki kurallarla kapanışı dener (testli kazanım
   * testsiz kapanmaz); kapanırsa oturum tamamlanır. force: "Yine de bitir" — eksik açık kalsa da oturum biter.
   */
  const finishTopic = async (reason: GainAdvanceReason, force = false) => {
    const plan = Array.isArray(resolvedContext.relatedMaterials?.gainPlan) ? resolvedContext.relatedMaterials.gainPlan : [];
    const fromIndex = gainIndexRef.current;
    if (!plan.length || fromIndex < plan.length - 1 || finishingTopicRef.current) return;
    finishingTopicRef.current = true;
    const fromGainId = String(plan[fromIndex]?.gainId || "");
    const fromTestable = plan[fromIndex]?.testable !== false && !nonTestableGainIdsRef.current.includes(fromGainId);
    const turnsOnGain = userTurnsOnGainRef.current + (liveTurnActiveRef.current && liveAudioTurnRef.current ? 1 : 0);
    const taught = reason === "test-passed" || turnsOnGain >= 2 || resolvedGainIds.includes(fromGainId) || passedTestGainIds.includes(fromGainId);
    const epoch = sessionEpochRef.current;
    setConfirmSkipGain(false);
    try {
      const r = await ApiService.advanceAITeacherGain({
        fromIndex, reason, taught, finish: true, force,
        context: slimRequestContext({ currentGainIndex: fromIndex, relatedMaterials: { gainPlan: gainPlanSummary(plan) } }),
      });
      if (epoch !== sessionEpochRef.current) return;
      if (r?.resolvedCount) markGainResolved(fromGainId, r.resolvedCount);
      if (r?.leftOpen && fromGainId) setLeftOpenGainIds((prev) => (prev.includes(fromGainId) ? prev : [...prev, fromGainId]));
      if (r?.sessionCompleted) {
        setTopicFinished(true);
        pushTutorNotice(r.leftOpen
          ? "Konuyu bitirdin. Son kazanımın testi çözülmediği için o eksik açık kaldı; Eksiklerim'den istediğin zaman dönebilirsin."
          : "✓ Konu tamamlandı — bu konudaki kazanımları bitirdin. Eksiklerim ve ilerlemen güncellendi.");
        window.dispatchEvent(new CustomEvent("aiTeacherGainResolved", { detail: { gainId: fromGainId, resolvedCount: r.resolvedCount || 0, topicFinished: true } }));
      } else if (r?.closeReason === "not-taught") {
        pushTutorNotice("Konuyu bitirmeden önce bu kazanımı birlikte biraz çalışalım: bir soru sor ya da örnekle devam edelim.");
      } else if (r?.leftOpen) {
        pushTutorNotice(fromTestable
          ? "Bu kazanımın testi henüz geçilmedi; testi geçince eksik kapanır ve konu tamamlanır."
          : "Bu eksik henüz kapanmadı; biraz daha birlikte çalışınca kapanır.");
      }
    } catch (error: any) {
      if (epoch === sessionEpochRef.current) reportTutorEvent("finish_failed", { reason, fromIndex, message: String(error?.message || "") });
    } finally {
      finishingTopicRef.current = false;
    }
  };

  // Sohbetten "bitirelim / sıradakine geçelim" (son kazanımda): testi çözülmemiş testli kazanımda önce test açılır —
  // geçince konu kendiliğinden biter; değilse konu bitirilir.
  const requestFinishTopic = (reason: GainAdvanceReason) => {
    const plan = Array.isArray(resolvedContext.relatedMaterials?.gainPlan) ? resolvedContext.relatedMaterials.gainPlan : [];
    const item = plan[gainIndexRef.current];
    const gid = String(item?.gainId || "");
    const testable = item?.testable !== false && !nonTestableGainIdsRef.current.includes(gid);
    if (gid && testable && !resolvedGainIds.includes(gid) && !passedTestGainIds.includes(gid)) {
      if (!reviewAllDone(gid)) {
        pushTutorNotice("Konuyu bitirmeden önce testteki yanlışlarının hepsini birlikte konuşalım; sonra test yeniden başlar.");
        return;
      }
      pushTutorNotice("Konuyu bitirmek için son kazanımın kısa testini çözelim; geçince konu tamamlanır. Test ekranda açılıyor.");
      applyToolCalls([{ id: "ui-open-test", name: "open_practice_test", arguments: { gain_id: gid } }], resolvedContext);
      return;
    }
    void finishTopic(reason);
  };

  // Panel nasıl kapanırsa kapansın (X, sayfa değişimi) üzerinde çalışılan kazanımı kapatmayı DENE — sayfa değişince panel
  // X'e basılmadan kapanıyor, kapanış hiç denenmiyordu (19 Eki). Son kazanımdaysa konuyu bitirme olarak denenir.
  // Öğrenci bu kazanımda en az iki kez yazmadıysa kapanış YOK (8 Eki kuralı); süren sesli tur da sayılır.
  const tryCloseActiveGainOnExit = () => {
    if (exitClosureEpochRef.current === sessionEpochRef.current || topicFinished) return;
    exitClosureEpochRef.current = sessionEpochRef.current;
    const gainPlan = Array.isArray(resolvedContext.relatedMaterials?.gainPlan) ? resolvedContext.relatedMaterials.gainPlan : [];
    const index = gainIndexRef.current;
    const activeGainId = String(gainPlan[index]?.gainId || resolvedContext.gainId || "");
    const turns = userTurnsOnGainRef.current + (liveTurnActiveRef.current && liveAudioTurnRef.current ? 1 : 0);
    if (!activeGainId || turns < 2) return;
    const notify = (r: any) => {
      if (r?.resolvedCount) {
        window.dispatchEvent(new CustomEvent("aiTeacherGainResolved", { detail: { gainId: activeGainId, resolvedCount: r.resolvedCount } }));
      }
    };
    if (gainPlan.length && index >= gainPlan.length - 1) {
      void ApiService.advanceAITeacherGain({
        fromIndex: index, reason: "close", taught: true, finish: true,
        context: slimRequestContext({ currentGainIndex: index, relatedMaterials: { gainPlan: gainPlanSummary(gainPlan) } }),
      }).then(notify).catch(() => {});
    } else {
      void ApiService.completeAITeacherGainTeaching({ gainId: activeGainId, context: getRequestContext(), taught: true }).then(notify).catch(() => {});
    }
  };

  const applyToolCalls = (
    toolCalls: AITutorToolCall[] = [],
    toolContext: Record<string, any> = resolvedContext,
  ): { blockedNextGain: boolean; blockedByModel: boolean; blockedReason: string; advancedTo: number | null; blockedReviewTest: boolean } => {
    // Geçiş kilide takıldıysa çağıran, modelin yeni kazanımı anlatan metnini göstermez.
    // blockedByModel: geçişi MODEL çağırmıştı (metni yeni kazanımı anlatıyordur); sunucunun öğrenci
    // cümlesinden eklediği geçişte (system-next-gain) modelin metni çoğu zaman bu kazanımı anlatır.
    let blockedNextGain = false;
    let blockedReviewTest = false;
    let blockedByModel = false;
    let blockedReason = "";
    let advancedTo: number | null = null;
    const workspaceToolNames = new Set(["show_pdf_on_canvas", "open_lesson_video", "open_simulation", "open_practice_test"]);
    let didApplyWorkspaceTool = false;
    const gainPlan = Array.isArray(toolContext.relatedMaterials?.gainPlan) ? toolContext.relatedMaterials.gainPlan : [];
    let plannedGainIndex = gainPlan.length
      ? Math.min(Math.max(0, Number(toolContext.currentGainIndex) || 0), gainPlan.length - 1)
      : 0;
    // move_to_next_gain ÖNCE işlensin ki index güncellensin; sonraki workspace tool'ları (test/PDF) YENİ kazanıma açılsın
    const orderedCalls = [
      ...toolCalls.filter((c) => c.name === "move_to_next_gain"),
      ...toolCalls.filter((c) => c.name !== "move_to_next_gain"),
    ];

    orderedCalls.forEach((toolCall) => {
      const plannedGain = gainPlan[plannedGainIndex] || {}; // her iterasyonda GÜNCEL index'ten
      const isWorkspaceTool = workspaceToolNames.has(toolCall.name);
      if (isWorkspaceTool && didApplyWorkspaceTool) return;

      if (toolCall.name === "show_pdf_on_canvas") {
        const plannedPdf = plannedGain.pdf || {};
        const matchingRelatedPdf = Array.isArray(toolContext.relatedMaterials?.pdfs)
          ? toolContext.relatedMaterials.pdfs.find((pdf: any) => pdf?.url && pdf.url === (plannedPdf.url || toolCall.arguments?.pdf_url))
          : null;
        const relatedPdf = gainPlan.length
          ? {
            ...matchingRelatedPdf,
            ...plannedPdf,
            pageImages: Array.isArray(plannedPdf.pageImages) && plannedPdf.pageImages.length
              ? plannedPdf.pageImages
              : matchingRelatedPdf?.pageImages,
          }
          : (matchingRelatedPdf || toolContext.relatedMaterials?.pdfs?.[0] || {});
        const payload = {
          pdf_url: String(gainPlan.length ? (relatedPdf.url || "") : (toolCall.arguments?.pdf_url || relatedPdf.url || toolContext.pdfUrl || "")),
          title: String(gainPlan.length ? (relatedPdf.title || "") : (toolCall.arguments?.title || relatedPdf.title || "")),
          total_pages: Number(gainPlan.length ? (relatedPdf.totalPages || 0) : (toolCall.arguments?.total_pages || relatedPdf.totalPages || 0)),
          page_number: Number(gainPlan.length ? (relatedPdf.pageNumber || 1) : (toolCall.arguments?.page_number || relatedPdf.pageNumber || 1)),
          page_images: Array.isArray(relatedPdf.pageImages) ? relatedPdf.pageImages : [],
        };

        setActiveMaterial({
          type: "pdf",
          pdfUrl: payload.pdf_url,
          pageNumber: payload.page_number,
          title: payload.title,
          totalPages: payload.total_pages,
          pageImages: payload.page_images,
        });
        // PDF testin yerini aldı → "test zaten açık" kaydı düşsün; yoksa öğrenci
        // sonra "test aç" dediğinde 1690'daki koruma isteği sessizce yutuyor (sahada görüldü).
        openTestGainIdRef.current = null;
        openTestQuestionsRef.current = [];
        window.dispatchEvent(new CustomEvent("aiTutorShowPdfOnCanvas", { detail: payload }));
        onShowPdfOnCanvas?.(payload);
        didApplyWorkspaceTool = true;
      }

      if (toolCall.name === "open_lesson_video") {
        // Video açılınca AI konuşmayı/sesi durdursun, mikrofon da kapansın
        stopCurrentAudio();
        setIsSpeaking(false);
        setIsPreparingSpeech(false);
        setIsTypingMessage(false);
        // Live bağlantı AÇIK kalsın — kullanıcı video izlediğinde de aynı oturumda devam etsin
        // Mikrofon ve konuşma modunu kapat — AI kendi sesini duyup cevap vermesin
        setVoiceConversationActive(false);
        voiceConversationActiveRef.current = false;
        setVoiceBubbleActive(false);
        cleanupRecordingMonitor();
        recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
        recordingStreamRef.current = null;
        recorderRef.current = null;
        setIsRecording(false);
        const primaryVideo = plannedGain.video || (!gainPlan.length ? toolContext.relatedMaterials?.primaryVideo : {}) || {};
        setActiveMaterial({
          type: "video",
          videoId: String(gainPlan.length ? (primaryVideo.id || "") : (toolCall.arguments?.video_id || primaryVideo.id || "")),
          videoUrl: String(gainPlan.length ? (primaryVideo.url || "") : (toolCall.arguments?.video_url || primaryVideo.url || "")),
          title: String(gainPlan.length ? (primaryVideo.title || "İlgili video") : (toolCall.arguments?.title || primaryVideo.title || "İlgili video")),
          startTime: String(gainPlan.length ? (primaryVideo.startTime || "00:00:00") : (toolCall.arguments?.start_time || primaryVideo.startTime || "00:00:00")),
          endTime: String(gainPlan.length ? (primaryVideo.endTime || "00:00:00") : (toolCall.arguments?.end_time || primaryVideo.endTime || "00:00:00")),
          transcriptExcerpt: String(gainPlan.length ? (primaryVideo.transcriptExcerpt || "") : (toolCall.arguments?.transcript_excerpt || primaryVideo.transcriptExcerpt || "")),
        });
        // Video testin yerini aldı → açık test kaydı düşsün (PDF dalındaki notla aynı sebep).
        openTestGainIdRef.current = null;
        openTestQuestionsRef.current = [];
        didApplyWorkspaceTool = true;
      }

      if (toolCall.name === "open_simulation") {
        const payload = {
          topic_id: String(toolCall.arguments?.topic_id || context.topicId || context.contentId || ""),
        };

        setActiveMaterial({ type: "simulation", topicId: payload.topic_id });
        // Simülasyon testin yerini aldı → açık test kaydı düşsün.
        openTestGainIdRef.current = null;
        openTestQuestionsRef.current = [];
        window.dispatchEvent(new CustomEvent("aiTutorOpenSimulation", { detail: payload }));
        onOpenSimulation?.(payload);
        didApplyWorkspaceTool = true;
      }

      if (toolCall.name === "open_practice_test") {
        const aiGainId = toolCall.arguments?.gain_id ? String(toolCall.arguments.gain_id) : null;
        // Fix D: AI'ın gain_id'si GEÇERLİ bir plan kazanımıysa ona güven (index bir tur geride
        // kalsa bile doğru YENİ kazanıma test açılsın; eski koşul geçerli hedefi de eskiye çekiyordu).
        // Yalnızca eksik ya da plan-DIŞI bir id ise aktif kazanıma düzelt.
        const planGainIds = new Set((gainPlan || []).map((g: any) => g?.gainId).filter(Boolean));
        let correctedGainId = aiGainId;
        if (plannedGain?.gainId && (!aiGainId || !planGainIds.has(aiGainId))) {
          correctedGainId = plannedGain.gainId;
        }
        const finalGainId = String(correctedGainId || plannedGain.gainId || toolContext.gainId || context.gainId || "");
        // Test açılınca AI konuşmayı/sesi durdursun, mikrofon da kapansın
        stopCurrentAudio();
        setIsSpeaking(false);
        setIsPreparingSpeech(false);
        setIsTypingMessage(false);
        // Test öncesi öğrenci sesli mi konuşuyordu? Test bitince sesi ancak o zaman geri açacağız.
        voiceActiveBeforeTestRef.current = voiceConversationActiveRef.current;
        // Live bağlantı AÇIK kalsın — test çözüldükten sonra aynı oturum devam etsin
        // Mikrofon ve konuşma modunu kapat — AI kendi sesini duyup cevap vermesin
        setVoiceConversationActive(false);
        voiceConversationActiveRef.current = false;
        setVoiceBubbleActive(false);
        cleanupRecordingMonitor();
        recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
        recordingStreamRef.current = null;
        recorderRef.current = null;
        setIsRecording(false);
        if (completedTestGainIdRef.current && finalGainId === completedTestGainIdRef.current) {
          // Bu kazanımın testi az önce tamamlandı; move_to_next_gain gelene kadar tekrar açma
          didApplyWorkspaceTool = true;
          return;
        }
        if (openTestGainIdRef.current === finalGainId && lastFailedTestGainIdRef.current !== finalGainId) {
          // Aynı kazanımın testi ZATEN açık ve devam ediyor → SORULARI YENİLEME (öğrencinin ilerlemesi kaybolmasın).
          // İstisna: başarısız test sonrası aynı kazanıma YENİ pekiştirme testi açılabilir.
          didApplyWorkspaceTool = true;
          return;
        }
        // Kazanım süzgeci YOK: sıradaki kazanımın kimliğiyle gelen test kilidi aşıp incelemeyi siliyordu. Gerçek geçiş incelemeyi zaten temizler.
        if (!reviewAllDone()) {
          // Kalınan testin yanlışlarının hepsi "anladım"la geçilmedi → test yeniden başlamaz (sahibin 20 Eki kararı)
          pushTutorNotice("Testi yeniden başlatmadan önce yanlışlarının hepsini birlikte konuşalım.");
          blockedReviewTest = true;
          didApplyWorkspaceTool = true;
          return;
        }
        void loadPracticeTest(
          finalGainId,
          toolCall.arguments?.question_id ? String(toolCall.arguments.question_id) : undefined,
        );
        didApplyWorkspaceTool = true;
      }

      if (toolCall.name === "move_to_next_gain") {
        // Geçişin KENDİSİ advanceGain'de (tek kapı); burada yalnız kilitler karar verir.
        // KİLİT 1: bu kazanımın pekiştirme testi BAŞARISIZ olduysa öğrenci açıkça istemeden geçilmez.
        // Sunucunun doğruladığı öğrenci isteği (studentRequested: "3. eksik kazanıma geçelim",
        // AI'nın teklifine onay) kilitleri aşar — açık istek sessizce yutulmasın (saha 7 Eki).
        const currentGainId = String(gainPlan[plannedGainIndex]?.gainId || "");
        const serverVerifiedRequest = Boolean(toolCall.arguments?.studentRequested);
        const studentAskedNext = serverVerifiedRequest || wantsNextGainMessage(lastUserMessageRef.current, plannedGainIndex);
        // KİLİT 2 — ÖĞRETİLMEDEN-ATLAMA: bu OTURUMDA az önce bir kazanıma geçildiyse ve öğrenci yeni
        // kazanımda <2 mesaj yazdıysa "geçelim / hazırım" BU kazanımı işlemeye başla demektir.
        // Oturum başındaki ilk istek ve açık atlama ("atla", "zaten biliyorum") serbest.
        const hadInSessionAdvance = lastGainAdvanceAtRef.current > 0;
        // "atlamayalım" atlama değildir
        const explicitSkip = /(atla(?!ma)\S*|zaten biliyorum|bunu biliyorum|biliyorum bunu)/i.test(lastUserMessageRef.current);
        let blockedBy = "";
        // KİLİT 0 — MODEL KENDİ BAŞINA GEÇEMEZ (sahibin kararı, 4 Ağu): yazılı modda sunucu bunu uygular
        // ve geçen her aracı studentRequested ile işaretler. Sesli (Live) modda araç doğrudan Gemini'den
        // gelir; aynı kural burada uygulanmazsa model "teste geçelim" sözünü geçiş sanıp ilerliyordu.
        const approvedOffer = approvesNextGainOffer(lastUserMessageRef.current, lastAssistantMessage);
        if (!studentAskedNext && !explicitSkip && !approvedOffer) {
          blockedBy = "not-requested";
        } else if (lastFailedTestGainIdRef.current && lastFailedTestGainIdRef.current === currentGainId && !studentAskedNext) {
          blockedBy = "failed-test";
        } else if (hadInSessionAdvance && userTurnsOnGainRef.current < 2 && !explicitSkip) {
          // Sahibin kuralı (4 Ağu): yeni gelinen kazanımda "geçelim / sıradaki kazanıma geçelim" bile
          // BU kazanıma başla demektir; yalnız "atla / zaten biliyorum" aşar. Bilerek geçmek isteyenin
          // yolu "Sıradaki kazanım" düğmesi (bağımsız inceleme: istek bayrağı kilidi fiilen kapatıyordu).
          blockedBy = "teach-lock";
        } else if (Date.now() - lastGainAdvanceAtRef.current <= 10000) {
          // KİLİT 3: test-geçişi ile aynı anda gelirse ikinci kez ilerleyip kazanım ATLATMA
          blockedBy = "cooldown";
        } else if (plannedGainIndex >= gainPlan.length - 1) {
          blockedBy = "last";
        }
        if (blockedBy) {
          blockedReason = blockedBy;
          // Son kazanımda "geçiş" metni zaten konu-sonu mesajıdır; diğerlerinde metin düzeltilir
          if (blockedBy !== "last") {
            blockedNextGain = true;
            if (toolCall.id !== "system-next-gain") blockedByModel = true;
          } else {
            // Son kazanımda geçiş isteği = konuyu bitir (eskiden sessizce düşüyordu, AI "tamamladık" deyip hiçbir şey
            // kapanmıyordu). Bu araç turu bitince: testi çözülmemişse test açılır, değilse konu kapatılır.
            window.setTimeout(() => requestFinishTopic("model-tool"), 0);
          }
          reportTutorEvent("move_blocked", { by: blockedBy, studentRequested: studentAskedNext });
          return;
        }
        const nextIndex = advanceGain("model-tool", { gainPlan });
        if (nextIndex !== null) {
          plannedGainIndex = nextIndex;
          advancedTo = nextIndex;
        }
        return;
      }
    });
    return { blockedNextGain, blockedByModel, blockedReason, advancedTo, blockedReviewTest };
  };

  const appendAssistantMessage = async (assistantMessage: string) => {
    let didStartTyping = false;
    const assistantMessageId = createMessageId();
    const startSyncedTyping = (durationMs?: number) => {
      if (didStartTyping) return;
      didStartTyping = true;
      setIsTypingMessage(true);
      let typingController: ReturnType<typeof streamAssistantText>;
      typingController = streamAssistantText(
        assistantMessage,
        (partialText) => {
          setMessages((prev) =>
            prev.map((message) =>
              message.id === assistantMessageId ? { ...message, content: partialText } : message,
            ),
          );
        },
        () => {
          if (typingFlushRef.current === typingController.flush) {
            typingFlushRef.current = null;
            typingCancelRef.current = null;
            activeTypingMessageRef.current = null;
          }
          setIsTypingMessage(false);
        },
        durationMs,
      );
      typingFlushRef.current = typingController.flush;
      typingCancelRef.current = typingController.cancel;
    };

    setLastAssistantMessage(assistantMessage);
    activeTypingMessageRef.current = { id: assistantMessageId, fullText: assistantMessage };
    setMessages((prev) => [...prev, { id: assistantMessageId, role: "assistant", content: "" }]);

    if (voiceEnabled) {
      // Ses hazırsa o süreyle sync et
      const leadInTimer = window.setTimeout(() => {
        if (didStartTyping) return;
        const ready = pendingSpeechReadyRef.current;
        if (!ready) return;
        pendingSpeechReadyRef.current = null;
        startSyncedTyping(ready.durationMs);
      }, 500);

      // Ses 1800ms'de hâlâ gelmemişse metni ses olmadan başlat
      // isPreparingSpeech'i de false yap — yoksa kullanıcı sonraki mesajı gönderemiyor
      // Süreyi konuşma hızından ver: geç kalan ses birazdan başlarsa metinle senkron kalır
      // (sesli yolun davranışına dokunulmadı; hızlı profil yalnız metin modunda).
      const forceStartTimer = window.setTimeout(() => {
        if (didStartTyping) return;
        window.clearTimeout(leadInTimer);
        pendingSpeechReadyRef.current = null;
        setIsPreparingSpeech(false);
        startSyncedTyping(estimateSpeechDurationMs(assistantMessage));
      }, 1800);

      void playTtsAudio(assistantMessage, {
        requireGestureFallback: true,
        onReady: (durationMs) => {
          if (didStartTyping) return;
          window.clearTimeout(forceStartTimer);
          pendingSpeechReadyRef.current = { durationMs };
          window.setTimeout(() => {
            if (didStartTyping) return;
            pendingSpeechReadyRef.current = null;
            window.clearTimeout(leadInTimer);
            startSyncedTyping(durationMs);
          }, 200);
        },
      });
    } else {
      // Metin modu: süre VERME — streamAssistantText süresizi hızlı metin profiliyle akıtır.
      // (Süre geçmek sesli-eşzamanlı tempo demek; eskiden burada konuşma hızı geçiliyordu
      // ve tamamı inmiş cevap 40+ saniyede yazılıyordu.)
      startSyncedTyping();
    }
  };

  const tryLiveAssistantMessage = async (
    text: string,
    requestContext: Record<string, any>,
    historyForRequest: TutorMessage[],
    audioTurn: LiveAudioTurn | null = null,
  ) => {
    if (!voiceEnabledRef.current) return false;
    // Kullanıcı TTS modelini seçtiyse Live mod hiç denenmesin — direkt classic + TTS
    if (audioModeRef.current !== "live") return false;
    const gainIndexAtLiveStart = gainIndexRef.current;

    let liveConnection = liveConnectionRef.current;
    const requestedContextKey = buildAITeacherLiveContextKey(requestContext);
    if (
      liveConnection &&
      (liveConnection.contextKey !== requestedContextKey || liveConnection.socket.readyState !== WebSocket.OPEN)
    ) {
      closeLiveConnection();
      liveConnection = null;
    }

    let clientContent: AITeacherLiveSession["clientContent"] = buildAITeacherLiveClientContent(text);
    let nextContext = requestContext || resolvedContext;

    if (liveConnection?.socket.readyState === WebSocket.OPEN) {
      // Backend'in enriched verisini (gainPlan, pdfs vb.) koruyalım AMA güncel frontend
      // state'i (currentGainIndex vb.) backend snapshot'ını ezsin.
      nextContext = {
        ...(liveConnection.session.context || {}),
        ...(requestContext || {}),
        relatedMaterials: {
          ...((liveConnection.session.context as any)?.relatedMaterials || {}),
          ...((requestContext as any)?.relatedMaterials || {}),
        },
      };
      clientContent = buildAITeacherLiveClientContent(text);
    } else {
      let openingSocket: WebSocket | null = null;
      try {
        const session = await ApiService.createAITeacherLiveSession({
          message: text,
          context: requestContext,
          history: historyForRequest,
        });
        const socket = new WebSocket(session.websocketUrl);
        openingSocket = socket;
        const connection: AITeacherLiveConnection = {
          session,
          socket,
          contextKey: buildAITeacherLiveContextKey(session.context || requestContext),
        };

        await new Promise<void>((resolve, reject) => {
          let settled = false;
          let timeoutId: number | null = null;

          const finish = (error?: Error) => {
            if (settled) return;
            settled = true;
            if (timeoutId !== null) window.clearTimeout(timeoutId);
            if (error) reject(error);
            else resolve();
          };

          timeoutId = window.setTimeout(
            () => finish(new Error("AI öğretmen Live oturumu zamanında hazırlanamadı.")),
            15000,
          );

          socket.onopen = () => {
            socket.send(JSON.stringify({ setup: session.setup || {} }));
          };

          socket.onmessage = async (event) => {
            try {
              const rawData = typeof event.data === "string"
                ? event.data
                : event.data instanceof Blob
                  ? await event.data.text()
                  : String(event.data || "{}");
              const response = JSON.parse(rawData || "{}");
              if (response.error) {
                finish(new Error(response.error.message || "Gemini Live API hata döndürdü."));
                return;
              }
              if (response.setupComplete) finish();
            } catch (error: any) {
              finish(error);
            }
          };

          socket.onerror = () => finish(new Error("Gemini Live WebSocket bağlantısı başarısız oldu."));
          socket.onclose = (ev) => finish(new Error(`Gemini Live WebSocket bağlantısı hazırlık sırasında kapandı (code=${ev.code} reason=${ev.reason || 'none'}).`));
        });

        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = () => {
          if (liveConnectionRef.current?.socket === socket) {
            liveConnectionRef.current = null;
          }
        };

        liveConnectionRef.current = connection;
        liveConnection = connection;
        // Backend session.context'inde gainPlan vb. enriched data var — kaybetmeyelim.
        // requestContext frontend state'idir (currentGainIndex vb.); her ikisini de tutalım.
        nextContext = {
          ...(session.context || {}),
          ...(requestContext || {}),
          relatedMaterials: {
            ...((session.context as any)?.relatedMaterials || {}),
            ...((requestContext as any)?.relatedMaterials || {}),
          },
        };
        clientContent = session.clientContent;
        openingSocket = null;
      } catch (error: any) {
        const socketToClose = openingSocket;
        if (socketToClose && (socketToClose.readyState === WebSocket.OPEN || socketToClose.readyState === WebSocket.CONNECTING)) {
          socketToClose.close();
        }
        closeLiveConnection();
        console.warn("AI öğretmen Live oturumu hazırlanamadı:", error?.message || error);
        return false;
      }
    }

    if (!liveConnection) return false;

    const assistantMessageId = createMessageId();
    const requestId = speechRequestIdRef.current + 1;
    const socket = liveConnection.socket;
    let finalText = "";
    let displayedLiveText = "";
    let liveTypingTarget = "";
    let receivedAnyLiveData = false;
    let userTranscript = ""; // sesli turda öğrencinin söylediklerinin dökümü (inputTranscription)
    let streamStarted = false;
    let sentClientContent = false;
    let liveFirstDataTimeoutId: number | null = null;
    let liveTypingTimerId: number | null = null;
    let liveTypingDoneResolve: (() => void) | null = null;
    const liveToolCalls: AITutorToolCall[] = [];
    // Tur bazında: araç çağrıldı mı, ses/yazı geldi mi (yalnız araçlı sessiz turdan sonra anlatım turunu beklemek için)
    let liveTurnHadToolCall = false;
    let liveTurnHadContent = false;

    const clearLiveFirstDataTimeout = () => {
      if (liveFirstDataTimeoutId === null) return;
      window.clearTimeout(liveFirstDataTimeoutId);
      liveFirstDataTimeoutId = null;
    };

    const markLiveDataReceived = () => {
      receivedAnyLiveData = true;
      clearLiveFirstDataTimeout();
    };

    const clearLiveTypingTimer = () => {
      if (liveTypingTimerId === null) return;
      window.clearTimeout(liveTypingTimerId);
      liveTypingTimerId = null;
    };

    const resolveLiveTypingIfComplete = () => {
      if (displayedLiveText.length < liveTypingTarget.length || !liveTypingDoneResolve) return;
      liveTypingDoneResolve();
      liveTypingDoneResolve = null;
    };

    const scheduleLiveTyping = () => {
      if (liveTypingTimerId !== null) return;

      if (!liveTypingTarget.startsWith(displayedLiveText)) {
        displayedLiveText = "";
      }

      if (displayedLiveText.length >= liveTypingTarget.length) {
        resolveLiveTypingIfComplete();
        return;
      }

      const nextIndex = displayedLiveText.length;
      const nextChar = liveTypingTarget[nextIndex] || "";
      displayedLiveText = liveTypingTarget.slice(0, nextIndex + 1);
      setMessages((prev) =>
        prev.map((messageItem) =>
          messageItem.id === assistantMessageId ? { ...messageItem, content: displayedLiveText } : messageItem,
        ),
      );

      const pauseMs = getTypingPauseAfterChar(liveTypingTarget, nextIndex, AI_TUTOR_TYPING_SETTINGS.pausesMs);
      const liveCharacterDelayMs = Math.min(
        AI_TUTOR_TYPING_SETTINGS.maxCharacterDelayMs,
        Math.max(AI_TUTOR_TYPING_SETTINGS.minCharacterDelayMs, AI_TUTOR_TYPING_SETTINGS.fallbackMsPerCharacter),
      );
      const nextDelayMs = /\s/.test(nextChar)
        ? AI_TUTOR_TYPING_SETTINGS.spaceDelayMs
        : liveCharacterDelayMs + pauseMs;

      liveTypingTimerId = window.setTimeout(() => {
        liveTypingTimerId = null;
        scheduleLiveTyping();
      }, nextDelayMs);
    };

    const waitForLiveTypingComplete = () => {
      if (displayedLiveText.length >= liveTypingTarget.length) return Promise.resolve();

      return new Promise<void>((resolve) => {
        liveTypingDoneResolve = resolve;
        scheduleLiveTyping();
      });
    };

    const updateLiveText = (fragment: string) => {
      const nextText = appendLiveTextFragment(finalText, fragment);
      if (nextText === finalText) return;

      finalText = nextText;
      if (isInternalMetaAssistantText(nextText)) {
        markLiveDataReceived();
        return;
      }

      liveTypingTarget = nextText;
      markLiveDataReceived();
      scheduleLiveTyping();
    };

    const liveFlushTyping = () => {
      clearLiveTypingTimer();
      const nextText = liveTypingTarget || finalText || displayedLiveText;
      displayedLiveText = nextText;
      setMessages((prev) =>
        prev.map((messageItem) =>
          messageItem.id === assistantMessageId ? { ...messageItem, content: nextText } : messageItem,
        ),
      );
      resolveLiveTypingIfComplete();
      setIsTypingMessage(false);
    };
    const liveCancelTyping = () => {
      clearLiveTypingTimer();
      resolveLiveTypingIfComplete();
    };
    typingFlushRef.current = liveFlushTyping;
    typingCancelRef.current = liveCancelTyping;

    const finishStreamPlayback = () => {
      const audioContext = streamAudioContextRef.current;
      const remainingMs = audioContext
        ? Math.max(0, (streamPlaybackTimeRef.current - audioContext.currentTime) * 1000)
        : 0;

      window.setTimeout(() => {
        if (requestId !== speechRequestIdRef.current) return;
        setIsSpeaking(false);
        setIsPreparingSpeech(false);
      }, remainingMs + 150);
    };

    const sendClientContent = () => {
      if (sentClientContent || !socket || socket.readyState !== WebSocket.OPEN) return;
      sentClientContent = true;
      if (audioTurn) {
        // Sesli tur: metin yerine öğrencinin sesi; sınırları biz veririz (otomatik VAD kapalı).
        socket.send(JSON.stringify({ realtimeInput: { activityStart: {} } }));
        for (let offset = 0; offset < audioTurn.pcm16.length; offset += LIVE_INPUT_CHUNK_SAMPLES) {
          const chunk = audioTurn.pcm16.subarray(offset, offset + LIVE_INPUT_CHUNK_SAMPLES);
          socket.send(JSON.stringify({
            realtimeInput: { audio: { mimeType: `audio/pcm;rate=${LIVE_INPUT_SAMPLE_RATE}`, data: int16ToBase64(chunk) } },
          }));
        }
        socket.send(JSON.stringify({ realtimeInput: { activityEnd: {} } }));
        return;
      }
      socket.send(JSON.stringify({ clientContent }));
    };

    const handleLiveToolCall = (response: any) => {
      const functionCalls = response?.toolCall?.functionCalls || response?.serverContent?.toolCall?.functionCalls || [];
      if (!Array.isArray(functionCalls) || !functionCalls.length || !socket || socket.readyState !== WebSocket.OPEN) return;

      const mappedCalls = functionCalls
        .filter((call: any) => call?.name)
        .map((call: any, index: number) => ({
          id: call.id || `live-tool-${liveToolCalls.length + index}`,
          name: call.name as TutorToolName,
          arguments: call.args || {},
        }));

      liveToolCalls.push(...mappedCalls);
      markLiveDataReceived();
      const applied = applyToolCalls(mappedCalls, nextContext);
      // Sistem turlarında (test sonucu, geçiş tanıtımı) geçiş zaten sistemce yapıldı — not gösterilmez
      const systemTurn = Boolean((nextContext as any)?.isPracticeResult || (nextContext as any)?.isGainTransition);
      // Sesli turda öğrencinin sözü henüz tam dökülmemiş olabilir: "istemedi" kararı tur sonuna ertelenir
      const deferMove = applied.blockedReason === "not-requested" && liveAudioTurnRef.current && !systemTurn;
      if (deferMove) {
        deferredLiveMoveRef.current = mappedCalls.find((call) => call.name === "move_to_next_gain") || null;
      } else if (applied.blockedNextGain && !systemTurn) {
        // Kilide takılan geçişte model "geçtik" diye konuşmuş olabilir — ekrana gerçek durum yazılır
        pushTutorNotice(buildStayOnGainText());
      }
      const moveResultText = applied.blockedReason === "last"
        ? "Bu planın son kazanımı; geçilecek kazanım yok. Sistem konuyu kapatmayı deniyor (testi varsa önce test açılır). 'Tamamladık' deme, yeni kazanım tanıtma."
        : deferMove
          ? "Geçiş henüz uygulanmadı; öğrencinin sözü tamamlanınca sistem karar verecek. Yeni kazanımı ANLATMA, bu kazanımda kal."
          : `Geçiş UYGULANMADI (${applied.blockedReason}). Yeni kazanımı ANLATMA; bu kazanımda devam et.`;

      liveTurnHadToolCall = true;

      // Yalnız { ok: true } dönünce 3.8 Live aracı tekrar çağırıp konuşmayı geciktiriyordu; ne olduğunu ve devam etmesini söyle.
      socket.send(JSON.stringify({
        toolResponse: {
          functionResponses: mappedCalls.map((call) => {
            // Engellenen geçişte "ok" dersek model yeni kazanımı anlatmaya devam ediyordu
            const moveRefused = call.name === "move_to_next_gain" && Boolean(applied.blockedReason);
            const testRefused = call.name === "open_practice_test" && applied.blockedReviewTest;
            return {
              id: call.id,
              name: call.name,
              response: {
                ok: !moveRefused && !testRefused,
                result: moveRefused
                  ? moveResultText
                  : testRefused
                    ? "Test AÇILMADI: yanlışların hepsi konuşulmadı. Testi açtığını söyleme; ekrandaki soruyu anladıysa \"Bu soruyu anladım\" düğmesine basmasını söyle."
                    : (AI_TUTOR_LIVE_TOOL_RESULT_TEXT[call.name] || "İstek öğrencinin ekranına iletildi."),
                instruction: "Bu aracı bu mesaj için tekrar çağırma; hemen sesli olarak öğrenciye anlatmaya devam et.",
              },
            };
          }),
        },
      }));
    };

    try {
      setResolvedContextNow(mergeServerContext(nextContext));
      setIsPreparingSpeech(true);
      setSpeechError("");
      setSpeechNeedsGesture(false);
      setIsSpeaking(false);
      setIsTypingMessage(true);
      setLastAssistantMessage("");
      stopCurrentAudio();
      speechRequestIdRef.current = requestId;
      getStreamAudioContext(24000);
      setMessages((prev) => [...prev, { id: assistantMessageId, role: "assistant", content: "" }]);

      liveTurnActiveRef.current = true;
      liveAudioTurnRef.current = Boolean(audioTurn);
      liveAdvanceDuringTurnRef.current = false;
      await new Promise<void>((resolve, reject) => {
        let settled = false;
        let timeoutId: number | null = null;
        let turnCompleteGraceTimeoutId: number | null = null;
        const finish = (error?: Error) => {
          if (settled) return;
          settled = true;
          clearLiveFirstDataTimeout();
          if (timeoutId !== null) window.clearTimeout(timeoutId);
          if (turnCompleteGraceTimeoutId !== null) window.clearTimeout(turnCompleteGraceTimeoutId);
          if (error) reject(error);
          else resolve();
        };
        const finishAfterTurnComplete = (waitMs = AI_TUTOR_LIVE_TURN_COMPLETE_GRACE_MS) => {
          if (turnCompleteGraceTimeoutId !== null) window.clearTimeout(turnCompleteGraceTimeoutId);
          turnCompleteGraceTimeoutId = window.setTimeout(() => finish(), waitMs);
        };
        // Tur bitmiş sayılıp kapanmayı beklerken yeni ses/yazı gelirse (araçtan sonraki anlatım turu) kapanma iptal edilir;
        // o turun turnComplete'i kapanmayı yeniden kurar.
        const markLiveTurnContent = () => {
          liveTurnHadContent = true;
          if (turnCompleteGraceTimeoutId !== null) {
            window.clearTimeout(turnCompleteGraceTimeoutId);
            turnCompleteGraceTimeoutId = null;
          }
        };
        timeoutId = window.setTimeout(() => finish(new Error("AI öğretmen Live yanıtı zaman aşımına uğradı.")), 45000);
        liveFirstDataTimeoutId = window.setTimeout(
          () => finish(new Error("AI öğretmen Live ilk ses/metin parçasını zamanında göndermedi.")),
          AI_TUTOR_LIVE_FIRST_DATA_TIMEOUT_MS,
        );
        socket.onmessage = async (event) => {
          try {
            const rawData = typeof event.data === "string"
              ? event.data
              : event.data instanceof Blob
                ? await event.data.text()
                : String(event.data || "{}");
            const response = JSON.parse(rawData || "{}");
            if (response.error) {
              finish(new Error(response.error.message || "Gemini Live API hata döndürdü."));
              return;
            }

            handleLiveToolCall(response);
            // Kazanım geçildi: eski kazanımın grounding'iyle konuşmayı sürdürmek yerine turu hemen bitir;
            // yeni kazanım taze oturumda tanıtılır (pendingGainKickoffRef).
            if (liveAdvanceDuringTurnRef.current) {
              finish();
              return;
            }

            const modelParts = response.serverContent?.modelTurn?.parts;
            if (Array.isArray(modelParts)) {
              modelParts.forEach((part: any) => {
                if (typeof part?.text === "string" && !isLiveToolArgumentEcho(part.text)) {
                  markLiveTurnContent();
                  updateLiveText(part.text);
                }

                const inlineData = part?.inlineData;
                if (inlineData?.data) {
                  const chunk = base64ToUint8Array(inlineData.data);
                  const sampleRate = getSampleRateFromMimeType(inlineData.mimeType || "audio/L16;rate=24000");
                  const queuedDurationMs = voiceEnabledRef.current ? queuePcmAudioChunk(chunk, sampleRate) : 0;
                  markLiveDataReceived();
                  markLiveTurnContent();
                  if (queuedDurationMs > 0) {
                    streamStarted = true;
                    setIsPreparingSpeech(false);
                    setIsSpeaking(true);
                  }
                }
              });
            }

            // Öğrencinin sesinin dökümü (yalnız sesli turda gelir): öğrenci balonuna yazılır, kural köprüleri için saklanır.
            const inputTranscriptText = response.serverContent?.inputTranscription?.text || "";
            if (inputTranscriptText && audioTurn) {
              userTranscript = `${userTranscript}${inputTranscriptText}`;
              const shownTranscript = userTranscript.trim();
              lastUserMessageRef.current = shownTranscript;
              markLiveDataReceived();
              setMessages((prev) =>
                prev.map((messageItem) =>
                  messageItem.id === audioTurn.userMessageId ? { ...messageItem, content: shownTranscript, transient: false } : messageItem,
                ),
              );
            }

            const transcriptText =
              response.serverContent?.outputTranscription?.text ||
              response.outputTranscription?.text ||
              "";
            if (transcriptText && !isLiveToolArgumentEcho(transcriptText)) {
              markLiveTurnContent();
              updateLiveText(transcriptText);
            }

            if (response.serverContent?.turnComplete) {
              // Yalnız araç çağrılıp hiç ses/yazı gelmeyen tur: model anlatımı araç yanıtından sonraki turda söyleyecek.
              const toolOnlyTurn = liveTurnHadToolCall && !liveTurnHadContent;
              liveTurnHadToolCall = false;
              liveTurnHadContent = false;
              finishAfterTurnComplete(toolOnlyTurn ? AI_TUTOR_LIVE_TOOL_CONTINUATION_WAIT_MS : AI_TUTOR_LIVE_TURN_COMPLETE_GRACE_MS);
            }
          } catch (error) {
            finish(error as Error);
          }
        };

        socket.onerror = () => finish(new Error("Gemini Live WebSocket bağlantısı başarısız oldu."));
        socket.onclose = (ev) => {
          if (liveConnectionRef.current?.socket === socket) {
            liveConnectionRef.current = null;
          }
          if (receivedAnyLiveData) {
            finish();
            return;
          }
          finish(new Error(`Gemini Live WebSocket bağlantısı yanıt tamamlanmadan kapandı (code=${ev.code} reason=${ev.reason || 'none'}).`));
        };

        sendClientContent();
      });

      clearLiveFirstDataTimeout();
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = () => {
        if (liveConnectionRef.current?.socket === socket) {
          liveConnectionRef.current = null;
        }
      };
      liveTurnActiveRef.current = false;
      if (liveAdvanceDuringTurnRef.current) {
        liveAdvanceDuringTurnRef.current = false;
        closeLiveConnection(); // yeni kazanım grounding'i için (Fix A) — tur bittikten sonra
      }
      finishStreamPlayback();
      liveTypingTarget = finalText;

      // Sesli turda öğrencinin metni dökümden gelir; kural köprüleri (video isteği vb.) bu metne bakar.
      const effectiveUserText = audioTurn ? userTranscript.trim() : text;
      if (audioTurn) {
        if (!effectiveUserText && !finalText.trim()) {
          // Sessizlik / anlaşılamayan ses: hiçbir şey olmamış gibi temizle.
          setMessages((prev) => prev.filter((messageItem) => messageItem.id !== audioTurn.userMessageId && messageItem.id !== assistantMessageId));
          setIsTypingMessage(false);
          setIsPreparingSpeech(false);
          setIsSpeaking(false);
          stopCurrentAudio();
          return true;
        }
        if (!effectiveUserText) {
          setMessages((prev) => prev.filter((messageItem) => messageItem.id !== audioTurn.userMessageId));
        } else {
          countUserTurn(); // aktif kazanımdaki öğretim etkileşimi sayacı (sendMessage ile aynı)
        }
      }
      // Tur içinde "öğrenci istemedi" diye bekletilen geçiş: artık TAM döküm elde — kural yeniden işletilir
      const deferredMove = deferredLiveMoveRef.current;
      deferredLiveMoveRef.current = null;
      if (deferredMove) {
        if (effectiveUserText) lastUserMessageRef.current = effectiveUserText;
        const recheck = applyToolCalls([{ ...deferredMove, id: `${deferredMove.id || "live-move"}-tur-sonu` }], nextContext);
        if (recheck.blockedNextGain) pushTutorNotice(buildStayOnGainText());
      }

      // Tur içinde kazanım geçildiyse yeni kazanım ekran boşalınca taze oturumda gizli mesajla tanıtılır.
      const advancedDuringTurn = liveToolCalls.some((call) => call.name === "move_to_next_gain")
        && gainIndexRef.current > gainIndexAtLiveStart;
      if (advancedDuringTurn) {
        pendingGainKickoffRef.current = gainIndexRef.current;
        pendingGainStartTestRef.current = null; // test, tanıtım bitince açılır (kickoff afterReply)
      }
      // Model araçtan sonra hiç konuşmadıysa yedek cümle araca göre seçilir ("Tamam, buradan devam edelim." test açılınca yanıltıcıydı).
      const silentToolName = liveToolCalls.find((call) => call.name !== "move_to_next_gain")?.name;
      const liveFallbackText = advancedDuringTurn
        ? "Tamam, sıradaki kazanıma geçiyoruz."
        : silentToolName === "open_practice_test" && !reviewAllDone()
          ? "Testi yeniden başlatmadan önce yanlışlarının hepsini konuşalım: anladıysan \"Bu soruyu anladım\" düğmesine bas."
          : silentToolName === "open_practice_test"
            ? "Pekiştirme testini açtım; bitirince yanlışlarına birlikte bakalım."
            : silentToolName === "open_lesson_video"
              ? "Videoyu açtım; izleyip dönünce kaldığımız yerden devam edelim."
              : silentToolName === "show_pdf_on_canvas"
                ? "PDF sayfasını açtım; birlikte inceleyelim."
                : "Tamam, buradan devam edelim.";
      // Geçişte tur kesildiği için yarım/meta metin kalabilir; bunu "başarısız" sayıp mesajı YENİDEN göndermek
      // ("zaten biliyorum, geç") bir kazanım daha atlatabiliyordu → geçiş turunda yedek cümle kullanılır.
      const liveTextUnusable = !finalText.trim()
        || isInternalMetaAssistantText(finalText)
        || isLikelyIncompleteAssistantText(finalText);
      const liveText = advancedDuringTurn && liveTextUnusable ? liveFallbackText : (finalText || liveFallbackText);
      // Geçiş yedek cümlesi AI'nın sözü değil, sistem notudur — geçmişe/modele girmez (yeni kazanımı tanıtım anlatır)
      const liveTextIsTransitionNotice = advancedDuringTurn && liveTextUnusable;

      const assistantPayload = extractTextualToolCallsFromMessage(liveText);
      const assistantMessage = assistantPayload.message || liveFallbackText;
      if (isInternalMetaAssistantText(assistantMessage)) {
        setMessages((prev) => prev.filter((messageItem) => messageItem.id !== assistantMessageId));
        setIsTypingMessage(false);
        setIsPreparingSpeech(false);
        setIsSpeaking(false);
        stopCurrentAudio();
        if (liveConnectionRef.current?.socket === socket) {
          closeLiveConnection();
        }
        return false;
      }

      if (isLikelyIncompleteAssistantText(assistantMessage)) {
        setMessages((prev) => prev.filter((messageItem) => messageItem.id !== assistantMessageId));
        setIsTypingMessage(false);
        setIsPreparingSpeech(false);
        setIsSpeaking(false);
        stopCurrentAudio();
        if (liveConnectionRef.current?.socket === socket) {
          closeLiveConnection();
        }
        return false;
      }

      // Öğrenci açıkça video istedi, model aracı çağırmadı, kazanımın videosu var → sistem açar (klasik yoldaki köprünün canlı eşi).
      const turnToolCalls = [...liveToolCalls, ...assistantPayload.toolCalls];
      const liveVideoBridgeCall = (() => {
        if (!wantsLiveVideoOpen(effectiveUserText)) return null;
        if (turnToolCalls.some((toolCall) => toolCall.name === "open_lesson_video")) return null;
        const bridgeGainPlan = Array.isArray(nextContext.relatedMaterials?.gainPlan) ? nextContext.relatedMaterials.gainPlan : [];
        const bridgeGainIndex = bridgeGainPlan.length
          ? Math.min(Math.max(0, Number(nextContext.currentGainIndex) || 0), bridgeGainPlan.length - 1)
          : 0;
        const bridgeVideo = bridgeGainPlan.length ? bridgeGainPlan[bridgeGainIndex]?.video : nextContext.relatedMaterials?.primaryVideo;
        if (!bridgeVideo?.url) return null;
        const bridgeCall: AITutorToolCall = {
          id: "ui-video-open-from-request",
          name: "open_lesson_video",
          arguments: {
            video_id: String(bridgeVideo.id || ""),
            video_url: String(bridgeVideo.url || ""),
            title: String(bridgeVideo.title || "İlgili video"),
            start_time: String(bridgeVideo.startTime || "00:00:00"),
            end_time: String(bridgeVideo.endTime || "00:00:00"),
            transcript_excerpt: String(bridgeVideo.transcriptExcerpt || ""),
          },
        };
        return bridgeCall;
      })();

      const mergedToolCalls = enhanceToolCallsForAssistantMessage(
        assistantMessage,
        liveVideoBridgeCall ? [liveVideoBridgeCall, ...turnToolCalls] : turnToolCalls,
        nextContext,
      );
      const pendingToolCalls = mergedToolCalls.filter(
        (toolCall) => !liveToolCalls.some((liveToolCall) => liveToolCall.id === toolCall.id && liveToolCall.name === toolCall.name),
      );
      const pendingApplied = applyToolCalls(pendingToolCalls, nextContext);
      if (pendingApplied.blockedNextGain && !(nextContext as any)?.isPracticeResult && !(nextContext as any)?.isGainTransition) {
        pushTutorNotice(buildStayOnGainText());
      }
      await waitForLiveTypingComplete();

      if (!liveTextIsTransitionNotice) setLastAssistantMessage(assistantMessage);
      setMessages((prev) =>
        prev.map((messageItem) =>
          messageItem.id === assistantMessageId
            ? { ...messageItem, content: assistantMessage, ...(liveTextIsTransitionNotice ? { transient: true, notice: true } : {}) }
            : messageItem,
        ),
      );
      setIsTypingMessage(false);
      if (typingFlushRef.current === liveFlushTyping) {
        typingFlushRef.current = null;
        typingCancelRef.current = null;
      }
      setIsLoading(false);

      if (!streamStarted) setIsPreparingSpeech(false);
      return true;
    } catch (error: any) {
      clearLiveFirstDataTimeout();
      clearLiveTypingTimer();
      if (typingFlushRef.current === liveFlushTyping) {
        typingFlushRef.current = null;
        typingCancelRef.current = null;
      }
      liveTurnActiveRef.current = false;
      if (liveAdvanceDuringTurnRef.current) {
        liveAdvanceDuringTurnRef.current = false;
        closeLiveConnection();
      }
      if (liveConnectionRef.current?.socket === socket) {
        closeLiveConnection();
      }
      setIsTypingMessage(false);
      setIsPreparingSpeech(false);
      setIsSpeaking(false);

      if (voiceCancelRequestedRef.current) {
        if (finalText || displayedLiveText) {
          const interruptedText = finalText || displayedLiveText;
          setMessages((prev) =>
            prev.map((messageItem) =>
              messageItem.id === assistantMessageId ? { ...messageItem, content: interruptedText } : messageItem,
            ),
          );
          setLastAssistantMessage(interruptedText);
        } else {
          setMessages((prev) => prev.filter((messageItem) => messageItem.id !== assistantMessageId));
        }
        voiceCancelRequestedRef.current = false;
        return true;
      }

      if (receivedAnyLiveData) {
        const fallbackMessage = finalText || error?.message || "AI öğretmen Live yanıtı tamamlanamadı.";
        if (isInternalMetaAssistantText(fallbackMessage)) {
          setMessages((prev) => prev.filter((messageItem) => messageItem.id !== assistantMessageId));
          stopCurrentAudio();
          return false;
        }

        setMessages((prev) =>
          prev.map((messageItem) =>
            messageItem.id === assistantMessageId ? { ...messageItem, content: fallbackMessage } : messageItem,
          ),
        );
        setLastAssistantMessage(fallbackMessage);
        return true;
      }

      setMessages((prev) => prev.filter((messageItem) => messageItem.id !== assistantMessageId));
      console.warn("AI öğretmen Live akışı başarısız, klasik akış kullanılacak:", error);
      return false;
    }
  };

  const isLiveModeActive = () => voiceEnabledRef.current && audioModeRef.current === "live";

  const tryLiveWithRetry = async (
    text: string,
    requestContext: Record<string, any>,
    historyForRequest: TutorMessage[],
    audioTurn: LiveAudioTurn | null = null,
  ) => {
    const first = await tryLiveAssistantMessage(text, requestContext, historyForRequest, audioTurn);
    if (first) return true;
    if (!isLiveModeActive()) return false;
    await new Promise((resolve) => window.setTimeout(resolve, 600));
    return tryLiveAssistantMessage(text, requestContext, historyForRequest, audioTurn);
  };

  // Sesli tur: kayıt bitince biriken PCM'i Live oturumuna verir. Başarısızsa false döner; çağıran OpenAI yoluna düşer.
  const sendAudioMessage = async (pcm16: Int16Array) => {
    if (isLoading || isTypingMessage || isPreparingSpeech) return false;
    if (!isLiveModeActive()) return false;

    const userMessageId = createMessageId();
    setMessages((prev) => [...prev, { id: userMessageId, role: "user", content: "🎤 …", transient: true }]);
    setIsLoading(true);

    const safetyTimer = window.setTimeout(() => {
      setIsLoading(false);
      setIsTypingMessage(false);
      setIsPreparingSpeech(false);
      setIsSpeaking(false);
    }, 45000);

    try {
      const trimmedHistory = messages.filter((m) => m.content && !m.transient).slice(-15);
      const handled = await tryLiveWithRetry("", getRequestContext(), trimmedHistory, { pcm16, userMessageId });
      if (!handled) setMessages((prev) => prev.filter((messageItem) => messageItem.id !== userMessageId));
      return handled;
    } catch {
      setMessages((prev) => prev.filter((messageItem) => messageItem.id !== userMessageId));
      return false;
    } finally {
      window.clearTimeout(safetyTimer);
      setIsLoading(false);
    }
  };

  const hiddenSendingRef = useRef(false);
  const sendHiddenMessage = async (
    text: string,
    contextOverride?: Record<string, any>,
    options: { queueIfBusy?: boolean; afterReply?: () => void; onError?: () => void } = {},
  ) => {
    const hiddenText = text.trim();
    if (!hiddenText) return;
    // Guard: eşzamanlı/burst gizli mesaj (ör. "Videoyu izledim" butonuna hızlı çoklu tıklama) → tek istek.
    // queueIfBusy: düşürülmemesi gereken mesaj (test sonucu) beklemeye alınır, panel boşalınca gider.
    // NOT: buradaki durum değerleri bu render'ın kopyasıdır — çağıran az önce yazma animasyonunu
    // bitirmiş olsa bile isTypingMessage burada hâlâ true görünür; beklemeye almak bu yüzden şart.
    if (isLoading || isTypingMessage || isPreparingSpeech || hiddenSendingRef.current) {
      if (options.queueIfBusy) {
        // Yeni bir geçiş bildirimi, kuyrukta bekleyen ESKİ geçiş/sonuç bildirimlerini geçersiz kılar —
        // yoksa AI önce artık geçerli olmayan kazanımı, sonra asıl kazanımı tanıtıyordu.
        const keep = contextOverride?.isGainTransition
          ? pendingHiddenMessagesRef.current.filter((item) => !item.context?.isGainTransition && !item.context?.isPracticeResult)
          : pendingHiddenMessagesRef.current;
        pendingHiddenMessagesRef.current = [...keep, { text: hiddenText, context: contextOverride, afterReply: options.afterReply, onError: options.onError }];
        setHiddenQueueTick((tick) => tick + 1);
      } else if (!hiddenSendingRef.current) {
        // Tıklama tekrarı (aynı anda ikinci gizli mesaj) bilinçli tekilleştirmedir; ötekiler kayda
        reportTutorEvent("hidden_dropped", { preview: hiddenText.slice(0, 80) });
      }
      return;
    }
    hiddenSendingRef.current = true;
    const requestContext = contextOverride || getRequestContext();

    setIsLoading(true);
    // sendMessage'daki güvenlik süresinin aynısı: istek asılı kalırsa mesaj kutusu ve "anladım" sonsuza dek kilitli kalıyordu
    const safetyTimer = window.setTimeout(() => {
      setIsLoading(false);
      setIsTypingMessage(false);
      setIsPreparingSpeech(false);
      setIsSpeaking(false);
      hiddenSendingRef.current = false;
    }, 45000);
    try {
      // Live mod aktifken Live'da kal; bir retry sonrası hâlâ başarısızsa /chat'e düşme — hata göster
      const liveHandled = await tryLiveWithRetry(hiddenText, requestContext, messages);
      if (liveHandled) {
        options.afterReply?.();
        return;
      }
      if (isLiveModeActive()) {
        setMessages((prev) => [...prev, { id: createMessageId(), role: "assistant", content: "Sesli oturum şu an açılamadı. Sıfırla ya da birkaç saniye sonra tekrar dene.", transient: true }]);
        options.onError?.();
        return;
      }

      const response = await ApiService.sendAITeacherMessage({
        message: hiddenText,
        context: requestContext,
        history: messages.filter((m) => m.content && !m.transient).slice(-15),
      });
      const nextContext = mergeServerContext(response.context || resolvedContextRef.current);
      const assistantPayload = extractTextualToolCallsFromMessage(response.message || "");
      const assistantMessage = getSafeAssistantMessage(
        assistantPayload.message || (requestContext?.isGainTransition ? "Yeni kazanımla devam ediyoruz." : "Test sonucunu birlikte değerlendirelim."),
        "Burada takıldığın noktayı birlikte toparlayalım. Önce ana fikri netleştirip sonra küçük bir örnek üzerinden ilerleyelim.",
      );

      const toolCalls = [...(response.toolCalls || []), ...assistantPayload.toolCalls];
      setResolvedContextNow(nextContext);
      const applied = applyToolCalls(enhanceToolCallsForAssistantMessage(assistantMessage, toolCalls, nextContext), nextContext);
      queueGainIntro(response, applied);

      setIsLoading(false);
      const hideTransitionText = applied.blockedNextGain && (applied.blockedByModel || announcesGainTransition(assistantMessage));
      await deliverAssistantReply(response, applied, hideTransitionText ? buildStayOnGainText() : assistantMessage);
      options.afterReply?.();
    } catch (error: any) {
      reportTutorEvent("chat_error", { hidden: true, message: String(error?.message || "") });
      setMessages((prev) => [...prev, { role: "assistant", content: error?.message || "AI öğretmene ulaşılamadı." }]);
      options.onError?.();
    } finally {
      window.clearTimeout(safetyTimer);
      setIsLoading(false);
      hiddenSendingRef.current = false;
    }
  };

  // Kazanım başı testi (ÖNCE ÖLÇ): AI tanıtımı bitirince, testi açılabilen kazanımda test kendiliğinden açılır.
  const scheduleGainStartTest = () => {
    pendingGainStartTestRef.current = gainIndexRef.current;
    setGainStartTick((tick) => tick + 1);
  };
  useEffect(() => {
    const idx = pendingGainStartTestRef.current;
    if (idx === null || !isOpen) return;
    if (isLoading || isTypingMessage || isPreparingSpeech || isSpeaking || hiddenSendingRef.current) return;
    pendingGainStartTestRef.current = null;
    if (gainIndexRef.current !== idx) return; // bu arada başka kazanıma geçildi
    const plan = Array.isArray(resolvedContext.relatedMaterials?.gainPlan) ? resolvedContext.relatedMaterials.gainPlan : [];
    const item = plan[idx];
    const gid = String(item?.gainId || "");
    // Yalnız plan "test açılabilir" diyorsa (taze havuz ≥3); zaten açık/çözülmüş testte tekrar açılmaz
    if (!gid || item?.testable !== true || nonTestableGainIdsRef.current.includes(gid)) return;
    if (completedTestGainIdRef.current === gid || openTestGainIdRef.current === gid || resolvedGainIds.includes(gid)) return;
    applyToolCalls([{ id: "ui-gain-start-test", name: "open_practice_test", arguments: { gain_id: gid } }], resolvedContext);
  }, [isOpen, isLoading, isTypingMessage, isPreparingSpeech, isSpeaking, gainStartTick]);

  // Bekleyen sistem mesajları (test sonucu, düğmeyle geçiş): panel boşalınca SIRAYLA gönder.
  // AI konuşurken de beklenir — yoksa yeni tur süren sesi keserdi (sesli/canlı mod).
  useEffect(() => {
    if (!isOpen || !pendingHiddenMessagesRef.current.length) return;
    if (isLoading || isTypingMessage || isPreparingSpeech || isSpeaking || hiddenSendingRef.current) return;
    const [pending, ...rest] = pendingHiddenMessagesRef.current;
    pendingHiddenMessagesRef.current = rest;
    void sendHiddenMessage(pending.text, pending.context, { queueIfBusy: true, afterReply: pending.afterReply, onError: pending.onError });
  }, [isOpen, isLoading, isTypingMessage, isPreparingSpeech, isSpeaking, hiddenQueueTick]);

  // Canlı modda konuşmasız kazanım geçişinden sonra: ekran boşalınca yeni kazanımı tanıttır (bkz. pendingGainKickoffRef).
  useEffect(() => {
    const kickoffIndex = pendingGainKickoffRef.current;
    if (kickoffIndex === null) return;
    if (isLoading || isTypingMessage || isPreparingSpeech || isSpeaking) return;
    pendingGainKickoffRef.current = null;
    // Bu arada başka bir geçiş olduysa eski tanıtım gönderilmez.
    if (gainIndexRef.current !== kickoffIndex) return;

    const plan = Array.isArray(resolvedContext.relatedMaterials?.gainPlan) ? resolvedContext.relatedMaterials.gainPlan : [];
    const nextGainText = gainLabel(plan[kickoffIndex]?.gainText || "");
    const kickoffTestable = plan[kickoffIndex]?.testable === true;
    void sendHiddenMessage(
      nextGainText
        ? `Sistem sıradaki kazanıma GEÇTİ: "${nextGainText}". ` +
          (kickoffTestable
            ? `Bu kazanımı EN FAZLA 2-3 cümleyle tanıt ve "önce kısa bir testle bakalım, test ekranda açılıyor" de; konuyu anlatma. `
            : "Bu kazanımı tek doğal cümleyle tanıt ve anlatmaya başla. ") +
          "Önceki kazanımı tekrar anlatma, başka kazanım adı uydurma."
        : "Sistem sıradaki kazanıma geçti. Yeni kazanımı kısaca tanıt; önceki kazanımı tekrar anlatma.",
      { ...getRequestContext(), currentGainIndex: kickoffIndex, isGainTransition: true },
      // Kazanım başı testi tanıtım BİTİNCE açılır (önce ölç); meşgulse tanıtım kuyruğa girer
      { queueIfBusy: true, afterReply: scheduleGainStartTest },
    );
  }, [isLoading, isTypingMessage, isPreparingSpeech, isSpeaking]);

  // Geçişler artık AI'nın move_to_next_gain tool çağrısıyla yapılıyor

  const sendMessage = async (
    text: string,
    options: { fromVoice?: boolean; contextOverride?: Record<string, any>; afterReply?: () => void } = {},
  ) => {
    if (isLoading || isTypingMessage || isPreparingSpeech) return;

    const trimmed = text.trim();
    const nextMessages = trimmed ? [...messages, { id: createMessageId(), role: "user" as const, content: trimmed }] : messages;

    if (trimmed) {
      lastUserMessageRef.current = trimmed;
      countUserTurn(); // aktif kazanımdaki öğretim etkileşimi sayacı
      const openReview = failedReviewRef.current;
      if (openReview && !options.contextOverride && openReview.currentNumber === reviewFrontier(openReview) && isReviewConfirmText(trimmed)) {
        setMessages(nextMessages);
        setInput("");
        confirmReviewQuestion();
        return;
      }
      if (!options.fromVoice && voiceConversationActiveRef.current) {
        setVoiceConversationActive(false);
        voiceConversationActiveRef.current = false;
        setVoiceBubbleActive(false);
        cleanupRecordingMonitor();
        recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
        recordingStreamRef.current = null;
        recorderRef.current = null;
        setIsRecording(false);
      }
      setMessages(nextMessages);
      setInput("");
    }

    setIsLoading(true);

    // 45 saniye içinde tamamlanmazsa tüm loading state'lerini sıfırla
    const safetyTimer = window.setTimeout(() => {
      setIsLoading(false);
      setIsTypingMessage(false);
      setIsPreparingSpeech(false);
      setIsSpeaking(false);
    }, 45000);

    try {
      // History = ÖNCEKİ mesajlar (messages, nextMessages DEĞİL) — güncel mesaj ayrıca `message`
      // alanında gidiyor; nextMessages kullanılırsa mesaj history'de de yer alıp AI'a 2 kez gider
      // ("7" -> "77"). transient/boş mesajlar da prompt'u kirletmesin diye filtrelenir.
      const trimmedHistory = messages.filter((m) => m.content && !m.transient).slice(-15);

      const effectiveRequestContext = options.contextOverride
        ? { ...getRequestContext(), ...options.contextOverride }
        : getRequestContext();

      // Live mod aktifken Live'da kal; bir retry sonrası hâlâ başarısızsa /chat'e düşme — hata göster
      const liveHandled = await tryLiveWithRetry(trimmed, effectiveRequestContext, trimmedHistory);
      if (liveHandled) {
        options.afterReply?.();
        return;
      }
      if (isLiveModeActive()) {
        setMessages((prev) => [...prev, { id: createMessageId(), role: "assistant", content: "Sesli oturum şu an açılamadı. Sıfırla ya da birkaç saniye sonra tekrar dene.", transient: true }]);
        return;
      }

      const response = await ApiService.sendAITeacherMessage({
        message: trimmed,
        context: effectiveRequestContext,
        history: trimmedHistory,
      });
      const nextContext = mergeServerContext(response.context || resolvedContextRef.current);
      setResolvedContextNow(nextContext);

      const assistantPayload = extractTextualToolCallsFromMessage(response.message || "");
      // Boş yanıt öğrenciye kalıp cümle olarak gidiyordu ("Devam edelim…", saha 7 Eki 4 kez) — kayda yaz
      if (!assistantPayload.message) reportTutorEvent("empty_reply", { message: trimmed.slice(0, 120) });
      const assistantMessage = getSafeAssistantMessage(
        assistantPayload.message || "Devam edelim. Bu konuda aklına gelen ilk şeyi söyle.",
        "Eksik olduğun bu kazanımda önce ana fikri netleştirelim. Konuyu küçük bir örnekle bağlayıp sonra sana kısa bir kontrol sorusu soracağım.",
      );

      const toolCalls = [...(response.toolCalls || []), ...assistantPayload.toolCalls];
      const applied = applyToolCalls(enhanceToolCallsForAssistantMessage(assistantMessage, toolCalls, nextContext), nextContext);
      queueGainIntro(response, applied);
      setIsLoading(false);
      const hideTransitionText = applied.blockedNextGain && (applied.blockedByModel || announcesGainTransition(assistantMessage));
      await deliverAssistantReply(response, applied, hideTransitionText ? buildStayOnGainText() : assistantMessage);
      options.afterReply?.();
    } catch (error: any) {
      const errorMessage = error?.message || "AI öğretmene ulaşılamadı.";
      reportTutorEvent("chat_error", { message: String(errorMessage).slice(0, 200) });
      setMessages((prev) => [...prev, { role: "assistant", content: errorMessage }]);
    } finally {
      window.clearTimeout(safetyTimer);
      setIsLoading(false);
    }
  };

  // Canlı modda mikrofon sesini 16 kHz PCM olarak biriktir (MediaRecorder yanında; OpenAI yolu geri dönüş için kalır).
  const startLivePcmCapture = (stream: MediaStream) => {
    stopLivePcmCapture();
    try {
      const AudioContextClass = window.AudioContext || (window as Window & typeof globalThis & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextClass) return;
      const context = new AudioContextClass();
      const source = context.createMediaStreamSource(stream);
      const processor = context.createScriptProcessor(4096, 1, 1);
      const chunks: Int16Array[] = [];
      processor.onaudioprocess = (event) => {
        const input = event.inputBuffer.getChannelData(0);
        chunks.push(downsampleFloat32ToInt16(input, context.sampleRate, LIVE_INPUT_SAMPLE_RATE));
      };
      source.connect(processor);
      processor.connect(context.destination); // ScriptProcessor çalışmak için bağlı olmalı; kazancı yok, hoparlöre ses vermez
      livePcmCaptureRef.current = { context, source, processor, chunks };
    } catch {
      livePcmCaptureRef.current = null; // yakalama kurulamazsa OpenAI yolu devam eder
    }
  };

  const stopLivePcmCapture = (): Int16Array | null => {
    const capture = livePcmCaptureRef.current;
    livePcmCaptureRef.current = null;
    if (!capture) return null;
    try {
      capture.processor.onaudioprocess = null;
      capture.source.disconnect();
      capture.processor.disconnect();
      void capture.context.close();
    } catch { /* kapatma hatası önemsiz */ }
    return capture.chunks.length ? concatInt16(capture.chunks) : null;
  };

  const startRecording = async () => {
    if (isRecording || isLoading || isPreparingSpeech) return;

    voiceCancelRequestedRef.current = false;
    setVoiceConversationActive(true);
    voiceConversationActiveRef.current = true;
    setVoiceBubbleActive(true);
    setVoiceLevel(AI_TUTOR_VOICE_LEVEL_IDLE);
    try {
      // Echo cancellation şart — AI kendi sesini duymasın
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorderRef.current = recorder;
      recordingStreamRef.current = stream;
      if (voiceEnabledRef.current && audioModeRef.current === "live") startLivePcmCapture(stream);

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

      recorder.onstop = async () => {
        cleanupRecordingMonitor();
        stream.getTracks().forEach((track) => track.stop());
        recordingStreamRef.current = null;
        recorderRef.current = null;
        setIsRecording(false);

        const audioBlob = new Blob(chunksRef.current, { type: "audio/webm" });
        const livePcm = stopLivePcmCapture();
        if (!voiceConversationActiveRef.current) return;
        if (audioBlob.size === 0) return;

        // Canlı mod: ses doğrudan Live'a. Sessizlikse hiç gönderme; Live başarısızsa aşağıdaki OpenAI yoluna düş.
        if (livePcm && isLiveModeActive()) {
          if (pcmPeak(livePcm) < LIVE_INPUT_MIN_PEAK) return;
          const handledByLive = await sendAudioMessage(livePcm);
          if (handledByLive) return;
        }

        setIsLoading(true);
        try {
          const transcription = await ApiService.transcribeAudio(audioBlob, "tr");
          const text = String(transcription.text || "").trim();
          setInput(text);
          setIsLoading(false);
          if (text) {
            await sendMessage(text, { fromVoice: true });
          }
        } catch (error: any) {
          setSpeechError(error?.message || "Ses metne çevrilemedi.");
        } finally {
          setIsLoading(false);
        }
      };

      recorder.start();
      startRecordingSilenceMonitor(stream);
      setIsRecording(true);
    } catch (error: any) {
      setSpeechError(error?.message || "Mikrofon açılamadı.");
      setVoiceBubbleActive(false);
      setVoiceConversationActive(false);
      voiceConversationActiveRef.current = false;
      cleanupRecordingMonitor();
      stopLivePcmCapture();
      recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
      recordingStreamRef.current = null;
      recorderRef.current = null;
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    cleanupRecordingMonitor();
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
      return;
    }

    recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
    recordingStreamRef.current = null;
    recorderRef.current = null;
    setIsRecording(false);
  };
  stopRecordingRef.current = stopRecording;

  useEffect(() => {
    if (
      !voiceConversationActive ||
      !voiceEnabled ||
      isRecording ||
      isLoading ||
      isTypingMessage ||
      isPreparingSpeech ||
      isSpeaking
    ) {
      return;
    }

    const listenAgainTimer = window.setTimeout(() => {
      if (!voiceConversationActiveRef.current || recorderRef.current || recordingStreamRef.current) return;
      void startRecording();
    }, 420);

    return () => window.clearTimeout(listenAgainTimer);
  }, [voiceConversationActive, voiceEnabled, isRecording, isLoading, isTypingMessage, isPreparingSpeech, isSpeaking]);

  const cancelVoiceConversation = () => {
    voiceCancelRequestedRef.current = true;
    flushCurrentAssistantTyping();
    setVoiceConversationActive(false);
    voiceConversationActiveRef.current = false;
    setVoiceBubbleActive(false);
    setVoiceLevel(AI_TUTOR_VOICE_LEVEL_IDLE);
    setIsRecording(false);

    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    } else {
      recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
      recordingStreamRef.current = null;
      recorderRef.current = null;
    }

    cleanupRecordingMonitor();
    stopLivePcmCapture();
    // NOT: stopCurrentAudio() ve closeLiveConnection() artık çağrılmıyor —
    // AI konuşuyorsa konuşmasını bitirsin, Live bağlantı bir sonraki turda da çalışsın
  };

  // Son kazanımda "Konuyu bitir": testi çözülmemiş (ölçülebilir) kazanımda önce sorulur (geçişteki onayla aynı).
  const finishTopicByButton = (force = false) => {
    if (!canFinishByButton || isLoading) return;
    const currentTestable = currentPlanItem?.testable !== false && !nonTestableGainIdsRef.current.includes(currentPlanGainId);
    const currentDone = resolvedGainIds.includes(currentPlanGainId) || passedTestGainIds.includes(currentPlanGainId);
    if (!force && currentTestable && !currentDone) {
      setConfirmSkipGain(true);
      return;
    }
    flushCurrentAssistantTyping();
    void finishTopic("button", force);
  };

  // Testi çözülmemiş (ölçülebilir) kazanımı düğmeyle geçmek eksiği AÇIK bırakır — önce sorulur.
  const goToNextGainByButton = (force = false) => {
    if (!canAdvanceByButton || isLoading) return;
    const currentTestable = currentPlanItem?.testable !== false && !nonTestableGainIdsRef.current.includes(currentPlanGainId);
    const currentResolved = resolvedGainIds.includes(currentPlanGainId);
    if (!force && currentTestable && !currentResolved) {
      setConfirmSkipGain(true);
      return;
    }
    flushCurrentAssistantTyping();
    stopCurrentAudio();
    // stopCurrentAudio konuşma durumunu sıfırlamaz; isSpeaking açık kalırsa bekleyen geçiş mesajı
    // hiç gitmiyor, AI yeni kazanımı tanıtmıyordu (TTS modu, ikinci inceleme)
    setIsSpeaking(false);
    setIsPreparingSpeech(false);
    const nextIndex = advanceGain("button");
    if (nextIndex === null) return;
    const nextText = gainLabel(topicGainPlan[nextIndex]?.gainText || "");
    const leftOpenNote = currentTestable && !currentResolved
      ? "Önceki kazanımın pekiştirme testi çözülmediği için o eksik açık kaldı; bunu tek cümleyle, suçlamadan hatırlat. "
      : "";
    // Model geçişi BİLMELİ: bildirim turu (isGainTransition) yeniden geçiş ya da test açmaz.
    const nextTestable = topicGainPlan[nextIndex]?.testable === true;
    void sendHiddenMessage(
      `[SİSTEM] Öğrenci ekrandaki "Sıradaki kazanım" düğmesine bastı; sistem sıradaki kazanıma GEÇTİ: "${nextText}". ` +
        leftOpenNote +
        (nextTestable
          ? "Bu kazanımı EN FAZLA 2-3 cümleyle tanıt ve \"önce kısa bir testle nerede olduğuna bakalım, test ekranda açılıyor\" de; konuyu anlatma. "
          : "Bu kazanımı kısaca tanıt ve anlatmaya başla. ") +
        "Önceki kazanımı tekrar anlatma, başka kazanım adı uydurma.",
      { ...getRequestContext(), currentGainIndex: nextIndex, isGainTransition: true },
      { queueIfBusy: true, afterReply: scheduleGainStartTest },
    );
  };

  // Geçilen testin ekranındaki düğme: sıradaki kazanımın testi açılabiliyorsa onu başlatır (önce ölç),
  // değilse incelemeyi kapatıp sohbete döner.
  const nextPlanItemForTest = topicGainPlan[currentGainIndex] || null;
  const passedNextAction = nextPlanItemForTest?.testable === true
    && !resolvedGainIds.includes(String(nextPlanItemForTest.gainId || ""))
    && !passedTestGainIds.includes(String(nextPlanItemForTest.gainId || ""))
    ? { label: "Sıradaki kazanımın testine başla", onClick: () => openTestForCurrentGain() }
    : { label: "İncelemeyi kapat, devam et", onClick: () => setActiveMaterial({ type: "empty" }) };

  // Onay kutusundaki "Testi aç": AI'nın test aracıyla AYNI yoldan (kilitler, ses, taslaklar)
  const openTestForCurrentGain = () => {
    setConfirmSkipGain(false);
    if (!currentPlanGainId) return;
    applyToolCalls([{ id: "ui-open-test", name: "open_practice_test", arguments: { gain_id: currentPlanGainId } }], resolvedContext);
  };

  const handleClose = () => {
    voiceCancelRequestedRef.current = true;
    flushCurrentAssistantTyping();
    stopCurrentAudio();
    closeLiveConnection();

    // Panel kapanırken üzerinde çalışılan kazanımı kapat DENE (testle ölçülemeyen kazanımın kapanma yolu; ölçülebilir
    // kazanımı sunucu reddeder). Sayfa değişiminde de aynı deneme isOpen=false bloğundan yapılır — oturumda bir kez.
    tryCloseActiveGainOnExit();

    onClose();
  };

  const replayLastAssistantMessage = () => {
    if (!lastAssistantMessage) return;

    setVoiceEnabled(true);
    setSpeechNeedsGesture(false);
    void playTtsAudio(lastAssistantMessage, { requireGestureFallback: false });
  };

  const handlePracticeTestComplete = async (summary: PracticeTestResultSummary) => {
    // Geçiş OLDU MU sorusunu cevaplayabilmek için index'i ilerlemeden ÖNCE yakala.
    // Aşağıdaki gizli mesaj modele "sıradaki kazanımı tanıt" diyordu — plan bittiğinde
    // tanıtacak bir şey olmadığı için model kazanım UYDURUYORDU (saha 9 Ağu: "Yeni konumuzda
    // ardışık sayıların toplamları..." dedi, oysa planda tek kazanım vardı).
    const gainIndexBeforeComplete = gainIndexRef.current;
    const epochAtComplete = sessionEpochRef.current;

    // Test bittikten sonra Live mod ve ses tam çalışsın
    try {
      getStreamAudioContext(24000);
    } catch {}

    // Sesi SADECE test öncesi öğrenci sesli konuşuyorsa geri aç — text kullanıcıyı ses moduna zorlama
    // (aksi halde test bitince ekranda beklenmedik ses balonu çıkıp interaktif akışı bozuyordu).
    if (voiceActiveBeforeTestRef.current) {
      voiceConversationActiveRef.current = true;
      setVoiceConversationActive(true);
      setVoiceBubbleActive(true);
    }

    const completedGainId = activeMaterial.type === "test" ? activeMaterial.gainId : "";
    const localStrictPass = summary.wrong === 0 && summary.blank === 0;

    // SUNUCU-OTORİTER SONUÇ: cevaplar sunucuda yeniden puanlanır; %70+ ise kazanım
    // "giderildi" (isResolved+resolvedAt) + defter yazılır ve GEÇİŞ kararı da bu sonuçtan
    // okunur. FE'nin %100 / BE'nin %70 kuralı ayrışınca "Eksiklerim'den düştü ama panel
    // beni kazanımda kilitledi" tutarsızlığı doğuyordu. Sunucuya ulaşılamazsa katı yerel kural.
    const closureGainId = completedGainId
      || String(topicGainPlan[gainIndexRef.current]?.gainId || resolvedContext.gainId || "");
    let serverResult: { passed: boolean; correct: number; total: number; percent: number; resolvedCount: number } | null = null;
    if (closureGainId && summary.answers?.length) {
      try {
        serverResult = await ApiService.completeAITeacherPracticeTest({
          gainId: closureGainId,
          answers: summary.answers,
          context: getRequestContext(),
        });
      } catch (error: any) {
        serverResult = null;
        reportTutorEvent("practice_complete_failed", { gainId: closureGainId, message: String(error?.message || "") });
      }
    }
    const passed = serverResult ? Boolean(serverResult.passed) : localStrictPass;
    // Puanlama beklenirken panel kapandı/sıfırlandıysa sonuç YENİ oturuma taşınmaz (✓, not, AI mesajı)
    if (epochAtComplete !== sessionEpochRef.current) return;
    if (passed && completedGainId) {
      setPassedTestGainIds((prev) => (prev.includes(completedGainId) ? prev : [...prev, completedGainId]));
    }

    // Sunucu gerçekten bir UserGain kapattıysa: kartta ✓, sohbette "eksiğin giderildi" notu ve açık
    // listeler (ders ekranındaki Eksikler, Eksiklerim, Konu Eksikleri) tazelenir.
    if (serverResult?.resolvedCount) markGainResolved(closureGainId, serverResult.resolvedCount);

    // Reopen guard SADECE başarıda — başarısızsa aynı kazanıma pekiştirme testi tekrar açılabilsin
    completedTestGainIdRef.current = passed ? (completedGainId || null) : null;
    // Geçildiyse "açık test" kaydı düşer → öğrenci isterse aynı kazanıma yeni test açılabilir
    // (tek kazanımlı planda test sonrası "bir test daha" isteği sessizce yutuluyordu)
    if (passed) openTestGainIdRef.current = null;
    // Başarısızsa bu kazanımda İLERLEME KİLİDİ; başarıyla geçilince kilit kalkar
    lastFailedTestGainIdRef.current = passed ? null : (completedGainId || null);
    // Kalınan test → SORU SORU İNCELEME: yanlış + boş sorular öğrencinin cevaplarıyla; ekranda ilk yanlış açılır, AI onu
    // konuşur; her biri "anladım"la geçilmeden test yeniden başlamaz (sahibin 20 Eki kararı).
    if (passed) {
      clearFailedReview();
    } else if (completedGainId) {
      const served = lastServedTestRef.current?.gainId === completedGainId ? lastServedTestRef.current.questions : [];
      const reviewQuestions: FailedReviewQuestion[] = (summary.answers || [])
        .map((answer, i) => ({ ...(served[i] || {}), number: i + 1, studentAnswer: answer.selected || null }))
        .filter((q: any) => q.correctAnswer && (!q.studentAnswer || q.studentAnswer !== q.correctAnswer));
      failedReviewRef.current = reviewQuestions.length
        ? {
            gainId: completedGainId,
            // İlk başarısızlıkta doğru şık söylenmez (öğrenci adım adım bulur); ikincisinden itibaren tam çözüm
            revealAnswer: (failedAttemptsByGainRef.current[completedGainId] || 0) + 1 >= 2,
            questions: reviewQuestions,
            currentNumber: reviewQuestions[0].number,
            discussed: [],
          }
        : null;
      syncReviewState();
    }

    // DETERMİNİSTİK İLERLEME: eşik geçildiyse sonraki kazanıma otomatik geç (tek kapıdan).
    // Yalnız test EDİLEN kazanım hâlâ aktifse — bu arada başka yoldan geçildiyse ikinci kez değil.
    const gp = Array.isArray(resolvedContext.relatedMaterials?.gainPlan) ? resolvedContext.relatedMaterials.gainPlan : [];
    const testedIndex = completedGainId
      ? gp.findIndex((g: any) => g?.gainId === completedGainId || g?.lessonSectionContentGainId === completedGainId)
      : -1;
    // Test ÖNCEKİ bir kazanıma aitse (yarım testle "Yine de geç" deyip sonra "Teste dön"): o eksik
    // kapanır ama ilerleme olmaz; AI'ya "planın sonu" DENMEZ, bulunulan kazanıma dönülür.
    const testedEarlierGain = testedIndex !== -1 && testedIndex < gainIndexBeforeComplete;
    if (passed) {
      if (testedIndex === -1 || testedIndex === gainIndexRef.current) {
        const advancedTo = advanceGain("test-passed", { gainPlan: gp, clearWorkspace: false });
        // Son kazanımın testi geçildi → konu tamamlandı (eskiden geçilecek kazanım yok diye oturum hiç bitmiyordu)
        if (advancedTo === null && gp.length && gainIndexRef.current >= gp.length - 1) void finishTopic("test-passed");
      }
    }

    // isPracticeResult: bu bir test SONUCU mesajı → backend otomatik test enjeksiyonundan MUAF.
    // Fix B: getRequestContext() bu render'da BAYAT currentGainIndex okuyor (setResolvedContext henüz
    // flush olmadı); otoriter gainIndexRef.current'i açıkça geçir ki geçince "tebrik + yeni kazanımı
    // tanıt" mesajı YENİ kazanıma grounded olsun (eskiyi yeniden anlatmasın).
    const resultContext = { ...getRequestContext(), currentGainIndex: gainIndexRef.current, isPracticeResult: true };

    // Uzun yazma animasyonu sürerken gelen sonuç mesajı sessizce düşüyordu (sendHiddenMessage
    // isTypingMessage guard'ına takılıp) — önce animasyonu tamamla, sonra gönder.
    flushCurrentAssistantTyping();

    // Kaçıncı başarısız deneme? İlkinde cevap SÖYLENMEZ (bkz. failedAttemptsByGainRef).
    const attemptGainId = String(closureGainId || completedGainId || "");
    if (!passed && attemptGainId) {
      failedAttemptsByGainRef.current[attemptGainId] = (failedAttemptsByGainRef.current[attemptGainId] || 0) + 1;
    }
    const failedAttempts = attemptGainId ? failedAttemptsByGainRef.current[attemptGainId] || 0 : 0;
    const ilkBasarisizlik = failedAttempts <= 1;

    // Takıldığı yer için elde olan materyal: kazanımın konu anlatım videosu (dakika aralığıyla) ve föy sayfası
    const testedPlanItem = planGainItemFor(String(closureGainId || completedGainId || ""));
    // Yanlışların yarıdan fazlası yanlış/boşsa sorun tek tek sorularda değil kavramın kendisinde:
    // önce konu anlatım videosunun bu kazanımı anlatan bölümü önerilir (sahibin isteği, 8 Eki)
    const missRatio = summary.total ? (summary.wrong + summary.blank) / summary.total : 0;
    const resourceNote = (missRatio >= 0.5 && testedPlanItem?.video?.url
      ? "Yanlışlarımın çoğu yanlış — kavramın kendisinde eksiğim var: soru soru gitmeden ÖNCE bu kazanımın konu anlatım videosunun ilgili bölümünü izlememi öner (istersem open_lesson_video ile aç), sonra soruları ele al. "
      : "") + buildResourceNote(testedPlanItem);
    const wrongText = summary.wrongQuestions
      .slice(0, 5)
      .map((item) => (
        `${item.index}. soru: öğrenci ${item.selectedAnswer || "boş"} işaretledi${item.selectedAnswerText ? ` (${item.selectedAnswerText})` : ""}` +
        // İlk başarısızlıkta doğru şık BU SATIRA yazılmaz — model parafrazlayıp söylemesin.
        (ilkBasarisizlik && !passed
          ? ""
          : `, doğru cevap ${item.correctAnswer || "belirsiz"}${item.correctAnswerText ? ` (${item.correctAnswerText})` : ""}`) +
        `. Soru: ${item.text}`
      ))
      .join("\n");

    // GEÇİŞ TALİMATI ARTIK KESİN: "geçmiş OLABİLİR, öyleyse tanıt" belirsizliği modele
    // kazanım uydurtuyordu. Geçiş gerçekten olduysa yeni kazanımın METNİNİ de veriyoruz
    // (içeriğini de uydurmasın); olmadıysa açıkça yasaklıyoruz.
    const planForTransition = Array.isArray(resolvedContext.relatedMaterials?.gainPlan)
      ? resolvedContext.relatedMaterials.gainPlan
      : [];
    const advancedToNextGain = gainIndexRef.current > gainIndexBeforeComplete;
    const nextGainText = String(planForTransition[gainIndexRef.current]?.gainText || "")
      .replace(/\s+/g, " ").trim().slice(0, 160);
    const currentGainText = String(planForTransition[gainIndexRef.current]?.gainText || "")
      .replace(/\s+/g, " ").trim().slice(0, 160);
    const isLastGain = gainIndexRef.current >= planForTransition.length - 1;
    const nextGainTestable = planForTransition[gainIndexRef.current]?.testable === true;
    const transitionInstruction = advancedToNextGain && nextGainText
      ? `Sistem sıradaki kazanıma GEÇTİ: "${nextGainText}". Bu kazanımı tek doğal cümleyle tanıt; başka kazanım adı uydurma. ` +
        (nextGainTestable
          ? `Yeni kazanımı ANLATMA: "hazır olduğunda soldaki düğmeyle yeni kazanımın kısa testine başlayalım" de (önce ölçüyoruz). `
          : "")
      : testedEarlierGain
        ? `Bu test daha önceki bir kazanımındı; şu an "${currentGainText}" kazanımındayız. Yeni kazanım TANITMA, bu kazanımdan devam et. `
        : !isLastGain
          ? `Şu an "${currentGainText}" kazanımındayız. Yeni kazanım TANITMA, bu kazanımdan devam et. `
          : `Bu, çalışma planının SON kazanımıydı; sırada BAŞKA KAZANIM YOK. Yeni konu/kazanım TANITMA ve "yeni konumuz..." gibi bir geçiş cümlesi KURMA. Oturumu toparla: istersem tekrar çalışabileceğimi ya da föydeki değerlendirme sorularına bakabileceğimizi söyle. `;

    if (passed && localStrictPass) {
      void sendHiddenMessage(
        `Pekiştirme testini YANLIŞSIZ tamamladım. Beni kısaca tebrik et. ${transitionInstruction}Aynı testi tekrar AÇMA.`,
        resultContext,
        { queueIfBusy: true },
      );
    } else if (passed) {
      void sendHiddenMessage(
        `Pekiştirme testini ${serverResult ? `%${serverResult.percent} ile` : ""} BAŞARIYLA GEÇTİM (${summary.correct} doğru, ${summary.wrong} yanlış, ${summary.blank} boş — eşik aşıldı, kazanım giderildi sayıldı). ` +
        `Önce beni kısaca tebrik et. ${transitionInstruction}` +
        `Sonra aşağıdaki hatalarımı KISACA (her biri 1-2 cümle) açıkla; YENİ TEST AÇMA. ` +
        `Bu kazanım KAPANDI: testi "tekrar çözmemi" ÖNERME, yanlışı açıkladıktan sonra yeni kazanımla devam et.\n${wrongText || "(yanlış işaretlediğim soru yok)"}`,
        resultContext,
        { queueIfBusy: true },
      );
    } else {
      const firstReview = failedReviewRef.current?.questions[0]?.number;
      void sendHiddenMessage(
        `Pekiştirme testini GEÇEMEDİM: ${summary.correct} doğru, ${summary.wrong} yanlış, ${summary.blank} boş. ` +
        `Yanlışlarımı SORU SORU konuşacağız: sol tarafta ${firstReview ? `${firstReview}. soru` : "ilk yanlışım"} açık. ŞİMDİ yalnız BU soruyu ele al — ne işaretlediğimi (ya da boş bıraktığımı), nerede yanılmış olabileceğimi ve doğru düşünme yolunu anlat; ` +
        (ilkBasarisizlik
          // İLK BAŞARISIZLIK — doğru şıkkı söyleme, adım adım buldur. Yoksa tekrar testinde aynı sorular gelince ezber olur.
          ? `doğru ŞIKKI DOĞRUDAN söyleme, beni adım adım cevaba götür (ekranda da doğru cevaplar gösterilmiyor). `
          : `bu kazanımda ikinci kez takıldım — tam çözümü anlatabilirsin (ekranda doğru cevaplar artık görünüyor); çözümlü videosu varsa izlememi öner. `) +
        `Sonunda anladığımı kısa bir soruyla kontrol et. Diğer yanlışlara KENDİN geçme: ben "anladım" deyince sistem sıradaki yanlışı açıp sana söyleyecek; hepsi bitince testi yeniden başlatmanı isteyecek. ` +
        `${resourceNote}Şimdi YENİ TEST AÇMA ve bir sonraki kazanıma GEÇME (move_to_next_gain çağırma).`,
        resultContext,
        { queueIfBusy: true },
      );
    }
  };

  // İlk kalışta doğru şık söylenmez; kural yalnız sonuç mesajında kalınca model sonraki yanlışlarda şıkkı söylüyordu (TED 30 Eki)
  const reviewAnswerRule = (review: { revealAnswer: boolean }) => (
    review.revealAnswer ? "" : "doğru ŞIKKI DOĞRUDAN söyleme, beni adım adım cevaba götür; "
  );

  // Sıradaki konuşulacak yanlış: ondan sonrakiler "anladım" denene kadar kilitli
  const reviewFrontier = (review: { questions: FailedReviewQuestion[]; discussed: number[] }) => (
    review.questions.find((q) => !review.discussed.includes(q.number))?.number ?? null
  );

  // Çip / "Önceki-Sonraki yanlış": konuşulmuş sorulara ya da sıradaki yanlışa dönülür. AI turu AÇMAZ: her tıklama tur açınca AI
  // ekranda olmayan soruları art arda yazıyor, mesaj kutusu kilitli kalıyordu (TED 30 Eki). Öğrenci yazarsa AI ekrandaki soruyla cevaplar.
  const focusReviewQuestion = (number: number) => {
    const review = failedReviewRef.current;
    if (!review || review.currentNumber === number) return;
    if (!review.discussed.includes(number) && number !== reviewFrontier(review)) return;
    review.currentNumber = number;
    syncReviewState();
  };

  // "Bu soruyu anladım": ekrandaki yanlış konuşuldu sayılır, sıradaki açılır ve AI onu anlatır; sonuncusuysa test yeniden başlatılır
  const confirmReviewQuestion = () => {
    const review = failedReviewRef.current;
    // hiddenSendingRef: çift tıklama bir yanlışı atlatmasın (ref anında değişir, ikinci tıklama sıradakini "anladım" sayardı)
    if (!review || isLoading || isTypingMessage || isPreparingSpeech || hiddenSendingRef.current) return;
    const done = review.currentNumber;
    if (done !== reviewFrontier(review)) return;
    review.discussed.push(done);
    const next = reviewFrontier(review);
    if (next !== null) review.currentNumber = next;
    syncReviewState();
    // Video/PDF açıkken yazıyla "anladım" denirse AI ekranda olmayan soruyu anlatmasın
    if (activeMaterial.type !== "test" && pendingTest) setActiveMaterial(pendingTest);
    setMobileView("chat"); // telefonda AI'nın anlatımı sohbette; soruya "Materyali Gör"le dönülür
    void sendHiddenMessage(
      next !== null
        ? `${done}. soruyu anladım. Sol tarafta şimdi ${next}. soru (sıradaki yanlışım) açık. Yalnız BU soruyu konuşalım: ne işaretlediğim, nerede yanıldığım ve doğru düşünme yolu; ${reviewAnswerRule(review)}anladığımı kısa bir soruyla kontrol et.`
        : `${done}. soruyu da anladım; yanlışlarımın HEPSİNİ konuştuk. Şimdi open_practice_test ile testi YENİDEN BAŞLAT ve "şimdi testi yeniden başlatıyorum" de (sistem varsa yeni, yoksa aynı soruları verir).`,
      { ...getRequestContext(), currentGainIndex: gainIndexRef.current },
      {
        queueIfBusy: true,
        // Sıradaki yanlış anlatılamadıysa geçilmiş sayılmaz: öğrenci aynı soruda kalır, "anladım" yeniden dener
        onError: () => {
          if (failedReviewRef.current !== review) return;
          review.discussed = review.discussed.filter((n) => n !== done);
          review.currentNumber = done;
          syncReviewState();
        },
      },
    );
  };

  const startRetest = (gainId: string) => {
    // AI son cevabını yazarken basılırsa testi o da açar; ikinci açılış işaretlenen cevapları siliyordu
    if (!reviewAllDone(gainId) || isLoading || isTypingMessage || isPreparingSpeech || hiddenSendingRef.current) return;
    flushCurrentAssistantTyping();
    void loadPracticeTest(gainId);
  };

  // Yanıt bitince mesaj kutusuna OTOMATİK ODAK — input yanıt sırasında disabled olduğu için
  // odak düşüyor ve her turda kutuya yeniden tıklamak gerekiyordu. Yalnız geniş ekranda
  // (mobilde otomatik odak klavyeyi istenmeden açar).
  useEffect(() => {
    if (!isOpen || isLoading || isTypingMessage || isPreparingSpeech) return;
    if (voiceBubbleActive || isRecording || isSpeaking) return; // sesli moddayken odak çalma
    if (typeof window === "undefined" || window.innerWidth < 768) return;
    chatInputRef.current?.focus();
  }, [isOpen, isLoading, isTypingMessage, isPreparingSpeech, voiceBubbleActive, isRecording, isSpeaking]);

  // Mobil görünüm: materyal açılınca çalışma alanına geç, materyal kapanınca sohbete dön
  useEffect(() => {
    setMobileView(activeMaterial.type === "empty" ? "chat" : "workspace");
  }, [activeMaterial.type]);

  // Açılan testi hatırla: öğrenci test ortasında "pdf aç" derse geri dönebilsin.
  useEffect(() => {
    if (activeMaterial.type === "test") setPendingTest(activeMaterial);
  }, [activeMaterial]);

  // PLANDAN TOHUMLA: teach/start artık kazanım başına GERÇEK taze havuzu sayıp testable
  // yazıyor. testable=false olanlar daha İLK turdan işaretlenir — model hiç teklif etmez,
  // "olur→geçelim→mühürleyelim" döngüsü hiç başlamaz (9 Ağu akşam sahası).
  useEffect(() => {
    const plan = Array.isArray(resolvedContext.relatedMaterials?.gainPlan)
      ? resolvedContext.relatedMaterials.gainPlan
      : [];
    for (const item of plan) {
      if (item?.testable !== false) continue;
      for (const rawId of [item.gainId, item.lessonSectionContentGainId]) {
        const id = String(rawId || "");
        if (id && !nonTestableGainIdsRef.current.includes(id)) nonTestableGainIdsRef.current.push(id);
      }
    }
  }, [resolvedContext]);

  // Taslağı bileşen dışında sakla (unmount'ta kaybolmasın)
  const handlePracticeDraftChange = (key: string, draft: PracticeTestDraft) => {
    practiceTestDraftsRef.current[key] = draft;
  };

  // "Teste dön" yalnız YARIM kalan testte anlamlı: tamamlanmışsa ya da hiç cevap
  // girilmemişse düğmeyi göstermeyip ekranı kalabalıklaştırmıyoruz.
  const pendingTestDraft = pendingTest ? practiceTestDraftsRef.current[buildPracticeTestKey(pendingTest)] : null;
  const canReturnToTest =
    Boolean(pendingTest) &&
    !["test", "test-loading", "test-error"].includes(activeMaterial.type) &&
    Boolean(pendingTestDraft) &&
    !pendingTestDraft?.isCompleted &&
    // Hepsi boş bırakılan testin incelemesinde cevap yok ama düğmeler orada: dönüş şeridi çıkmazsa öğrenci videoda kalıyordu
    (Object.keys(pendingTestDraft?.selectedAnswers || {}).length > 0 || Boolean(pendingTestDraft?.isReviewMode));

  if (!isOpen) return null;

  const shellLeftClass =
    typeof window !== "undefined" && window.innerWidth < 768
      ? "left-0"
      : sidebarExpanded
        ? "md:left-64 left-0"
        : "md:left-[50px] left-0";
  // Tam ekranda üst başlık ve sol menü de kaplanır. z-index uygulama başlığının
  // ÜSTÜNDE olmalı: Header z-[999] ve opak; altında kalınca panelin kendi başlık
  // düğmeleri (tam ekran/kalem/ses) ilk 60px'te görünmez oluyordu.
  const shellPositionClass = isExpanded ? "top-0 left-0 z-[1000]" : `top-[58px] ${shellLeftClass} z-[70]`;
  // Telefonda materyale geçiş düğmesi (yalnız sohbet görünümünde ve materyal varken)
  const showMaterialButton =
    activeMaterial.type !== "empty" && mobileView === "chat" ? (
      <button
        type="button"
        onClick={() => setMobileView("workspace")}
        className="md:hidden rounded-full bg-orange-500 px-4 py-2 text-xs font-bold text-white shadow active:scale-95"
      >
        Materyali Gör
      </button>
    ) : null;
  const inputBusy = isLoading || isTypingMessage || isPreparingSpeech;
  const recordingDisabled = (inputBusy || isSpeaking) && !isRecording;
  const voiceBubbleExpanded = voiceBubbleActive || isRecording || isSpeaking || isPreparingSpeech;
  const voiceBubbleAnimating = isRecording || isSpeaking;
  const handleVoiceBubbleClick = () => {
    if (isRecording) {
      stopRecording();
      return;
    }

    void startRecording();
  };

  const handleResetConversation = async () => {
    const sessionPlanId = context?.sessionPlanId || resolvedContext?.sessionPlanId;
    if (!sessionPlanId) return;
    if (!window.confirm("Bu konuşmayı sıfırlamak istediğine emin misin? Tüm mesajlar silinecek ve 1. kazanımdan yeniden başlayacaksın.")) return;

    try {
      await ApiService.resetSessionPlan(String(sessionPlanId));
    } catch {
      // Backend'e ulaşılamazsa yine de local state'i temizle — kullanıcı tekrar deneyebilir
    }

    stopCurrentAudio();
    closeLiveConnection();
    cleanupRecordingMonitor();
    recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
    recordingStreamRef.current = null;
    recorderRef.current = null;
    setIsRecording(false);
    setIsSpeaking(false);
    setIsPreparingSpeech(false);
    setIsTypingMessage(false);
    setLastAssistantMessage("");
    setInput("");
    setMessages([]);
    setActiveMaterial({ type: "empty" });
    gainIndexRef.current = 0;
    lastGainAdvanceAtRef.current = 0; // sıfırla: yeni oturum sayaçları
    userTurnsOnGainRef.current = 0;
    openTestGainIdRef.current = null;
    pendingHiddenMessagesRef.current = [];
    pendingGainStartTestRef.current = null;
    deferredLiveMoveRef.current = null;
    sessionEpochRef.current += 1;
    setResolvedGainIds([]);
    setLeftOpenGainIds([]);
    setPassedTestGainIds([]);
    setConfirmSkipGain(false);
    // Sıfırlama = yeni oturum: test kilitleri, taslaklar ve sayaçlar da temizlenir —
    // yoksa "baştan başlayan" öğrenci önceki turun kilitlerini/taslaklarını taşıyordu.
    openTestQuestionsRef.current = [];
    completedTestGainIdRef.current = null;
    lastFailedTestGainIdRef.current = null;
    clearFailedReview();
    definitionOnlyNotifiedRef.current = new Set();
    nonTestableGainIdsRef.current = []; // plandan tohumlama effect'i yeniden doldurur
    failedAttemptsByGainRef.current = {};
    practiceTestDraftsRef.current = {};
    setPendingTest(null);
    conversationIdRef.current = newConversationId(); // telemetride yeni sohbet grubu
    setResolvedContextNow((prev) => ({ ...prev, currentGainIndex: 0 }));
    void sendMessage("", { contextOverride: { currentGainIndex: 0 }, afterReply: scheduleGainStartTest });
  };

  return (
    <div className={`fixed right-0 bottom-0 ${shellPositionClass} bg-white border-t border-gray-200 shadow-2xl flex`}>
      <section className={`${mobileView === "workspace" ? "flex" : "hidden"} md:flex flex-col flex-1 min-w-0 bg-white relative overflow-hidden`}>
        {/* Teste dönüş ŞERİT olarak akış içinde durur. Eskiden sağ üst köşede yüzen bir düğmeydi ve
            video/PDF başlığındaki "Videoyu izledim" düğmesinin üstüne binip basılamaz yapıyordu
            (stage sahası, 8 Eki). Kalınan testte de görünür: öğrenci videodan sonra "Tekrar çöz"e döner. */}
        {canReturnToTest && pendingTest && (
          <div className="shrink-0 flex items-center justify-between gap-3 border-b border-purple-100 bg-purple-50 px-4 py-2">
            <span className="min-w-0 truncate text-xs font-semibold text-purple-900">
              {pendingTestDraft?.isReviewMode
                ? "Testin seni bekliyor: yanlışlarını tekrar çözebilirsin."
                : `Yarım kalan testin var (${Object.keys(pendingTestDraft?.selectedAnswers || {}).length} cevap saklandı).`}
            </span>
            <button
              type="button"
              onClick={() => {
                setActiveMaterial(pendingTest);
                setMobileView("workspace");
              }}
              className="shrink-0 rounded-full bg-purple-600 px-4 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-purple-700 active:scale-95"
            >
              Teste dön
            </button>
          </div>
        )}
        <div className="relative min-h-0 flex-1">
        <Workspace
          material={activeMaterial}
          context={resolvedContext}
          onPracticeTestComplete={handlePracticeTestComplete}
          onMaterialAction={sendHiddenMessage}
          onPdfPageChange={(page) => { activePdfPageRef.current = page; }}
          practiceDrafts={practiceTestDraftsRef.current}
          onPracticeDraftChange={handlePracticeDraftChange}
          passedTestGainIds={passedTestGainIds}
          revealAnswersFor={(gid: string) => passedTestGainIds.includes(gid) || (failedAttemptsByGainRef.current[gid] || 0) >= 2}
          passedNextAction={passedNextAction}
          reviewFor={(gid: string) => {
            const review = failedReviewRef.current;
            if (!reviewState || reviewState.gainId !== gid || !review) return null;
            const numbers = review.questions.map((q) => q.number);
            return {
              currentNumber: reviewState.currentNumber,
              numbers,
              discussed: reviewState.discussed,
              frontier: reviewFrontier({ questions: review.questions, discussed: reviewState.discussed }),
              canConfirm: !isLoading && !isTypingMessage && !isPreparingSpeech && !isSpeaking && !pendingHiddenMessagesRef.current.length,
              allDone: numbers.every((n) => reviewState.discussed.includes(n)),
              onFocus: focusReviewQuestion,
              onConfirm: confirmReviewQuestion,
              onStartRetest: () => startRetest(gid),
            };
          }}
          // Telefonda sohbete dönüş düğmesi AKIŞ İÇİNDE verilir (yüzen değil):
          // yüzerken testin gezinme satırının ve şıkların üstüne biniyordu.
          mobileBackToChat={
            <button
              type="button"
              onClick={() => setMobileView("chat")}
              className="rounded-full bg-orange-500 px-4 py-2.5 text-xs font-bold text-white shadow active:scale-95"
            >
              Sohbete Dön
            </button>
          }
        />
        </div>

        {/* Çizim katmanı materyalin ÜSTÜNE biner ve zemini şeffaftır: öğrenci PDF'in
            veya pekiştirme testinin üzerine işlem yapar. "İçeriğe dokun" moduna
            geçince tıklamalar alttaki materyale iner (kaydırma, şık işaretleme). */}
        {showDrawingBoard && (
          <DrawingBoard
            onClose={() => {
              setShowDrawingBoard(false);
              // Dar ekranda katman kapanınca sohbete dön: materyal yoksa "Sohbete Dön"
              // düğmesi çıkmıyor ve öğrenci boş çalışma alanında kalırdı.
              setMobileView("chat");
            }}
            className="absolute inset-0 z-40"
          />
        )}

      </section>

      <aside className={`${mobileView === "chat" ? "flex" : "hidden"} md:flex w-full md:w-[380px] md:max-w-[42vw] md:min-w-[340px] border-l border-gray-200 bg-gray-50 flex-col`}>
        <div className="px-4 py-3 bg-[#240d07] text-white flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center flex-shrink-0">
              <Bot className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-sm">AI Öğretmen Destek</div>
              <div className="text-[11px] text-white/70 truncate">
                {context.topicTitle || context.contentTitle || context.gainText || "Aktif konu"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Konuşmayı sıfırla — handleResetConversation bugüne kadar hiçbir düğmeye bağlı
                değildi (ölü kod) ama hata mesajları kullanıcıya "Sıfırla"yı öneriyordu.
                Yalnız oturum kabuğu varken görünür (kabuksuz sohbette sıfırlanacak sunucu
                durumu yok; handleReset zaten erken dönüyordu). */}
            {Boolean(context?.sessionPlanId || resolvedContext?.sessionPlanId) && (
              <button
                onClick={handleResetConversation}
                disabled={isLoading || isTypingMessage}
                className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title="Konuşmayı sıfırla — baştan başla"
                aria-label="Konuşmayı sıfırla"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setIsExpanded((prev) => !prev)}
              className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              title={isExpanded ? "Tam ekrandan çık (Esc)" : "Tam ekran — menüyü de kapla"}
              aria-label={isExpanded ? "Tam ekrandan çık" : "Tam ekran"}
              aria-pressed={isExpanded}
            >
              {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={() => {
                const opening = !showDrawingBoard;
                setShowDrawingBoard(opening);
                // Dar ekranda tahta çalışma alanında açılır; sohbetteyken görülmezdi.
                if (opening) setMobileView("workspace");
              }}
              className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${showDrawingBoard ? "bg-orange-500 text-white" : "bg-white/10 hover:bg-white/20 text-white"
                }`}
              title={showDrawingBoard ? "Karalama tahtasını kapat" : "Karalama tahtası — elle işlem yap"}
              aria-label="Karalama tahtası"
              aria-pressed={showDrawingBoard}
            >
              <PenLine className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                const nextVoiceEnabled = !voiceEnabled;
                voiceEnabledRef.current = nextVoiceEnabled;
                setVoiceEnabled(nextVoiceEnabled);
                setSpeechNeedsGesture(false);
                if (!nextVoiceEnabled) {
                  setIsSpeaking(false);
                  setIsPreparingSpeech(false);
                  stopCurrentAudio();
                  // Voice kapanınca Live bağlantıyı da kapat
                  closeLiveConnection();
                } else {
                  // Voice tekrar açılınca AudioContext'i şimdiki gesture ile başlat
                  // (sonraki AI mesajının sesi çalabilsin diye)
                  try {
                    getStreamAudioContext(24000);
                  } catch {}
                }
              }}
              className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${voiceEnabled
                ? "text-white"
                : "text-white/70"
                }`}
              title={voiceEnabled ? "Sesli okumayı kapat" : "Sesli okumayı aç"}
              aria-label={voiceEnabled ? "Sesli okumayı kapat" : "Sesli okumayı aç"}
            >
              {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center"
              aria-label="AI Öğretmen Destek kapat"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {messages.map((message, index) => {
            const isEmptyAssistant = message.role === "assistant" && !message.content.trim();
            if (isEmptyAssistant) {
              return (
                <ShinyStatusBubble
                  key={message.id || `${message.role}-${index}`}
                  text={isPreparingSpeech ? "Hazırlanıyor..." : "Düşünüyor..."}
                />
              );
            }

            if (message.notice) {
              return (
                <div key={message.id || `notice-${index}`} className="flex justify-center">
                  <div className="max-w-[92%] rounded-lg bg-gray-100 px-3 py-1.5 text-center text-xs leading-snug text-gray-600">
                    {message.content}
                  </div>
                </div>
              );
            }

            return (
              <div key={message.id || `${message.role}-${index}`} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[88%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${message.role === "user"
                    ? "bg-orange-500 text-white rounded-tr-md"
                    : "bg-white text-gray-800 border border-gray-200 rounded-tl-md"
                    }`}
                >
                  <span className="whitespace-pre-wrap">{message.content}</span>
                  {message.role === "assistant" && isTypingMessage && index === messages.length - 1 && (
                    <span className="ml-0.5 inline-block h-4 w-0.5 translate-y-0.5 animate-pulse rounded-full bg-orange-400" />
                  )}
                </div>
              </div>
            );
          })}

          {isLoading && !isTypingMessage && !isPreparingSpeech && !messages.some((message) => message.role === "assistant" && !message.content.trim()) && (
            <ShinyStatusBubble text="Düşünüyor..." />
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="p-4 border-t border-gray-200 bg-white">
          {/* Kazanım kartı yoksa materyale geçiş düğmesi burada durur (kart varsa kartın
              sağ alt köşesinde). Her iki durumda da AKIŞ İÇİNDE — yüzerken gönder
              tuşunun üstüne biniyordu. */}
          {showMaterialButton && !hasTopicGainPlan && <div className="md:hidden mb-2 flex justify-end">{showMaterialButton}</div>}
          {hasTopicGainPlan && (
            <div className="mb-2 rounded-[22px] border border-orange-100 bg-orange-50 px-3 py-2 shadow-sm">
              <div className="flex items-start gap-2">
                <button
                  type="button"
                  onClick={() => setIsGainCardExpanded((prev) => !prev)}
                  className="flex min-w-0 flex-1 items-start justify-between gap-2 rounded-2xl text-left transition"
                >
                  <div className="min-w-0 flex-1">
                    {/* Kazanım metni KAPALIYKEN yazılmaz: aynı yazı zaten sohbetin
                        içinde ve test başlığında geçiyordu; telefonda 4-5 satır ölü alan
                        demekti. Merak eden karta dokunup listeyi açar. */}
                    <div className="text-sm font-extrabold leading-5 text-orange-700">
                      {currentGainIndex + 1}/{topicGainPlan.length} {["deficiency", "topicDeficiency"].includes(String(resolvedContext.source)) ? "Eksik Kazanım" : "Kazanım"}
                      {resolvedGainIds.length > 0 && (
                        <span className="ml-1.5 font-semibold text-emerald-600">· {resolvedGainIds.length} giderildi</span>
                      )}
                    </div>
                  </div>
                  <span className="mt-0.5 rounded-full p-1 text-gray-400">
                    {isGainCardExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </span>
                </button>
                {canAdvanceByButton && (
                  <button
                    type="button"
                    onClick={() => goToNextGainByButton()}
                    disabled={isLoading}
                    className="inline-flex shrink-0 items-center gap-0.5 rounded-full border border-orange-200 bg-white px-2.5 py-1 text-xs font-bold text-orange-700 shadow-sm transition hover:bg-orange-100 disabled:opacity-50"
                  >
                    Sıradaki kazanım
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                )}
                {canFinishByButton && (
                  <button
                    type="button"
                    onClick={() => finishTopicByButton()}
                    disabled={isLoading}
                    className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-xs font-bold text-emerald-700 shadow-sm transition hover:bg-emerald-50 disabled:opacity-50"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Konuyu bitir
                  </button>
                )}
                {topicFinished && (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {leftOpenGainIds.includes(currentPlanGainId) ? "Konu bitirildi" : "Konu tamamlandı"}
                  </span>
                )}
              </div>
              {confirmSkipGain && (canAdvanceByButton || canFinishByButton) && (
                <div className="mt-2 rounded-xl border border-orange-200 bg-white px-3 py-2 text-xs leading-5 text-gray-700">
                  <p>
                    {canAdvanceByButton ? (
                      <>
                        Bu kazanımın testini çözmeden geçersen eksik <strong>açık kalır</strong>. Testi geçersen eksik kapanır ve
                        sıradakine kendiliğinden geçersin.
                      </>
                    ) : (
                      <>
                        Bu son kazanımın testini çözmeden bitirirsen eksik <strong>açık kalır</strong>. Testi geçersen eksik kapanır
                        ve konu tamamlanır.
                      </>
                    )}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={openTestForCurrentGain}
                      className="rounded-lg bg-orange-500 px-3 py-1.5 font-semibold text-white transition hover:bg-orange-600"
                    >
                      Testi aç
                    </button>
                    <button
                      type="button"
                      onClick={() => (canAdvanceByButton ? goToNextGainByButton(true) : finishTopicByButton(true))}
                      className="rounded-lg border border-gray-200 px-3 py-1.5 font-semibold text-gray-700 transition hover:bg-gray-50"
                    >
                      {canAdvanceByButton ? "Yine de geç" : "Yine de bitir"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmSkipGain(false)}
                      className="px-2 py-1.5 text-gray-500 transition hover:text-gray-700"
                    >
                      Vazgeç
                    </button>
                  </div>
                </div>
              )}
              {isGainCardExpanded && (
                <ul className="mt-2 space-y-1">
                  {topicGainPlan.map((gain: any, index: number) => {
                    const isActive = index === currentGainIndex;
                    const isResolvedGain = resolvedGainIds.includes(String(gain.gainId || ""));
                    const isLeftOpenGain = !isResolvedGain && leftOpenGainIds.includes(String(gain.gainId || ""));
                    return (
                      <li
                        key={gain.gainId || gain.userGainId || index}
                        className={`flex items-start gap-2 rounded-xl px-2 py-1.5 text-xs leading-5 ${
                          isActive
                            ? "bg-orange-100 text-orange-950 font-semibold"
                            : "text-orange-900/70"
                        }`}
                      >
                        <span className={`shrink-0 ${isActive ? "font-extrabold text-orange-700" : "text-orange-700/60"}`}>
                          {index + 1}/{topicGainPlan.length}
                        </span>
                        <span className="min-w-0 flex-1 break-words">{gain.gainText || "Kazanım"}</span>
                        {isResolvedGain && <span className="shrink-0 font-bold text-emerald-600">✓ giderildi</span>}
                        {isLeftOpenGain && <span className="shrink-0 font-semibold text-amber-600">açık</span>}
                      </li>
                    );
                  })}
                </ul>
              )}
              {showMaterialButton && <div className="md:hidden mt-2 flex justify-end">{showMaterialButton}</div>}
            </div>
          )}
          {speechError && (
            <div className="mb-2 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
              {speechError}
            </div>
          )}
          {speechNeedsGesture && lastAssistantMessage && (
            <button
              onClick={replayLastAssistantMessage}
              className="mb-2 w-full rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-xs font-semibold text-orange-700 flex items-center justify-center gap-2 hover:bg-orange-100"
            >
              <Volume2 className="w-3.5 h-3.5" />
              Sesi başlat
            </button>
          )}
          <div
            className={`relative flex items-center overflow-hidden transition-[gap,padding] duration-300 ease-out ${
              voiceBubbleExpanded ? "gap-0 pr-0" : "gap-3 pr-[60px]"
            }`}
          >
            <VoiceBubbleControl
              expanded={voiceBubbleExpanded}
              animating={voiceBubbleAnimating}
              disabled={recordingDisabled}
              level={voiceLevel}
              onClick={handleVoiceBubbleClick}
              onCancel={cancelVoiceConversation}
            />
            <input
              ref={chatInputRef}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey && input.trim() && !inputBusy && !voiceBubbleExpanded) {
                  event.preventDefault();
                  void sendMessage(input);
                }
              }}
              disabled={inputBusy || voiceBubbleExpanded}
              tabIndex={voiceBubbleExpanded ? -1 : 0}
              placeholder="Konuş ya da yaz..."
              className={`min-w-0 h-12 rounded-2xl border border-gray-200 text-sm outline-none transition-all duration-300 ease-out focus:border-orange-400 disabled:bg-gray-50 disabled:text-gray-400 ${
                voiceBubbleExpanded
                  ? "pointer-events-none w-0 flex-none border-transparent px-0 opacity-0"
                  : "flex-1 px-4 opacity-100"
              }`}
            />
            <button
              onClick={() => void sendMessage(input)}
              disabled={!input.trim() || inputBusy || voiceBubbleExpanded}
              tabIndex={voiceBubbleExpanded ? -1 : 0}
              className={`absolute right-0 top-0 h-12 w-12 rounded-2xl bg-orange-500 text-white flex items-center justify-center transition-all duration-300 ease-out disabled:opacity-50 hover:bg-orange-600 ${
                voiceBubbleExpanded
                  ? "pointer-events-none translate-x-[calc(100%+12px)] scale-95 opacity-0"
                  : "translate-x-0 scale-100 opacity-100"
              }`}
              title="Gönder"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
};

const Workspace = ({
  material,
  context,
  onPracticeTestComplete,
  onMaterialAction,
  onPdfPageChange,
  practiceDrafts,
  onPracticeDraftChange,
  mobileBackToChat,
  passedTestGainIds = [],
  revealAnswersFor,
  passedNextAction,
  reviewFor,
}: {
  material: ActiveMaterial;
  context: Record<string, any>;
  onPracticeTestComplete: (summary: PracticeTestResultSummary) => void;
  onMaterialAction: (message: string) => void;
  onPdfPageChange?: (page: number) => void;
  practiceDrafts: Record<string, PracticeTestDraft>;
  onPracticeDraftChange: (key: string, draft: PracticeTestDraft) => void;
  mobileBackToChat?: React.ReactNode;
  passedTestGainIds?: string[];
  revealAnswersFor?: (gainId: string) => boolean;
  passedNextAction?: { label: string; onClick: () => void };
  reviewFor?: (gainId: string) => PracticeReview | null;
}) => {
  // Testte düğme gezinme satırının içine (Önceki ile Sonraki arasına) girer; diğer
  // materyallerde altta kendi şeridinde durur. İkisi de akış içinde — içerik örtülmez.
  const withMobileBar = (content: React.ReactNode) => (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1">{content}</div>
      {mobileBackToChat && (
        <div
          className="md:hidden shrink-0 flex justify-center border-t border-slate-200 bg-white px-3 py-2"
          style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}>
          {mobileBackToChat}
        </div>
      )}
    </div>
  );

  if (material.type === "pdf") {
    return withMobileBar(
      <PdfMaterialWorkspace material={material} context={context} onMaterialAction={onMaterialAction} onPageChange={onPdfPageChange} />
    );
  }

  if (material.type === "video") {
    const startSeconds = timecodeToSeconds(material.startTime);
    const endSeconds = timecodeToSeconds(material.endTime);
    const resolvedVideoUrl = resolveTutorMediaUrl(material.videoUrl);
    const vimeoId = getVimeoId(resolvedVideoUrl);
    const youtubeId = getYoutubeId(resolvedVideoUrl);

    return withMobileBar(
      <div className="h-full bg-white flex flex-col">
        <div className="h-14 px-5 border-b border-gray-200 flex items-center justify-between gap-3">
          <div className="min-w-0 flex items-center gap-2 text-sm font-semibold text-gray-900">
            <Video className="w-4 h-4 text-purple-600 flex-shrink-0" />
            <span className="truncate">{material.title}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-100 rounded-full px-3 py-1 whitespace-nowrap">
              {material.startTime || "00:00:00"} - {material.endTime || "00:00:00"}
            </div>
            <button
              type="button"
              onClick={() => onMaterialAction("Videoyu izledim. Aynı videoyu tekrar açma; bu aralıkta anlatılanları eksiğimle ilişkilendir ve sıradaki uygun adımı öner.")}
              className="rounded-full bg-purple-600 px-3 py-1 text-xs font-bold text-white transition hover:bg-purple-700"
            >
              Videoyu izledim
            </button>
          </div>
        </div>

        <div className="bg-black">
          <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
            {youtubeId ? (
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1${startSeconds ? `&start=${startSeconds}` : ""}${endSeconds > startSeconds ? `&end=${endSeconds}` : ""}`}
                className="absolute inset-0 h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={`ai-tutor-video-${youtubeId}`}
              />
            ) : vimeoId ? (
              <iframe
                src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1${startSeconds ? `#t=${startSeconds}s` : ""}`}
                className="absolute inset-0 h-full w-full"
                allow="autoplay; fullscreen"
                title={`ai-tutor-video-${vimeoId}`}
              />
            ) : resolvedVideoUrl ? (
              <TutorDirectVideoPlayer src={resolvedVideoUrl} startSeconds={startSeconds} />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-sm font-medium text-white">
                Video URL bilgisi bulunamadı.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (material.type === "simulation") {
    return withMobileBar(
      <div className="h-full bg-white flex items-center justify-center p-8">
        <div className="w-full max-w-3xl rounded-2xl border border-orange-200 bg-orange-50 p-8">
          <div className="flex items-center gap-3 text-lg font-bold text-orange-900 mb-6">
            <Shapes className="w-6 h-6" />
            Simülasyon Çalışma Alanı
          </div>
          <div className="grid grid-cols-3 gap-4">
            {["Gözlemle", "Değiştir", "Sonuç çıkar"].map((item, index) => (
              <div key={item} className="rounded-xl bg-white border border-orange-100 p-5 text-center">
                <div className="w-9 h-9 rounded-full bg-orange-500 text-white flex items-center justify-center mx-auto mb-3 font-bold">
                  {index + 1}
                </div>
                <div className="font-semibold text-orange-900">{item}</div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm leading-7 text-orange-800">
            Konu kimliği: {material.topicId || context.topicId || "aktif konu"}. AI öğretmenin sağdaki yönergelerine göre simülasyonu yorumla.
          </p>
        </div>
      </div>
    );
  }

  if (material.type === "test-loading") {
    return withMobileBar(
      <div className="h-full bg-white flex items-center justify-center p-8">
        <div className="rounded-2xl border border-orange-100 bg-orange-50 px-5 py-4 text-orange-800 flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin" />
          Pekiştirme testi yükleniyor...
        </div>
      </div>
    );
  }

  if (material.type === "test-error") {
    return withMobileBar(<BlankWorkspace title="Test bulunamadı" text={material.message} />);
  }

  if (material.type === "test") {
    const testKey = buildPracticeTestKey(material);
    // key: aynı kazanıma YENİ set gelirse bileşen sıfırdan kurulsun (taslak karışmasın)
    // Sohbete dönüş burada ALT ŞERİDE değil, testin kendi Önceki/Sonraki satırına girer.
    return (
      <PracticeTestWorkspace
        key={testKey}
        material={material}
        context={context}
        onComplete={onPracticeTestComplete}
        draft={practiceDrafts[testKey] || null}
        onDraftChange={(draft) => onPracticeDraftChange(testKey, draft)}
        mobileBackToChat={mobileBackToChat}
        // Önceden geçilmiş kazanımın testi yeniden açılıp kalınırsa inceleme geçerlidir, "geçtin" ekranı değil
        passed={passedTestGainIds.includes(String(material.gainId || "")) && !reviewFor?.(String(material.gainId || ""))}
        revealAnswers={revealAnswersFor ? revealAnswersFor(String(material.gainId || "")) : true}
        nextAction={passedNextAction}
        review={reviewFor ? reviewFor(String(material.gainId || "")) : null}
      />
    );
  }

  return withMobileBar(
    <BlankWorkspace
      title={context.topicTitle || context.contentTitle || context.gainText || "AI Öğretmen Çalışma Alanı"}
      text="AI öğretmen konuşma sırasında gerektiğinde burada PDF, simülasyon veya test çalışma alanını açacak."
    />
  );
};

const normalizeQuestionOptions = (options: TutorQuestion["options"]) => {
  if (Array.isArray(options)) {
    return options.map((option, index) => {
      const label = String.fromCharCode(65 + index);
      if (typeof option === "string") return { label, text: option };
      return { label: option.label || label, text: option.text || option.value || "" };
    }).filter((option) => option.text);
  }

  return Object.entries(options || {}).map(([label, text]) => ({
    label,
    text: String(text || ""),
  })).filter((option) => option.text);
};

const getActivePracticeGainText = (context: Record<string, any>) => {
  const gainPlan = Array.isArray(context.relatedMaterials?.gainPlan) ? context.relatedMaterials.gainPlan : [];
  const activeIndex = gainPlan.length
    ? Math.min(Math.max(0, Number(context.currentGainIndex) || 0), gainPlan.length - 1)
    : 0;
  const activeGainText = gainPlan[activeIndex]?.gainText || context.gainText || context.topicTitle || context.contentTitle || "Aktif kazanım";
  return String(activeGainText).split("|")[0].replace(/\s+/g, " ").trim();
};

// Çözümlü video gömme — Vimeo / YouTube / mp4 (soru solutionVideoUrl'i).
const SolutionVideoEmbed = ({ url }: { url: string }) => {
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (vimeo) {
    return (
      <div className="relative w-full overflow-hidden rounded-xl bg-black" style={{ paddingTop: "56.25%" }}>
        <iframe className="absolute inset-0 h-full w-full" src={`https://player.vimeo.com/video/${vimeo[1]}`} title="Çözüm videosu" frameBorder={0} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
      </div>
    );
  }
  if (yt) {
    return (
      <div className="relative w-full overflow-hidden rounded-xl bg-black" style={{ paddingTop: "56.25%" }}>
        <iframe className="absolute inset-0 h-full w-full" src={`https://www.youtube-nocookie.com/embed/${yt[1]}?rel=0`} title="Çözüm videosu" frameBorder={0} allow="accelerometer; autoplay; encrypted-media; picture-in-picture" allowFullScreen />
      </div>
    );
  }
  return <video src={url} controls className="w-full rounded-xl bg-black" />;
};

const PracticeTestWorkspace = ({
  material,
  context,
  onComplete,
  draft,
  onDraftChange,
  mobileBackToChat,
  passed = false,
  revealAnswers = true,
  nextAction,
  review = null,
}: {
  material: Extract<ActiveMaterial, { type: "test" }>;
  context: Record<string, any>;
  onComplete: (summary: PracticeTestResultSummary) => void;
  draft: PracticeTestDraft | null;
  onDraftChange: (draft: PracticeTestDraft) => void;
  mobileBackToChat?: React.ReactNode;
  // Sunucu testi GEÇTİ saydı (eşik aşıldı, eksik kapandı) — tekrar çözme anlamsız
  passed?: boolean;
  // Kalınan İLK denemede doğru şık ve çözüm videosu gösterilmez (yoksa "Tekrar çöz" ezberle geçilirdi)
  revealAnswers?: boolean;
  nextAction?: { label: string; onClick: () => void };
  // Kalınan testte SORU SORU inceleme: ekrandaki yanlış AI'yla konuşulur, "anladım"la geçilir (eski "Tekrar çöz" yerine, 20 Eki)
  review?: PracticeReview | null;
}) => {
  // Başlangıç değerleri TASLAKTAN: test açıkken PDF/video açılırsa bileşen unmount olur,
  // geri dönüldüğünde cevaplar ve kalınan soru buradan geri yüklenir.
  const [currentIndex, setCurrentIndex] = useState(draft?.currentIndex ?? 0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string>>(draft?.selectedAnswers ?? {});
  const [isCompleted, setIsCompleted] = useState(draft?.isCompleted ?? false);
  const [isReviewMode, setIsReviewMode] = useState(draft?.isReviewMode ?? false);
  const [solutionUrl, setSolutionUrl] = useState<string | null>(null);

  // Her değişiklikte taslağı dışarı yaz — bileşen unmount olsa da cevaplar yaşamaya devam eder.
  useEffect(() => {
    onDraftChange({
      selectedAnswers,
      currentIndex,
      isCompleted,
      isReviewMode,
    });
    // Bağımlılıkta onDraftChange YOK: her render'da yeni inline closure geldiği için
    // eklenirse effect sonsuz döner. Effect çalıştığında zaten en güncel closure kullanılır.
  }, [selectedAnswers, currentIndex, isCompleted, isReviewMode]);

  const questions = material.questions;
  // İncelemede ekrandaki soruyu ana bileşen yönetir (AI hangi yanlışı konuştuğunu bilsin)
  useEffect(() => {
    if (isReviewMode && review?.currentNumber && currentIndex !== review.currentNumber - 1) {
      setCurrentIndex(review.currentNumber - 1);
      setSolutionUrl(null); // önceki sorunun çözüm videosu sıradaki sorunun altında açık kalmasın
    }
    // currentIndex bilerek bağımlılıkta değil: yalnız ana bileşen soruyu değiştirince eşitlenir
  }, [review?.currentNumber, isReviewMode]);
  const question = questions[currentIndex];
  const options = normalizeQuestionOptions(question?.options);
  const questionKey = String(question?.id ?? currentIndex);
  const selectedAnswer = selectedAnswers[questionKey];
  const isLastQuestion = currentIndex >= questions.length - 1;
  const answeredCount = questions.filter((item, index) => selectedAnswers[String(item?.id ?? index)]).length;
  const isCorrectAnswer = (item: TutorQuestion, index: number) => {
    const answer = selectedAnswers[String(item?.id ?? index)];
    return Boolean(answer && item.correctAnswer && answer === item.correctAnswer);
  };
  const correctCount = questions.filter((item, index) => {
    return isCorrectAnswer(item, index);
  }).length;
  const wrongQuestions = questions
    .map((item, index) => ({
      item,
      index,
      selectedAnswer: selectedAnswers[String(item?.id ?? index)],
    }))
    .filter(({ item, selectedAnswer }) => selectedAnswer && item.correctAnswer && selectedAnswer !== item.correctAnswer)
    .map(({ item, index, selectedAnswer }) => {
      const itemOptions = normalizeQuestionOptions(item.options);
      const selectedOption = itemOptions.find((option) => option.label === selectedAnswer);
      const correctOption = itemOptions.find((option) => option.label === item.correctAnswer);

      return {
        index: index + 1,
        text: item.text || "Soru metni yok",
        selectedAnswer,
        selectedAnswerText: selectedOption?.text,
        correctAnswer: item.correctAnswer,
        correctAnswerText: correctOption?.text,
      };
    });
  const wrongCount = wrongQuestions.length;
  const blankCount = questions.length - answeredCount;
  // İnceleme/tekrar kümesi YANLIŞ + BOŞ soruları kapsar (yalnız-boş bırakılan test,
  // içinden çıkılamayan bir ekrana dönüşüyordu: review boş, retry butonu yok).
  const reviewQuestionIndexes = questions
    .map((item, index) => ({ item, index, selected: selectedAnswers[String(item?.id ?? index)] }))
    .filter(({ item, selected }) => item.correctAnswer && (!selected || selected !== item.correctAnswer))
    .map(({ index }) => index);
  const currentReviewPosition = reviewQuestionIndexes.indexOf(currentIndex);
  const isReviewFirstQuestion = isReviewMode && currentReviewPosition <= 0;
  const isReviewLastQuestion = isReviewMode && currentReviewPosition >= reviewQuestionIndexes.length - 1;
  // Kalınan testte sıradaki yanlış "anladım" denene kadar kilitli; konuşulmuşlar arasında serbest
  const isReviewLocked = (number: number) => Boolean(review && !review.discussed.includes(number) && number !== review.frontier);
  const isNextWrongLocked = isReviewMode && !isReviewLastQuestion && isReviewLocked(reviewQuestionIndexes[currentReviewPosition + 1] + 1);
  const activeGainText = getActivePracticeGainText(context);
  const questionText = String(question?.text || "").replace(/\s+/g, " ").trim();
  const questionMediaUrl = question?.mediaUrl ? resolveTutorMediaUrl(question.mediaUrl) : "";
  const hasQuestionBody = Boolean(questionText || questionMediaUrl);

  const completeTest = () => {
    onComplete({
      total: questions.length,
      answered: answeredCount,
      correct: correctCount,
      wrong: wrongCount,
      blank: blankCount,
      wrongQuestions,
      answers: material.questions.map((item, index) => ({
        questionId: item?.id ?? index,
        selected: selectedAnswers[String(item?.id ?? index)] || null,
      })),
    });

    if (reviewQuestionIndexes.length) {
      setIsReviewMode(true);
      setIsCompleted(false);
      setCurrentIndex(reviewQuestionIndexes[0]);
      return;
    }

    setIsCompleted(true);
  };

  if (isCompleted) {
    return (
      <div className="h-full bg-white flex items-center justify-center p-8">
        <div className="w-full max-w-2xl rounded-2xl border border-green-200 bg-green-50 p-8 text-center">
          <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-green-600" />
          <h2 className="text-2xl font-bold text-green-950">Test Tamamlandı</h2>
          <p className="mt-3 text-lg font-semibold">
            <span className="text-green-600">{correctCount} doğru</span>
            <span className="text-green-900">, </span>
            <span className="text-red-600">{wrongCount} yanlış</span>
            <span className="text-green-900">, </span>
            <span className="text-gray-500">{blankCount} boş</span>
          </p>
          <button
            onClick={() => {
              setIsCompleted(false);
              setIsReviewMode(true);
              setCurrentIndex(reviewQuestionIndexes[0] ?? 0);
            }}
            className="mt-6 rounded-xl bg-orange-500 px-5 py-2 text-sm font-semibold text-white hover:bg-orange-600"
          >
            Soruları Gözden Geçir
          </button>
          {passed && nextAction && (
            <button
              onClick={nextAction.onClick}
              className="mt-3 block w-full rounded-xl border border-emerald-200 bg-white px-5 py-2 text-sm font-bold text-emerald-700 hover:bg-emerald-50"
            >
              {nextAction.label}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-[#f8fafc] flex flex-col">
      {/* Başlık telefonda tek satır: kazanım metni burada da tekrarlanınca (sohbetteki
          kazanım kartıyla aynı yazı) ekranın üstünü ölü alana çeviriyordu — yalnız
          geniş ekranda gösterilir. Sayaç shrink-0 + whitespace-nowrap olmalı, yoksa
          dar ekranda "1 / 10" üç satıra bölünüp başlığı büyütüyor. */}
      <div className="px-4 py-2 sm:h-16 sm:px-6 sm:py-0 border-b border-gray-200 bg-white flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-blue-950 font-bold">
            <CheckCircle2 className="w-5 h-5 text-blue-600 shrink-0" />
            Pekiştirme Testi
          </div>
          <div className="mt-0.5 hidden truncate text-xs font-medium text-gray-500 sm:block">
            {activeGainText}
          </div>
        </div>
        <div className="shrink-0 whitespace-nowrap rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-sm font-bold text-blue-700">
          {currentIndex + 1} / {questions.length}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="mx-auto max-w-4xl space-y-4">
          {/* Yalnız inceleme modunda: renk kodunu (kırmızı=senin, yeşil=doğru) açıklar,
              yani bilgi taşır. Normal moddaki genel öğüt ("önce hangi kavram ölçülüyor")
              her soruda tekrarlanıyor, hiçbir şey öğretmiyor ve telefonda sorunun
              yerini yiyordu — kaldırıldı. */}
          {isReviewMode && passed && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-900 shadow-sm">
              <span className="font-semibold">Testi geçtin, bu eksik kapandı ✓</span>{" "}
              Yanlış yaptığın soruyu burada inceleyebilirsin: kırmızı senin cevabın, yeşil doğrusu.
            </div>
          )}
          {isReviewMode && !passed && revealAnswers && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900 shadow-sm">
              <span className="font-semibold">Yanlış yaptığın soru açık.</span>{" "}
              Kırmızı seçtiğin cevap, yeşil doğru cevap. Sonucu AI Öğretmen sağ tarafta açıklıyor.
            </div>
          )}
          {isReviewMode && !passed && !revealAnswers && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900 shadow-sm">
              <span className="font-semibold">Kırmızılar yanlış cevapların.</span>{" "}
              Doğrusunu şimdilik göstermiyoruz: AI Öğretmen sağda yanlışlarını tek tek seninle konuşacak; takılırsan konu
              videosunu ya da föy sayfasını açabilir. Konu oturunca test yeniden başlar.
            </div>
          )}

          <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-5 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-extrabold text-orange-700">
                Soru {currentIndex + 1}
              </span>
              <span className="min-w-0 max-w-full truncate rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
                {activeGainText}
              </span>
            </div>

            {questionMediaUrl && (
              <div className="mb-5 flex justify-center overflow-hidden rounded-2xl border border-gray-200 bg-white px-4 py-4">
                <img src={questionMediaUrl} alt="Soru görseli" className="max-h-[420px] max-w-full object-contain" />
              </div>
            )}

            {/* Soru GÖRSELİ varsa metin (OCR) GÖSTERİLMEZ — resim tek kaynaktır; OCR yalnız AI içindir. */}
            {!questionMediaUrl && (
              <div className={`rounded-2xl px-4 py-4 ${hasQuestionBody ? "bg-white" : "border border-amber-200 bg-amber-50"}`}>
                {questionText ? (
                  <div className="whitespace-pre-wrap text-lg font-semibold leading-8 text-gray-950">
                    {questionText}
                  </div>
                ) : (
                  <div className="text-sm font-semibold leading-6 text-amber-900">
                    Bu sorunun metni sistemden eksik geldi. Bu soru yerine metni veya görseli olan bir pekiştirme sorusu gösterilmeli.
                  </div>
                )}
              </div>
            )}

            {/* Görsel soruda şıklar sadece etiket → YAN YANA (dikey alan tasarrufu); metinli soruda alt alta. */}
            <div className={questionMediaUrl ? "mt-6 grid grid-cols-5 gap-2" : "mt-6 grid gap-3"}>
              {options.map((option) => {
                const isSelected = selectedAnswer === option.label;
                const isCorrect = isReviewMode && revealAnswers && question?.correctAnswer === option.label;
                const isWrongSelected = isReviewMode && isSelected && !isCorrect;
                return (
                  <button
                    key={option.label}
                    onClick={() => {
                      if (isReviewMode) return;
                      setSelectedAnswers((prev) => ({ ...prev, [questionKey]: option.label }));
                    }}
                    disabled={!hasQuestionBody && !isReviewMode}
                    className={`flex rounded-2xl border transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${questionMediaUrl ? "items-center justify-center px-2 py-3.5" : "items-start gap-3 px-4 py-3.5 text-left"} ${isCorrect
                      ? "border-emerald-400 bg-emerald-50 text-emerald-900"
                      : isWrongSelected
                        ? "border-red-400 bg-red-50 text-red-900"
                        : isSelected
                          ? "border-orange-400 bg-orange-50 text-orange-900"
                          : "border-gray-200 bg-white text-gray-800 hover:border-orange-200 hover:bg-orange-50/50"
                      }`}
                  >
                    <span className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl text-sm font-bold ${isCorrect
                      ? "bg-emerald-600 text-white"
                      : isWrongSelected
                        ? "bg-red-600 text-white"
                        : isSelected
                          ? "bg-orange-500 text-white"
                          : "bg-gray-100 text-gray-600"
                      }`}>
                      {option.label}
                    </span>
                    {/* Görsel soruda şıklar resmin içinde → yalnız etiket (A-E); OCR şık metni gösterilmez. */}
                    {!questionMediaUrl && <span className="pt-1 leading-6">{option.text}</span>}
                  </button>
                );
              })}
            </div>

            {/* Çözümlü video — yalnız inceleme modunda, videosu olan soruda (konu anlatımından sonra öğrenci açar) */}
            {isReviewMode && revealAnswers && question?.solutionVideoUrl && (
              <div className="mt-5 border-t border-gray-100 pt-4">
                {solutionUrl ? (
                  <div>
                    <SolutionVideoEmbed url={solutionUrl} />
                    <button
                      onClick={() => setSolutionUrl(null)}
                      className="mt-2 text-xs font-medium text-gray-500 hover:text-gray-700"
                    >
                      Videoyu kapat
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setSolutionUrl(resolveTutorMediaUrl(question.solutionVideoUrl as string))}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-violet-200 bg-violet-50 px-4 py-2 text-sm font-semibold text-violet-700 transition-colors hover:bg-violet-100"
                  >
                    🎬 Bu sorunun çözümlü videosunu izle
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <button
              onClick={() => {
                if (isReviewMode) {
                  const previousWrongIndex = reviewQuestionIndexes[Math.max(0, currentReviewPosition - 1)];
                  if (previousWrongIndex !== undefined) {
                    if (review) review.onFocus(previousWrongIndex + 1);
                    else setCurrentIndex(previousWrongIndex);
                  }
                  setSolutionUrl(null);
                  return;
                }

                setCurrentIndex((index) => Math.max(0, index - 1));
              }}
              disabled={isReviewMode ? isReviewFirstQuestion : currentIndex === 0}
              className="rounded-2xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:opacity-40"
            >
              {isReviewMode ? "Önceki Yanlış" : "Önceki"}
            </button>

            {/* Sohbete dönüş telefonda BURAYA girer: yüzerken şıkların ve Sonraki
                tuşunun üstüne biniyordu. Satır justify-between, ortası zaten boş. */}
            {mobileBackToChat && <div className="md:hidden">{mobileBackToChat}</div>}

            <button
              onClick={() => {
                if (isReviewMode) {
                  const nextWrongIndex = reviewQuestionIndexes[Math.min(reviewQuestionIndexes.length - 1, currentReviewPosition + 1)];
                  if (nextWrongIndex !== undefined) {
                    if (review) review.onFocus(nextWrongIndex + 1);
                    else setCurrentIndex(nextWrongIndex);
                  }
                  setSolutionUrl(null);
                  return;
                }
                if (isLastQuestion) {
                  completeTest();
                  return;
                }
                setCurrentIndex((index) => Math.min(questions.length - 1, index + 1));
              }}
              disabled={isReviewLastQuestion || isNextWrongLocked || (!isReviewMode && !hasQuestionBody)}
              className="rounded-2xl bg-orange-500 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-orange-600 disabled:opacity-40"
            >
              {isReviewMode ? "Sonraki Yanlış" : isLastQuestion ? "Bitir" : "Sonraki"}
            </button>
          </div>

          {/* Geçilen testte tekrar çözme yok: eksik kapandı, plan ilerledi — inceleme bitince sohbete dönülür */}
          {isReviewMode && passed && nextAction && (
            <button
              onClick={nextAction.onClick}
              className="w-full rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700 shadow-sm transition hover:bg-emerald-100"
            >
              {nextAction.label}
            </button>
          )}
          {/* Kalınan test: SORU SORU inceleme (sahibin 20 Eki kararı). Ekrandaki yanlışı AI konuşur; öğrenci o soru hakkında
              yazınca "anladım" açılır; hepsi geçilmeden test yeniden başlamaz. */}
          {isReviewMode && !passed && review && (() => {
            const currentDone = review.discussed.includes(review.currentNumber);
            const remaining = review.numbers.filter((n) => !review.discussed.includes(n));
            const isLastOpen = remaining.length === 1 && remaining[0] === review.currentNumber;
            return (
              <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 shadow-sm">
                <div className="mb-2 flex flex-wrap items-center gap-1.5">
                  <span className="mr-1 text-xs font-semibold text-blue-900">
                    Yanlışlar {review.discussed.length}/{review.numbers.length}:
                  </span>
                  {review.numbers.map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => review.onFocus(n)}
                      disabled={isReviewLocked(n)}
                      title={isReviewLocked(n) ? "Önce sıradaki yanlışı bitir: \"Bu soruyu anladım\"a bas" : undefined}
                      className={`rounded-full px-2.5 py-0.5 text-xs font-bold transition disabled:cursor-not-allowed disabled:opacity-50 ${n === review.currentNumber
                        ? "bg-blue-600 text-white"
                        : review.discussed.includes(n)
                          ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                          : "border border-blue-200 bg-white text-blue-800 hover:bg-blue-100 disabled:hover:bg-white"
                        }`}
                    >
                      {review.discussed.includes(n) ? "✓ " : isReviewLocked(n) ? "🔒 " : ""}{n}. soru
                    </button>
                  ))}
                </div>
                {review.allDone ? (
                  <>
                    <p className="text-sm leading-6 text-blue-900">
                      Yanlışlarının hepsini konuştunuz. Test yeniden başlıyor — varsa yeni sorular, yoksa aynı sorular gelir.
                    </p>
                    <button
                      onClick={review.onStartRetest}
                      disabled={!review.canConfirm}
                      className="mt-2 w-full rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Testi yeniden başlat
                    </button>
                  </>
                ) : currentDone ? (
                  <>
                    <p className="text-sm leading-6 text-blue-900">
                      ✓ Bu soruyu konuştunuz. Takıldığın bir yer varsa AI Öğretmen'e yazabilirsin.
                    </p>
                    {review.frontier !== null && (
                      <button
                        onClick={() => review.onFocus(review.frontier as number)}
                        className="mt-2 w-full rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700"
                      >
                        Sıradaki yanlışa dön ({review.frontier}. soru)
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-sm leading-6 text-blue-900">
                      {review.canConfirm
                        ? "Anladıysan devam et; takıldığın bir yer varsa AI Öğretmen'e yazabilirsin."
                        : "AI Öğretmen bu soruyu anlatıyor. Anlatım bitince \"anladım\" düğmesi açılır."}
                    </p>
                    <button
                      onClick={review.onConfirm}
                      disabled={!review.canConfirm}
                      className="mt-2 w-full rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {isLastOpen ? "Bu soruyu anladım → testi yeniden başlat" : "Bu soruyu anladım → sıradaki yanlış"}
                    </button>
                  </>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

const TutorDirectVideoPlayer = ({ src, startSeconds }: { src: string; startSeconds: number }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoError, setVideoError] = useState("");

  useEffect(() => {
    setVideoError("");
  }, [src]);

  return (
    <>
      <video
        ref={videoRef}
        className="absolute inset-0 h-full w-full bg-black"
        src={src}
        controls
        autoPlay
        preload="metadata"
        onLoadedMetadata={(event) => {
          if (startSeconds > 0 && Number.isFinite(event.currentTarget.duration)) {
            event.currentTarget.currentTime = Math.min(startSeconds, Math.max(0, event.currentTarget.duration - 1));
          }
        }}
        onError={() => {
          const code = videoRef.current?.error?.code;
          setVideoError(code ? `Video dosyası oynatılamadı. Hata kodu: ${code}` : "Video dosyası oynatılamadı.");
        }}
      />
      {videoError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 px-6 text-center text-sm font-semibold text-white">
          {videoError}
        </div>
      )}
    </>
  );
};

const PdfMaterialWorkspace = ({
  material,
  context,
  onMaterialAction,
  onPageChange,
}: {
  material: Extract<ActiveMaterial, { type: "pdf" }>;
  context: Record<string, any>;
  onMaterialAction: (message: string) => void;
  onPageChange?: (page: number) => void;
}) => {
  const initialPageNumber = Math.max(1, Number(material.pageNumber) || 1);
  const [currentPage, setCurrentPage] = useState(initialPageNumber);

  // Ekranda gerçekten bakılan sayfayı üst bileşene bildir — backend aktif sayfanın
  // GÖRÜNTÜSÜNÜ modele bu numaradan seçip gönderiyor (Faz 3)
  useEffect(() => {
    onPageChange?.(currentPage);
  }, [currentPage, onPageChange]);
  const targetPageRef = useRef(initialPageNumber);
  const relatedPdf = context.relatedMaterials?.pdfs?.find((pdf: any) => pdf?.url === material.pdfUrl);
  const rawPageImages: any[] = (Array.isArray(material.pageImages) && material.pageImages.length
    ? material.pageImages
    : Array.isArray(relatedPdf?.pageImages)
      ? relatedPdf.pageImages
      : []);
  const pageImages: PdfPageImage[] = rawPageImages
    .map((page: any, index: number) => ({
      id: page?.id || page?.pageNumber || index + 1,
      pageNumber: Number(page?.pageNumber) || index + 1,
      imageUrl: String(page?.imageUrl || ""),
    }))
    .filter((page: PdfPageImage) => page.imageUrl)
    .sort((left: PdfPageImage, right: PdfPageImage) => left.pageNumber - right.pageNumber);
  const totalPages = Math.max(0, Number(material.totalPages || relatedPdf?.totalPages || pageImages.length || 0));
  const pdfTitle = material.title || relatedPdf?.title || context.contentTitle || "PDF Materyali";
  const resolvedPdfUrl = resolveTutorMediaUrl(material.pdfUrl);
  const pdfViewerUrl = resolvedPdfUrl
    ? `${resolvedPdfUrl}#page=${currentPage}&toolbar=0&navpanes=0&scrollbar=1&view=FitH`
    : "";
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const updateVisiblePage = () => {
    if (!scrollContainerRef.current || !pageImages.length) return;

    const container = scrollContainerRef.current;
    const containerRect = container.getBoundingClientRect();
    let nextPage = pageImages[0]?.pageNumber || 1;
    let maxVisibleHeight = 0;

    pageImages.forEach((page, index) => {
      const node = pageRefs.current[page.pageNumber];
      if (!node) return;
      const pageNumber = Number(page.pageNumber) || index + 1;
      const pageRect = node.getBoundingClientRect();
      const visibleTop = Math.max(pageRect.top, containerRect.top);
      const visibleBottom = Math.min(pageRect.bottom, containerRect.bottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      if (visibleHeight > maxVisibleHeight) {
        maxVisibleHeight = visibleHeight;
        nextPage = pageNumber;
      }
    });

    setCurrentPage((previousPage) => (nextPage === previousPage ? previousPage : nextPage));
  };

  const scrollToPage = (nextPage: number, behavior: ScrollBehavior = "smooth") => {
    const safePage = totalPages ? Math.min(Math.max(1, nextPage), totalPages) : Math.max(1, nextPage);
    targetPageRef.current = safePage;
    const container = scrollContainerRef.current;
    const targetNode = pageRefs.current[safePage];

    if (container && targetNode) {
      setCurrentPage(safePage);
      const containerRect = container.getBoundingClientRect();
      const targetRect = targetNode.getBoundingClientRect();
      container.scrollTo({
        top: container.scrollTop + targetRect.top - containerRect.top - 8,
        behavior,
      });
      window.setTimeout(updateVisiblePage, behavior === "smooth" ? 260 : 0);
      return;
    }

    setCurrentPage(safePage);
  };

  useEffect(() => {
    targetPageRef.current = initialPageNumber;
    setCurrentPage(pageImages.length ? pageImages[0]?.pageNumber || 1 : initialPageNumber);
  }, [initialPageNumber, material.pdfUrl, pageImages.length]);

  useEffect(() => {
    if (!pageImages.length) return;

    const timers = [0, 120, 360].map((delay) =>
      window.setTimeout(() => {
        scrollToPage(initialPageNumber, delay === 0 ? "auto" : "smooth");
        updateVisiblePage();
      }, delay)
    );

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [initialPageNumber, material.pdfUrl, pageImages.length]);

  const handleImagePdfScroll = () => {
    updateVisiblePage();
  };

  const goToPage = (nextPage: number) => {
    scrollToPage(nextPage);
  };

  return (
    <div className="h-full min-h-0 bg-white flex flex-col">
      <div className="h-12 px-4 border-b border-[#dbe7ed] bg-white flex items-center justify-between gap-3">
        <div className="min-w-0 flex items-center gap-2 text-sm font-bold text-[#214b5d]">
          <FileText className="w-4 h-4 text-[#2d7087] flex-shrink-0" />
          <span className="truncate">{pdfTitle}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="min-w-[86px] rounded-full border border-[#d6e2e8] bg-[#edf6fa] px-3 py-1 text-center text-xs font-bold text-[#2d7087]">
            {totalPages ? `${currentPage}/${totalPages} sayfa` : `${currentPage}. sayfa`}
          </span>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 1}
            className="h-8 rounded-lg border border-[#d6e2e8] bg-white px-3 text-xs font-bold text-[#315869] disabled:opacity-40"
          >
            Önceki
          </button>
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={!totalPages || currentPage >= totalPages}
            className="h-8 rounded-lg border border-[#d6e2e8] bg-white px-3 text-xs font-bold text-[#315869] disabled:opacity-40"
          >
            Sonraki
          </button>
          <button
            type="button"
            onClick={() => onMaterialAction(`PDF materyalini okudum. Aynı PDF'i tekrar açma; özellikle açılan ${currentPage}. sayfayı eksiğimle ilişkilendir ve sıradaki uygun adımı öner.`)}
            className="h-8 rounded-lg bg-[#2d7087] px-3 text-xs font-bold text-white transition hover:bg-[#23576a]"
          >
            PDF'i okudum
          </button>
        </div>
      </div>

      {material.pdfUrl ? (
        <div ref={scrollContainerRef} onScroll={handleImagePdfScroll} className="flex-1 min-h-0 overflow-y-auto px-2 py-2">
          <div className="mx-auto flex max-w-[980px] flex-col gap-5">
            {pageImages.length ? (
              pageImages.map((page) => (
                <div
                  key={String(page.id || page.pageNumber)}
                  ref={(node) => {
                    pageRefs.current[page.pageNumber] = node;
                  }}
                  className="overflow-hidden rounded-[18px] border border-[#dde7eb] bg-white shadow-[0_10px_28px_rgba(26,72,92,0.08)]"
                >
                  <img
                    src={page.imageUrl}
                    alt={`${pdfTitle} sayfa ${page.pageNumber}`}
                    className="block h-auto w-full"
                    loading={page.pageNumber <= 2 ? "eager" : "lazy"}
                    onLoad={() => {
                      if (page.pageNumber !== targetPageRef.current) return;
                      window.setTimeout(() => scrollToPage(targetPageRef.current, "auto"), 0);
                    }}
                  />
                </div>
              ))
            ) : (
              <div className="overflow-hidden rounded-[18px] border border-[#dde7eb] bg-white shadow-[0_10px_28px_rgba(26,72,92,0.08)]">
                <iframe
                  key={`${resolvedPdfUrl}-${currentPage}`}
                  src={pdfViewerUrl}
                  title={`${pdfTitle} sayfa ${currentPage}`}
                  className="block h-[calc(100vh-116px)] min-h-[640px] w-full bg-white"
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <BlankWorkspace title="PDF bulunamadı" text="Bu konu için bağlı PDF URL bilgisi yok. Ders materyali bağlandığında burada açılacak." />
      )}
    </div>
  );
};

const BlankWorkspace = ({ title, text }: { title: string; text: string }) => (
  <div className="h-full bg-white flex items-center justify-center p-8">
    <div className="max-w-xl text-center">
      <div className="w-14 h-14 rounded-2xl bg-orange-100 text-orange-600 flex items-center justify-center mx-auto mb-5">
        <Bot className="w-7 h-7" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-3">{title}</h2>
      <p className="text-gray-500 leading-7">{text}</p>
    </div>
  </div>
);

export default AITutor;
